// ---------------------------------------------------------------------------
//  PresentationBuilder.js  –  Modern, user-friendly PowerPoint builder
//  2025-07-08 – + custom font support, bold/italic/underline for text items
//  2025-07-08 – NEW: full chart editing (type, sort, metric, orientation, legend …)
//  2025-07-08 – NEW: per-graph colour picker + PPT export of custom colours
// ---------------------------------------------------------------------------
import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useDeferredValue,
  useRef
} from 'react';
import {
  Box,
  Chip,
  Button,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  List,
  ListItemButton,
  Menu,
  Divider,
  Paper,
  Switch,
  Slider,
  ButtonBase,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FlipToFrontIcon from '@mui/icons-material/FlipToFront';
import FlipToBackIcon from '@mui/icons-material/FlipToBack';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PercentIcon from '@mui/icons-material/Percent';
import LooksOneIcon from '@mui/icons-material/LooksOne';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import PaletteIcon from '@mui/icons-material/Palette';
import PeopleIcon from '@mui/icons-material/People';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TableChartIcon from '@mui/icons-material/TableChart';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import RoundedCornerIcon from '@mui/icons-material/RoundedCorner';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory';
import DiamondIcon from '@mui/icons-material/Diamond';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import OpacityIcon from '@mui/icons-material/Opacity';
import LineWeightIcon from '@mui/icons-material/LineWeight';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import HexagonIcon from '@mui/icons-material/Hexagon';
import PentagonIcon from '@mui/icons-material/Pentagon';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import PaddingIcon from '@mui/icons-material/Padding';
import SaveIcon from '@mui/icons-material/Save';
import CategoryIcon from '@mui/icons-material/Category';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Rnd } from 'react-rnd';
import PptxGenJS from 'pptxgenjs/dist/pptxgen.es.js';
import { v4 as uuid } from 'uuid';
import { useParams } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { icons as LucideIcons } from 'lucide-react';

import {
  doc,
  onSnapshot,
  setDoc,
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  getDoc,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

/* -------------------------------------------------------------------- */
/*  Chart rendering helpers                                             */
/* -------------------------------------------------------------------- */
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from 'recharts';

// ---------- COLOUR + FONT CONSTANTS ----------------------------------
export const DEFAULT_COLORS = [
  '#6366f1',
  '#22c55e',
  '#facc15',
  '#fb923c',
  '#06b6d4',
  '#4ade80',
  '#d946ef',
  '#f87171',
];

export const FONTS = [
  'Arial',
  'Calibri',
  'Times New Roman',
  'Verdana',
  'Helvetica',
  'Georgia',
  'Courier New',
  'Comic Sans MS',
];

const DEFAULT_TEXT_STYLE = {
  fontSize: 18,
  color: '#111827',
  fontFamily: FONTS[1] || FONTS[0],
  align: 'left',
  bold: false,
  italic: false,
  underline: false,
};

const RICH_TEXT_ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'BR', 'DIV', 'P']);
const RICH_TEXT_ALLOWED_STYLES = new Set([
  'color',
  'background-color',
  'font-weight',
  'font-style',
  'font-size',
  'font-family',
  'text-decoration',
  'text-align',
]);

const HISTORY_LIMIT = 15;

const cloneSlidesState = (slidesArray) => {
  if (!Array.isArray(slidesArray)) return [];
  try {
    return JSON.parse(JSON.stringify(slidesArray));
  } catch {
    return slidesArray.map((slide) => ({
      ...slide,
      items: Array.isArray(slide.items)
        ? slide.items.map((item) => ({ ...item }))
        : [],
    }));
  }
};

const createHistorySnapshot = (slidesArray, activeIndex, selectionIds) => ({
  slides: cloneSlidesState(slidesArray),
  activeIdx: typeof activeIndex === 'number' ? activeIndex : 0,
  selectedItemIds: Array.isArray(selectionIds) ? [...selectionIds] : [],
});

const canUseDom = () => typeof window !== 'undefined' && typeof document !== 'undefined';

const formatIconSearchLabel = (name) =>
  String(name || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .toLowerCase();

const LUCIDE_ICON_OPTIONS = Object.entries(LucideIcons)
  .filter(([name, Component]) => /^[A-Z]/.test(name) && Component)
  .map(([name, Component]) => ({
    name,
    Component,
    keywords: formatIconSearchLabel(name),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const LUCIDE_ICON_SET = new Set(LUCIDE_ICON_OPTIONS.map((opt) => opt.name));
const LUCIDE_ICON_FALLBACK = LUCIDE_ICON_OPTIONS[0]?.name || 'Activity';
const DEFAULT_ICON_COLOR = '#0f172a';
const DEFAULT_ICON_STROKE = 1.75;
const ICON_STROKE_MIN = 0.5;
const ICON_STROKE_MAX = 4;
const DEFAULT_ICON_SIZE = 120;

const FONT_SIZE_COMMAND_MAP = {
  1: 8,
  2: 10,
  3: 12,
  4: 14,
  5: 18,
  6: 24,
  7: 32,
};

const EMPTY_RICH_TEXT_HTML = '<div><br /></div>';

const escapeHtml = (str) =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const plainTextToRichTextHtml = (text = '') => {
  const safe = escapeHtml(text);
  const html = safe.replace(/\r\n|\r|\n/g, '<br />');
  return `<div>${html || '<br />'}</div>`;
};

const sanitizeRichTextHtml = (input) => {
  if (typeof input !== 'string' || !input.trim()) {
    return '';
  }
  if (!canUseDom()) {
    return plainTextToRichTextHtml(input);
  }

  const container = document.createElement('div');
  container.innerHTML = input;

  const normaliseFontElement = (node) => {
    if (node.tagName !== 'FONT') return node;
    const span = document.createElement('span');
    const color = node.getAttribute('color');
    if (color) span.style.color = color;
    const face = node.getAttribute('face');
    if (face) span.style.fontFamily = face;
    const size = node.getAttribute('size');
    if (size) {
      const mapped = FONT_SIZE_COMMAND_MAP[size] ?? Number(size);
      if (Number.isFinite(mapped) && mapped > 0) {
        span.style.fontSize = `${mapped}px`;
      }
    }
    while (node.firstChild) {
      span.appendChild(node.firstChild);
    }
    node.replaceWith(span);
    return span;
  };

  const traverse = (parent) => {
    const childNodes = Array.from(parent.childNodes);
    childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) {
        parent.removeChild(node);
        return;
      }

      const element = normaliseFontElement(node);
      const tagName = element.tagName.toUpperCase();
      if (!RICH_TEXT_ALLOWED_TAGS.has(tagName)) {
        const frag = document.createDocumentFragment();
        while (element.firstChild) {
          frag.appendChild(element.firstChild);
        }
        element.replaceWith(frag);
        return;
      }

      Array.from(element.attributes).forEach((attr) => {
        if (attr.name.toLowerCase() !== 'style') {
          element.removeAttribute(attr.name);
        }
      });

      if (element.hasAttribute('style')) {
        const nextRules = [];
        element
          .getAttribute('style')
          .split(';')
          .forEach((rule) => {
            if (!rule.trim()) return;
            const [propRaw, valRaw] = rule.split(':');
            if (!propRaw || !valRaw) return;
            const prop = propRaw.trim().toLowerCase();
            const value = valRaw.trim();
            if (RICH_TEXT_ALLOWED_STYLES.has(prop) && value) {
              nextRules.push(`${prop}: ${value}`);
            }
          });
        if (nextRules.length) {
          element.setAttribute('style', nextRules.join('; '));
        } else {
          element.removeAttribute('style');
        }
      }

      traverse(element);
    });
  };

  traverse(container);
  return container.innerHTML;
};

const extractPlainTextFromRichText = (html) => {
  if (typeof html !== 'string' || !html.trim()) return '';
  if (!canUseDom()) {
    return html.replace(/<[^>]+>/g, ' ');
  }
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const isRichTextHtmlEmpty = (html) => {
  if (typeof html !== 'string') return true;
  if (!html.trim()) return true;
  const plain = extractPlainTextFromRichText(html);
  return !plain.trim();
};

const ensureRichTextValue = (value, fallbackPlain = '') => {
  const candidate = typeof value === 'string' ? value : '';
  const sanitized = sanitizeRichTextHtml(candidate);
  if (sanitized && sanitized.replace(/<[^>]*>/g, '').trim()) {
    return sanitized;
  }
  const fallback = typeof fallbackPlain === 'string' ? fallbackPlain : '';
  if (fallback && fallback.trim()) {
    return plainTextToRichTextHtml(fallback);
  }
  return EMPTY_RICH_TEXT_HTML;
};

const normaliseFontSize = (value, fallback) => {
  const num = Number(value);
  if (Number.isFinite(num) && num > 0) return num;
  return fallback;
};

const parseInlineStyleRules = (styleAttr) => {
  if (!styleAttr || typeof styleAttr !== 'string') return {};
  return styleAttr
    .split(';')
    .map((rule) => rule.trim())
    .filter(Boolean)
    .reduce((acc, rule) => {
      const [prop, val] = rule.split(':');
      if (!prop || !val) return acc;
      acc[prop.trim().toLowerCase()] = val.trim();
      return acc;
    }, {});
};

const DEFAULT_TABLE_BORDER = {
  color: '#000000',
  width: 1,
  style: 'solid',
};

const createDefaultTable = () => {
  const rows = 3;
  const cols = 3;
  const columnSizes = Array.from({ length: cols }, () => 1 / cols);
  const rowSizes = Array.from({ length: rows }, () => 1 / rows);
  const cells = Array.from({ length: rows }, (_, r) =>
    Array.from({ length: cols }, (_, c) => {
      const label = r === 0 ? `Başlık ${c + 1}` : `Satır ${r} Kolon ${c + 1}`;
      return plainTextToRichTextHtml(label);
    }),
  );
  return {
    cells,
    columnSizes,
    rowSizes,
    headerRows: 1,
    border: { ...DEFAULT_TABLE_BORDER },
    fontSize: 14,
    fontFamily: FONTS[0],
    textColor: '#111111',
    align: 'left',
    headerFill: '#f3f4f6',
    cellPadding: 6,
  };
};

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);
const normalizeRotation = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  const normalized = num % 360;
  return normalized < 0 ? normalized + 360 : normalized;
};
const MIN_TABLE_FRACTION = 0.02;

const SHAPE_OPTIONS = [
  {
    value: 'rectangle',
    label: 'Dikdörtgen',
    Icon: CropSquareIcon,
    defaults: {
      borderRadius: 0,
      fill: '#eeeeee',
      fillOpacity: 1,
    },
    initialSize: { w: 240, h: 160 },
  },
  {
    value: 'roundedRect',
    label: 'Yuvarlak Köşe',
    Icon: RoundedCornerIcon,
    defaults: {
      borderRadius: 28,
      fill: '#e0f2fe',
      fillOpacity: 1,
    },
    initialSize: { w: 260, h: 160 },
  },
  {
    value: 'ellipse',
    label: 'Elips',
    Icon: PanoramaFishEyeIcon,
    defaults: {
      borderRadius: 0,
      fill: '#fef3c7',
      fillOpacity: 1,
    },
    initialSize: { w: 240, h: 180 },
  },
  {
    value: 'circle',
    label: 'Daire',
    Icon: RadioButtonUncheckedIcon,
    defaults: {
      borderRadius: 0,
      fill: '#ede9fe',
      fillOpacity: 1,
    },
    initialSize: { w: 220, h: 220 },
  },
  {
    value: 'triangle',
    label: 'Üçgen',
    Icon: ChangeHistoryIcon,
    defaults: {
      borderRadius: 0,
      fill: '#fee2e2',
      fillOpacity: 1,
    },
    initialSize: { w: 240, h: 200 },
  },
  {
    value: 'diamond',
    label: 'Elmas',
    Icon: DiamondIcon,
    defaults: {
      borderRadius: 0,
      fill: '#dcfce7',
      fillOpacity: 1,
    },
    initialSize: { w: 240, h: 200 },
  },
  {
    value: 'pentagon',
    label: 'Beşgen',
    Icon: PentagonIcon,
    defaults: {
      borderRadius: 0,
      fill: '#fde68a',
      fillOpacity: 1,
    },
    initialSize: { w: 260, h: 220 },
  },
  {
    value: 'hexagon',
    label: 'Altıgen',
    Icon: HexagonIcon,
    defaults: {
      borderRadius: 0,
      fill: '#bbf7d0',
      fillOpacity: 1,
    },
    initialSize: { w: 260, h: 220 },
  },
  {
    value: 'star',
    label: 'Yıldız',
    Icon: StarBorderIcon,
    defaults: {
      borderRadius: 0,
      fill: '#fcd34d',
      fillOpacity: 1,
    },
    initialSize: { w: 260, h: 240 },
  },
  {
    value: 'line',
    label: 'Çizgi',
    Icon: RemoveIcon,
    defaults: {
      fillOpacity: 0,
      strokeWidth: 4,
    },
    initialSize: { w: 320, h: 14 },
  },
  {
    value: 'arrowRight',
    label: 'Sağ Ok',
    Icon: ArrowRightAltIcon,
    defaults: {
      borderRadius: 0,
      fill: '#c7d2fe',
      fillOpacity: 1,
    },
    initialSize: { w: 280, h: 160 },
  },
  {
    value: 'arrowLeft',
    label: 'Sol Ok',
    Icon: ArrowLeftIcon,
    defaults: {
      borderRadius: 0,
      fill: '#bae6fd',
      fillOpacity: 1,
    },
    initialSize: { w: 280, h: 160 },
  },
  {
    value: 'arrowUp',
    label: 'Yukarı Ok',
    Icon: ArrowUpwardIcon,
    defaults: {
      borderRadius: 0,
      fill: '#fecdd3',
      fillOpacity: 1,
    },
    initialSize: { w: 200, h: 260 },
  },
  {
    value: 'arrowDown',
    label: 'Aşağı Ok',
    Icon: ArrowDownwardIcon,
    defaults: {
      borderRadius: 0,
      fill: '#ddd6fe',
      fillOpacity: 1,
    },
    initialSize: { w: 200, h: 260 },
  },
];

const SHAPE_MAP = SHAPE_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt;
  return acc;
}, {});

const ensureShapeConfig = (shape) => {
  const kind = SHAPE_MAP[shape?.kind] ? shape.kind : 'rectangle';
  const template = SHAPE_MAP[kind];
  const fallbackStroke = '#1f2937';
  const fallbackFill = template.defaults.fill ?? '#ffffff';
  const fillOpacityRaw =
    typeof shape?.fillOpacity === 'number' ? shape.fillOpacity : template.defaults.fillOpacity ?? 1;
  const strokeWidthRaw =
    typeof shape?.strokeWidth === 'number'
      ? shape.strokeWidth
      : template.defaults.strokeWidth ?? (kind === 'line' ? 4 : 2);
  const borderRadiusRaw =
    typeof shape?.borderRadius === 'number'
      ? shape.borderRadius
      : template.defaults.borderRadius ?? (kind === 'roundedRect' ? 24 : 0);

  return {
    kind,
    fill: typeof shape?.fill === 'string' ? shape.fill : fallbackFill,
    fillOpacity: clamp(fillOpacityRaw, 0, 1),
    stroke: typeof shape?.stroke === 'string' ? shape.stroke : shape?.strokeColor || fallbackStroke,
    strokeWidth: clamp(strokeWidthRaw, 0, 40),
    strokeStyle: ['solid', 'dashed', 'dotted'].includes(shape?.strokeStyle)
      ? shape.strokeStyle
      : 'solid',
    borderRadius: Math.max(0, borderRadiusRaw),
  };
};

const createShapeDefaults = (kind) => ensureShapeConfig({ kind });

const shapeDashArray = (style, width) => {
  const w = Math.max(1, width);
  if (style === 'dashed') return `${w * 3} ${w * 2}`;
  if (style === 'dotted') return `${w} ${w * 1.5}`;
  return undefined;
};

const regularPolygonPoints = (sides, w, h, strokeWidth = 0) => {
  const cx = w / 2;
  const cy = h / 2;
  const rx = Math.max((w - strokeWidth) / 2, 0);
  const ry = Math.max((h - strokeWidth) / 2, 0);
  const step = (Math.PI * 2) / sides;
  const start = -Math.PI / 2;
  return Array.from({ length: sides }, (_, i) => {
    const angle = start + i * step;
    const x = cx + rx * Math.cos(angle);
    const y = cy + ry * Math.sin(angle);
    return `${x},${y}`;
  }).join(' ');
};

const starPolygonPoints = (points, w, h, strokeWidth = 0, innerScale = 0.5) => {
  const total = points * 2;
  const cx = w / 2;
  const cy = h / 2;
  const rx = Math.max((w - strokeWidth) / 2, 0);
  const ry = Math.max((h - strokeWidth) / 2, 0);
  const step = Math.PI / points;
  const start = -Math.PI / 2;
  return Array.from({ length: total }, (_, i) => {
    const radius = i % 2 === 0 ? 1 : innerScale;
    const angle = start + i * step;
    const x = cx + rx * radius * Math.cos(angle);
    const y = cy + ry * radius * Math.sin(angle);
    return `${x},${y}`;
  }).join(' ');
};

const toolbarContainerSx = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 1,
  padding: '10px 14px',
  borderRadius: 16,
  border: '1px solid rgba(148,163,184,0.2)',
  backgroundColor: 'rgba(248,250,252,0.92)',
  boxShadow: '0 12px 24px rgba(15,23,42,0.08)',
  flexWrap: 'nowrap',
  overflowX: 'auto',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    height: 6,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(148,163,184,0.5)',
    borderRadius: 8,
  },
  '& > *': {
    flexShrink: 0,
  },
};

const toolbarIconButtonSx = {
  width: 36,
  height: 36,
  borderRadius: 1.5,
  border: '1px solid rgba(148,163,184,0.35)',
  backgroundColor: '#fff',
  boxShadow: '0 4px 12px rgba(148,163,184,0.24)',
  color: 'text.primary',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 20px rgba(100,116,139,0.25)',
    backgroundColor: '#f8fafc',
  },
  '&.Mui-disabled': {
    opacity: 0.45,
    boxShadow: 'none',
    transform: 'none',
    backgroundColor: '#f1f5f9',
    borderColor: 'rgba(148,163,184,0.2)',
  },
};

const ToolbarContainer = ({ children, sx, ...props }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ ...toolbarContainerSx, ...sx }} {...props}>
    {children}
  </Stack>
);

const ToolbarColorButton = ({ value, onChange, label, IconComponent = PaletteIcon }) => (
  <Tooltip title={label}>
    <Box sx={{ position: 'relative', width: 36, height: 36, flexShrink: 0 }}>
      <IconButton
        size="small"
        sx={{ ...toolbarIconButtonSx, width: '100%', height: '100%', position: 'relative' }}
        onMouseDown={(e) => e.preventDefault()}
      >
        <IconComponent fontSize="inherit" />
        <Box
          sx={{
            position: 'absolute',
            bottom: 4,
            right: 4,
            width: 12,
            height: 12,
            borderRadius: '50%',
            border: '1px solid rgba(148,163,184,0.6)',
            backgroundColor: value,
          }}
        />
      </IconButton>
      <Box
        component="input"
        type="color"
        value={value}
        onChange={onChange}
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0,
          cursor: 'pointer',
          width: '100%',
          height: '100%',
          border: 'none',
          padding: 0,
        }}
      />
    </Box>
  </Tooltip>
);

const isLineLikeShape = (shape) => shape.kind === 'line';

const normaliseFractions = (values) => {
  const len = values.length;
  if (!len) return [];
  if (len === 1) return [1];
  const minAllowed = Math.min(MIN_TABLE_FRACTION, 0.5 / len);
  const sanitized = values.map((v) => {
    const num = Number(v);
    if (!Number.isFinite(num) || num <= 0) return minAllowed;
    return Math.max(minAllowed, num);
  });
  const sum = sanitized.reduce((total, v) => total + v, 0);
  if (sum <= 0) {
    return Array.from({ length: len }, () => 1 / len);
  }
  return sanitized.map((v) => v / sum);
};

const adjustFractionAtIndex = (fractions, index, nextFraction) => {
  const len = fractions.length;
  if (!len) return [];
  if (len === 1) return [1];
  const minAllowed = Math.min(MIN_TABLE_FRACTION, 0.5 / len);
  const maxAllowed = 1 - minAllowed * (len - 1);
  const target = clamp(nextFraction, minAllowed, maxAllowed);
  const normalised = normaliseFractions(fractions);
  const restTotal = normalised.reduce((sum, val, idx) => (idx === index ? sum : sum + val), 0);
  const newRestTotal = clamp(1 - target, minAllowed * (len - 1), 1);
  if (restTotal <= 0) {
    return normaliseFractions(
      normalised.map((_, idx) => (idx === index ? target : newRestTotal / (len - 1))),
    );
  }
  return normaliseFractions(
    normalised.map((val, idx) => {
      if (idx === index) return target;
      return val * (newRestTotal / restTotal);
    }),
  );
};

const normaliseTableCellHtml = (cell) => {
  if (cell == null) {
    return EMPTY_RICH_TEXT_HTML;
  }

  if (typeof cell === 'object' && cell !== null) {
    const hasHtml = typeof cell.html === 'string' ? cell.html : null;
    if (hasHtml && hasHtml.trim()) {
      const fallbackPlain = typeof cell.text === 'string' ? cell.text : extractPlainTextFromRichText(hasHtml);
      let sanitized = ensureRichTextValue(hasHtml, fallbackPlain || '');
      if (!sanitized || !sanitized.trim()) {
        return EMPTY_RICH_TEXT_HTML;
      }
      if (!/(<\s*(div|p|br)\b)/i.test(sanitized)) {
        sanitized = `<div>${sanitized}</div>`;
      }
      return sanitized;
    }
    if (typeof cell.text === 'string' && cell.text.trim()) {
      return plainTextToRichTextHtml(cell.text);
    }
    if (typeof cell.value === 'string' && cell.value.trim()) {
      return plainTextToRichTextHtml(cell.value);
    }
  }

  if (typeof cell === 'string') {
    const sanitized = ensureRichTextValue(cell, extractPlainTextFromRichText(cell));
    if (!sanitized || !sanitized.trim()) {
      return EMPTY_RICH_TEXT_HTML;
    }
    if (!/(<\s*(div|p|br)\b)/i.test(sanitized)) {
      return `<div>${sanitized}</div>`;
    }
    return sanitized;
  }

  if (typeof cell === 'number' && Number.isFinite(cell)) {
    return plainTextToRichTextHtml(String(cell));
  }

  if (typeof cell === 'boolean') {
    return plainTextToRichTextHtml(cell ? 'Doğru' : 'Yanlış');
  }

  return plainTextToRichTextHtml(String(cell));
};

const ensureTableData = (table) => {
  const base = createDefaultTable();
  if (!table) return base;
  const cellsRaw = Array.isArray(table.cells) && table.cells.length
    ? table.cells
    : base.cells;
  const cells = cellsRaw.map((row) =>
    Array.isArray(row)
      ? row.map((cell) => normaliseTableCellHtml(cell))
      : Array.from({ length: cellsRaw[0]?.length || base.cells[0].length }, () => EMPTY_RICH_TEXT_HTML),
  );
  const rowCount = cells.length || 1;
  const colCount = cells[0]?.length || 1;
  const columnSizesSrc = Array.isArray(table.columnSizes) && table.columnSizes.length
    ? table.columnSizes.slice(0, colCount)
    : base.columnSizes;
  const rowSizesSrc = Array.isArray(table.rowSizes) && table.rowSizes.length
    ? table.rowSizes.slice(0, rowCount)
    : base.rowSizes;

  const columnSizes = normaliseFractions(
    columnSizesSrc.length === colCount
      ? columnSizesSrc
      : [
          ...columnSizesSrc,
          ...Array.from({ length: colCount - columnSizesSrc.length }, () => 1 / colCount),
        ],
  );
  const rowSizes = normaliseFractions(
    rowSizesSrc.length === rowCount
      ? rowSizesSrc
      : [
          ...rowSizesSrc,
          ...Array.from({ length: rowCount - rowSizesSrc.length }, () => 1 / rowCount),
        ],
  );

  return {
    ...base,
    ...table,
    cells,
    columnSizes,
    rowSizes,
    headerRows: clamp(Number.isFinite(+table.headerRows) ? Number(table.headerRows) : base.headerRows, 0, rowCount),
    border: { ...DEFAULT_TABLE_BORDER, ...(table.border || {}) },
    fontSize: Number.isFinite(+table.fontSize) ? Number(table.fontSize) : base.fontSize,
    fontFamily: table.fontFamily || base.fontFamily,
    textColor: table.textColor || base.textColor,
    align: table.align || base.align,
    headerFill: table.headerFill || base.headerFill,
    cellPadding: Number.isFinite(+table.cellPadding) ? Number(table.cellPadding) : base.cellPadding,
  };
};

const encodeTableForFirestore = (table) => {
  const safe = ensureTableData(table);
  const rows = safe.cells.map((row) => ({
    values: Array.isArray(row) ? row : [],
  }));
  const { cells, ...rest } = safe;
  return { ...rest, rows };
};

const decodeTableFromFirestore = (table) => {
  if (!table || typeof table !== 'object') {
    return ensureTableData(table);
  }
  if (Array.isArray(table.cells)) {
    return ensureTableData(table);
  }
  if (Array.isArray(table.rows)) {
    const cells = table.rows.map((row) => {
      if (!row) return [];
      if (Array.isArray(row.values)) return row.values;
      if (Array.isArray(row)) return row;
      if (typeof row === 'object') {
        const keys = Object.keys(row)
          .filter((key) => key !== 'values')
          .sort();
        if (keys.length) {
          return keys.map((key) => row[key]);
        }
      }
      return [];
    });
    const { rows, ...rest } = table;
    return ensureTableData({ ...rest, cells });
  }
  return ensureTableData(table);
};

const prepareTableForPresentation = (tableAsset, ctx = {}) => {
  if (!tableAsset || !tableAsset.table) return null;
  const header =
    tableAsset.header ||
    tableAsset.title ||
    tableAsset.table?.title ||
    'Tablo';
  const normalized = ensureTableData(tableAsset.table);
  const normalizeStr = (val) => {
    if (val == null) return '';
    return String(val).trim();
  };
  const asset = {
    assetType: 'table',
    header,
    table: normalized,
  };
  const ctxMap = {
    __analysisId: ctx.analysisId ?? tableAsset.analysisId,
    __analysisName: ctx.analysisName ?? tableAsset.analysisName,
    __projectId: ctx.projectId ?? tableAsset.projectId,
    __projectCode: ctx.projectCode ?? tableAsset.projectCode,
    __groupId: ctx.groupId ?? tableAsset.groupId,
    __groupName: ctx.groupName ?? tableAsset.groupName,
    __sourceTableId: tableAsset.tableId ?? tableAsset.id ?? tableAsset.sourceTableId,
  };
  Object.entries(ctxMap).forEach(([key, value]) => {
    const str = normalizeStr(value);
    if (str) {
      asset[key] = str;
    }
  });
  const tableLink = sanitizeTableLink(tableAsset.tableLink, {
    organisationFallback: ctx.organisationId ?? tableAsset.organisationId,
  });
  if (tableLink) {
    asset.tableLink = tableLink;
  }
  return asset;
};

const tableAlignToFlex = (align) => {
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return 'flex-start';
};

const cssLineStyle = (style) => {
  if (style === 'dashed') return 'dashed';
  if (style === 'dotted') return 'dotted';
  return 'solid';
};

const TABLE_LINK_KIND = 'tableDataset';
const GRAPH_LINK_KIND = 'graphDataset';

const sanitizeFirestorePath = (path) => {
  if (typeof path !== 'string') return '';
  return path.trim().replace(/^\/+|\/+$/g, '');
};

const normalizeOptionalString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const sanitizeTableLink = (link, { organisationFallback } = {}) => {
  if (!link || typeof link !== 'object') return null;
  const docPath = sanitizeFirestorePath(
    link.docPath || link.path || link.datasetPath || link.tablePath
  );
  if (!docPath) return null;
  const kind =
    !link.kind || link.kind === TABLE_LINK_KIND ? TABLE_LINK_KIND : link.kind;
  if (kind !== TABLE_LINK_KIND) return null;
  const payload = {
    kind: TABLE_LINK_KIND,
    docPath,
  };
  const orgId =
    normalizeOptionalString(link.organisationId) ||
    normalizeOptionalString(organisationFallback);
  if (orgId) payload.organisationId = orgId;
  ['tableId', 'analysisId', 'projectId', 'groupId'].forEach((key) => {
    const value = normalizeOptionalString(link[key]);
    if (value) payload[key] = value;
  });
  return payload;
};

const getTableLinkKey = (link) => {
  const safe = sanitizeTableLink(link);
  return safe ? `${safe.kind}:${safe.docPath}` : null;
};

const sanitizeGraphLink = (link, { organisationFallback } = {}) => {
  if (!link || typeof link !== 'object') return null;
  const docPath = sanitizeFirestorePath(
    link.docPath || link.datasetPath || link.graphDocPath || link.graphDatasetPath
  );
  if (!docPath) return null;
  const payload = {
    kind: GRAPH_LINK_KIND,
    docPath,
  };
  const orgId =
    normalizeOptionalString(link.organisationId) ||
    normalizeOptionalString(organisationFallback);
  if (orgId) payload.organisationId = orgId;
  ['analysisId', 'projectId', 'groupId', 'graphId'].forEach((key) => {
    const value = normalizeOptionalString(link[key] ?? link[`${key}_`]);
    if (value) payload[key] = value;
  });
  if (
    Object.prototype.hasOwnProperty.call(link, 'graphIndex') &&
    Number.isFinite(+link.graphIndex)
  ) {
    payload.graphIndex = Number(link.graphIndex);
  }
  return payload;
};

const getGraphLinkKey = (link) => {
  const safe = sanitizeGraphLink(link);
  if (!safe) return null;
  const suffix =
    safe.graphId || Number.isFinite(safe.graphIndex) ? `${safe.graphId || safe.graphIndex}` : '';
  return `${safe.kind}:${safe.docPath}${suffix ? `:${suffix}` : ''}`;
};

const datasetNumberFormatter =
  typeof Intl !== 'undefined'
    ? new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 })
    : null;

const formatDatasetCellValue = (value) => {
  if (value == null) return '';
  if (typeof value === 'number' && Number.isFinite(value)) {
    return datasetNumberFormatter ? datasetNumberFormatter.format(value) : String(value);
  }
  if (typeof value === 'object') {
    if ('text' in value) return formatDatasetCellValue(value.text);
    if ('value' in value && typeof value.value !== 'object') {
      return formatDatasetCellValue(value.value);
    }
    try {
      return JSON.stringify(value);
    } catch {
      return '';
    }
  }
  return String(value);
};

const datasetColumnHeader = (col, fallbackIdx) => {
  if (!col || typeof col !== 'object') return `Kolon ${fallbackIdx + 1}`;
  return (
    col.headerName ||
    col.header ||
    col.title ||
    col.name ||
    col.field ||
    `Kolon ${fallbackIdx + 1}`
  );
};

const datasetColumnField = (col, fallbackIdx) => {
  if (!col || typeof col !== 'object') return `col_${fallbackIdx}`;
  return (
    col.field ||
    col.id ||
    col.header ||
    col.headerName ||
    col.name ||
    `col_${fallbackIdx}`
  );
};

const extractDatasetColumns = (meta) => {
  if (!meta || typeof meta !== 'object') return [];
  if (typeof meta.columnsJSON === 'string' && meta.columnsJSON.trim()) {
    try {
      const parsed = JSON.parse(meta.columnsJSON);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed;
      }
    } catch (err) {
      console.warn('columnsJSON parse failed', err);
    }
  }
  if (Array.isArray(meta.columns) && meta.columns.length) {
    return meta.columns;
  }
  return [];
};

const buildDatasetTable = (meta, rows) => {
  const columns = extractDatasetColumns(meta);
  if (!columns.length) return null;
  const headers = columns.map((col, idx) => datasetColumnHeader(col, idx));
  const fields = columns.map((col, idx) => datasetColumnField(col, idx));
  const safeRows = Array.isArray(rows) ? rows : [];
  const cellRows = safeRows.map((row) =>
    fields.map((field, idx) => {
      if (row && Object.prototype.hasOwnProperty.call(row, field)) {
        return formatDatasetCellValue(row[field]);
      }
      const fallback = headers[idx];
      if (fallback && row && Object.prototype.hasOwnProperty.call(row, fallback)) {
        return formatDatasetCellValue(row[fallback]);
      }
      return '';
    })
  );
  const cells = [headers, ...cellRows];
  return {
    cells,
    headerRows: 1,
    columnCount: fields.length,
    rowCount: cells.length,
  };
};

const deriveProjectId = (analysis, ctx = {}) => {
  const candidate =
    analysis?.projectID ??
    analysis?.projectId ??
    analysis?.project_id ??
    ctx?.projectID ??
    ctx?.projectId ??
    ctx?.project_id ??
    '';
  if (!candidate) return '';
  const trimmed = String(candidate).trim();
  return trimmed;
};

const normalizeReportingTableColumns = (tableDoc) => {
  const rawColumns = Array.isArray(tableDoc?.columns) && tableDoc.columns.length
    ? tableDoc.columns
    : Array.isArray(tableDoc?.headers)
      ? tableDoc.headers
      : [];
  return rawColumns.map((col, idx) => {
    if (typeof col === 'string') {
      const header = col.trim();
      const field = header || `col_${idx + 1}`;
      return {
        field,
        headerName: header || `Kolon ${idx + 1}`,
      };
    }
    if (col && typeof col === 'object') {
      const field =
        col.field ||
        col.id ||
        col.header ||
        col.headerName ||
        col.name ||
        `col_${idx + 1}`;
      const headerName =
        col.headerName ||
        col.header ||
        col.title ||
        col.name ||
        field ||
        `Kolon ${idx + 1}`;
      return {
        ...col,
        field,
        headerName,
      };
    }
    return {
      field: `col_${idx + 1}`,
      headerName: `Kolon ${idx + 1}`,
    };
  });
};

const normalizeReportingRows = (tableDoc, columns) => {
  const rawRows = Array.isArray(tableDoc?.rows) ? tableDoc.rows : [];
  if (!columns.length) return [];
  const fields = columns.map((col, idx) => col.field || `col_${idx + 1}`);
  return rawRows.map((row) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) {
      return row;
    }
    if (Array.isArray(row)) {
      const obj = {};
      row.forEach((value, idx) => {
        const field = fields[idx] || `col_${idx + 1}`;
        obj[field] = value;
      });
      return obj;
    }
    return {};
  });
};

const mergeLinkedTableData = (currentTable, incoming) => {
  const safeCurrent = ensureTableData(currentTable);
  const sanitized = ensureTableData({
    ...safeCurrent,
    cells: incoming.cells,
    headerRows: Number.isFinite(incoming.headerRows)
      ? incoming.headerRows
      : safeCurrent.headerRows,
  });
  const incomingColCount = sanitized.cells[0]?.length || 1;
  const incomingRowCount = sanitized.cells.length || 1;
  const columnSizes =
    safeCurrent.columnSizes.length === incomingColCount
      ? safeCurrent.columnSizes
      : sanitized.columnSizes;
  const rowSizes =
    safeCurrent.rowSizes.length === incomingRowCount
      ? safeCurrent.rowSizes
      : sanitized.rowSizes;
  return {
    ...sanitized,
    columnSizes: normaliseFractions(columnSizes),
    rowSizes: normaliseFractions(rowSizes),
  };
};

// ---------- PREDEFINED COLOR PALETTES -------------------------
export const COLOR_PALETTES = {
  default: DEFAULT_COLORS,
  pastel: [
    '#AEC6CF',
    '#FFB3BA',
    '#FFDFBA',
    '#FFFFBA',
    '#BFFCC6',
    '#B4A7E7',
    '#D3FFE5',
    '#FFB347',
  ],
  bright: [
    '#e6194b',
    '#3cb44b',
    '#ffe119',
    '#0082c8',
    '#f58231',
    '#911eb4',
    '#46f0f0',
    '#f032e6',
  ],
  dark: [
    '#1b1b1e',
    '#273469',
    '#3e59ff',
    '#ff3860',
    '#ff7f47',
    '#ffa600',
    '#fcd000',
    '#ff5470',
  ],
};

// Convert "#RRGGBB" → "RRGGBB" for pptxgenjs
const toPptxColor = (hex) => (hex || '').replace('#', '').toUpperCase();

const CANVAS_W = 960;
const CANVAS_H = 540;
const PX_TO_IN = 10 / CANVAS_W;
const GRID_SIZE = 10;
const MIN_TABLE_ITEM_WIDTH = GRID_SIZE * 20;
const MIN_TABLE_ITEM_HEIGHT = GRID_SIZE * 14;
const SIDEBAR_W = 220;

const DEFAULT_LAYOUT = 'titleContent';

const SLIDE_LAYOUTS = [
  {
    key: 'title',
    label: 'Başlık',
    description: 'Büyük başlık ve alt başlık',
  },
  {
    key: 'titleContent',
    label: 'Başlık + İçerik',
    description: 'Üstte başlık, altta içerik alanı',
  },
  {
    key: 'twoColumn',
    label: 'İki Sütun',
    description: 'Başlık ve iki içerik sütunu',
  },
  {
    key: 'comparison',
    label: 'Karşılaştırma',
    description: 'İki başlık ve karşılaştırma alanları',
  },
  {
    key: 'full',
    label: 'Tam Genişlik',
    description: 'Grafik ya da görsel için geniş alan',
  },
  {
    key: 'blank',
    label: 'Boş',
    description: 'Serbest yerleşim',
  },
];

const SLIDE_LAYOUT_MAP = SLIDE_LAYOUTS.reduce((acc, layout) => {
  acc[layout.key] = layout;
  return acc;
}, {});


const stylesEqual = (a, b) =>
  a.bold === b.bold &&
  a.italic === b.italic &&
  a.underline === b.underline &&
  a.color === b.color &&
  a.fontFamily === b.fontFamily &&
  a.fontSize === b.fontSize;

const richTextHtmlToPptxRuns = (html, baseStyle = {}) => {
  const defaultStyle = {
    bold: !!baseStyle.bold,
    italic: !!baseStyle.italic,
    underline: !!baseStyle.underline,
    color: baseStyle.color || DEFAULT_TEXT_STYLE.color,
    fontFamily: baseStyle.fontFamily || DEFAULT_TEXT_STYLE.fontFamily,
    fontSize: normaliseFontSize(baseStyle.fontSize, DEFAULT_TEXT_STYLE.fontSize),
  };

  if (!html || typeof html !== 'string') {
    return [
      {
        text: '',
        options: {
          bold: defaultStyle.bold || undefined,
          italic: defaultStyle.italic || undefined,
          underline: defaultStyle.underline || undefined,
          color: toPptxColor(defaultStyle.color),
          fontFace: defaultStyle.fontFamily,
          fontSize: defaultStyle.fontSize,
        },
      },
    ];
  }

  if (!canUseDom()) {
    const plain = extractPlainTextFromRichText(html);
    return [
      {
        text: plain,
        options: {
          bold: defaultStyle.bold || undefined,
          italic: defaultStyle.italic || undefined,
          underline: defaultStyle.underline || undefined,
          color: toPptxColor(defaultStyle.color),
          fontFace: defaultStyle.fontFamily,
          fontSize: defaultStyle.fontSize,
        },
      },
    ];
  }

  const container = document.createElement('div');
  container.innerHTML = html;

  const runs = [];

  const pushRun = (text, style) => {
    if (!text) return;
    const normalisedText = text.replace(/\u00a0/g, ' ');
    const last = runs[runs.length - 1];
    if (last && last.text !== '\n' && stylesEqual(last.style, style)) {
      last.text += normalisedText;
    } else {
      runs.push({ text: normalisedText, style: { ...style } });
    }
  };

  const traverse = (node, currentStyle) => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushRun(node.nodeValue, currentStyle);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();
    if (tag === 'br') {
      pushRun('\n', currentStyle);
      return;
    }

    let nextStyle = { ...currentStyle };

    if (tag === 'b' || tag === 'strong') nextStyle.bold = true;
    if (tag === 'i' || tag === 'em') nextStyle.italic = true;
    if (tag === 'u') nextStyle.underline = true;

    const styleRules = parseInlineStyleRules(node.getAttribute('style'));
    if (styleRules.color) {
      nextStyle.color = styleRules.color;
    }
    if (styleRules['font-weight'] && styleRules['font-weight'].includes('bold')) {
      nextStyle.bold = true;
    }
    if (styleRules['font-style'] && styleRules['font-style'].includes('italic')) {
      nextStyle.italic = true;
    }
    if (styleRules['text-decoration'] && styleRules['text-decoration'].includes('underline')) {
      nextStyle.underline = true;
    }
    if (styleRules['font-family']) {
      nextStyle.fontFamily = styleRules['font-family'].replace(/['"]/g, '').trim();
    }
    if (styleRules['font-size']) {
      const parsedSize = parseFloat(styleRules['font-size']);
      if (Number.isFinite(parsedSize)) {
        nextStyle.fontSize = parsedSize;
      }
    }

    const isBlock = tag === 'div' || tag === 'p';
    const children = Array.from(node.childNodes);
    children.forEach((child) => traverse(child, nextStyle));
    if (isBlock && children.length) {
      pushRun('\n', currentStyle);
    }
  };

  Array.from(container.childNodes).forEach((child) => traverse(child, defaultStyle));

  while (runs.length && runs[runs.length - 1].text === '\n') {
    runs.pop();
  }

  if (!runs.length) {
    return [
      {
        text: '',
        options: {
          bold: defaultStyle.bold || undefined,
          italic: defaultStyle.italic || undefined,
          underline: defaultStyle.underline || undefined,
          color: toPptxColor(defaultStyle.color),
          fontFace: defaultStyle.fontFamily,
          fontSize: defaultStyle.fontSize,
        },
      },
    ];
  }

  return runs.map((run) => ({
    text: run.text,
    options: {
      bold: run.style.bold || undefined,
      italic: run.style.italic || undefined,
      underline: run.style.underline || undefined,
      color: toPptxColor(run.style.color || defaultStyle.color),
      fontFace: run.style.fontFamily || defaultStyle.fontFamily,
      fontSize: normaliseFontSize(run.style.fontSize, defaultStyle.fontSize),
    },
  }));
};


const MIN_TEXT_ITEM_HEIGHT = 48;
const TEXT_AUTO_GROW_PADDING = 6;

const createTextItem = ({
  text,
  richText,
  x,
  y,
  w,
  h,
  style = {},
  id = uuid(),
}) => {
  const plainText = typeof text === 'string' ? text : '';
  const baseHtml = ensureRichTextValue(
    typeof richText === 'string' ? richText : '',
    plainText
  );
  return {
    id,
    type: 'text',
    text: extractPlainTextFromRichText(baseHtml) || plainText,
    richText: baseHtml,
    style: { ...DEFAULT_TEXT_STYLE, ...style },
    x,
    y,
    w,
    h,
  };
};

const createLayoutItems = (layoutKey = DEFAULT_LAYOUT) => {
  const makeTitle = (text, overrides = {}) =>
    createTextItem({
      text,
      x: 80,
      y: 60,
      w: CANVAS_W - 160,
      h: 100,
      style: { fontSize: 42, bold: true, align: 'center', ...overrides },
    });

  switch (layoutKey) {
    case 'title':
      return [
        makeTitle('Başlık girin'),
        createTextItem({
          text: 'Alt başlığı yazın',
          x: 120,
          y: 220,
          w: CANVAS_W - 240,
          h: 120,
          style: { fontSize: 24, align: 'center', color: '#334155' },
        }),
      ];
    case 'twoColumn':
      return [
        createTextItem({
          text: 'Başlık',
          x: 80,
          y: 50,
          w: CANVAS_W - 160,
          h: 80,
          style: { fontSize: 36, bold: true, align: 'left' },
        }),
        createTextItem({
          text: 'Sol sütun içeriği',
          x: 80,
          y: 150,
          w: (CANVAS_W - 200) / 2,
          h: 320,
          style: { fontSize: 18, align: 'left' },
        }),
        createTextItem({
          text: 'Sağ sütun içeriği',
          x: 80 + (CANVAS_W - 200) / 2 + 40,
          y: 150,
          w: (CANVAS_W - 200) / 2,
          h: 320,
          style: { fontSize: 18, align: 'left' },
        }),
      ];
    case 'comparison':
      return [
        createTextItem({
          text: 'Karşılaştırma Başlığı',
          x: 80,
          y: 50,
          w: CANVAS_W - 160,
          h: 80,
          style: { fontSize: 36, bold: true, align: 'left' },
        }),
        createTextItem({
          text: 'Sol Başlık',
          x: 80,
          y: 150,
          w: (CANVAS_W - 200) / 2,
          h: 60,
          style: { fontSize: 22, bold: true, align: 'left' },
        }),
        createTextItem({
          text: 'Sağ Başlık',
          x: 80 + (CANVAS_W - 200) / 2 + 40,
          y: 150,
          w: (CANVAS_W - 200) / 2,
          h: 60,
          style: { fontSize: 22, bold: true, align: 'left' },
        }),
        createTextItem({
          text: 'Sol içerik alanı',
          x: 80,
          y: 230,
          w: (CANVAS_W - 200) / 2,
          h: 240,
          style: { fontSize: 18, align: 'left' },
        }),
        createTextItem({
          text: 'Sağ içerik alanı',
          x: 80 + (CANVAS_W - 200) / 2 + 40,
          y: 230,
          w: (CANVAS_W - 200) / 2,
          h: 240,
          style: { fontSize: 18, align: 'left' },
        }),
      ];
    case 'full':
      return [
        createTextItem({
          text: 'Başlık',
          x: 80,
          y: 40,
          w: CANVAS_W - 160,
          h: 80,
          style: { fontSize: 34, bold: true, align: 'left' },
        }),
        createTextItem({
          text: 'İçeriğinizi yerleştirin veya bir grafik ekleyin.',
          x: 80,
          y: 140,
          w: CANVAS_W - 160,
          h: CANVAS_H - 200,
          style: { fontSize: 18, align: 'left', color: '#475569' },
        }),
      ];
    case 'blank':
      return [];
    case 'titleContent':
    default:
      return [
        createTextItem({
          text: 'Başlık',
          x: 80,
          y: 60,
          w: CANVAS_W - 160,
          h: 90,
          style: { fontSize: 38, bold: true, align: 'left' },
        }),
        createTextItem({
          text: 'İçeriği buraya yazın. Metin, grafik veya tablo yerleştirin.',
          x: 120,
          y: 180,
          w: CANVAS_W - 240,
          h: 260,
          style: { fontSize: 18, align: 'left', color: '#475569' },
        }),
      ];
  }
};

const createDefaultSlide = (index = 0) => {
  const layoutKey = DEFAULT_LAYOUT;
  return {
    id: uuid(),
    name: `Sayfa ${index + 1}`,
    items: createLayoutItems(layoutKey),
    layout: layoutKey,
  };
};

const toFiniteNumber = (value, fallback) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
};

const stripUndefined = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
};

const rawPexelsKey = process.env.REACT_APP_PEXELS_API_KEY;
const PEXELS_FALLBACK_KEY = 'VQhYbYlpAbCKxEdeRnrC3cZIGNpJa58ZOTGtdJxI4PBUvoQvSj1E2DHE';
const PEXELS_API_KEY = typeof rawPexelsKey === 'string' && rawPexelsKey.trim()
  ? rawPexelsKey.trim()
  : PEXELS_FALLBACK_KEY;
const PEXELS_PAGE_SIZE = 18;

const normalisePexelsPhoto = (photo) => {
  if (!photo || typeof photo !== 'object') return null;
  const src = photo.src || {};
  const id = photo.id != null ? String(photo.id) : undefined;
  const primary = src.large2x || src.large || src.original || src.landscape || '';
  const medium = src.medium || src.large || src.landscape || src.small || primary;
  const thumb = src.small || src.tiny || src.portrait || medium;
  if (!primary) return null;
  return {
    id,
    width: Number(photo.width) || null,
    height: Number(photo.height) || null,
    photographer: typeof photo.photographer === 'string' ? photo.photographer : '',
    photographerUrl: typeof photo.photographer_url === 'string' ? photo.photographer_url : '',
    url: typeof photo.url === 'string' ? photo.url : '',
    srcLarge: primary,
    srcMedium: medium,
    srcSmall: thumb,
  };
};

const fetchPexelsPhotos = async ({ query = '', page = 1, perPage = PEXELS_PAGE_SIZE } = {}) => {
  const safePage = Math.max(1, Number(page) || 1);
  const safePerPage = Math.max(1, Math.min(80, Number(perPage) || PEXELS_PAGE_SIZE));
  const trimmed = String(query || '').trim();
  const params = new URLSearchParams({ per_page: String(safePerPage), page: String(safePage) });
  const endpoint = trimmed
    ? `https://api.pexels.com/v1/search`
    : `https://api.pexels.com/v1/curated`;
  if (trimmed) {
    params.set('query', trimmed);
  }
  const res = await fetch(`${endpoint}?${params.toString()}`, {
    headers: {
      Authorization: PEXELS_API_KEY,
    },
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(message || 'Pexels API isteği başarısız oldu.');
  }
  const data = await res.json();
  const photosRaw = Array.isArray(data?.photos) ? data.photos : [];
  const photos = photosRaw.map(normalisePexelsPhoto).filter(Boolean);
  const totalResults = Number(data?.total_results) || 0;
  const hasMore = Boolean(data?.next_page) || safePage * safePerPage < totalResults;
  return {
    photos,
    totalResults,
    hasMore,
    page: safePage,
  };
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Görsel verisi okunamadı.'));
    reader.readAsDataURL(blob);
  });

const fetchImageAsDataUrl = async (url) => {
  const res = await fetch(url, { mode: 'cors' });
  if (!res.ok) {
    throw new Error('Görsel indirilemedi.');
  }
  const blob = await res.blob();
  return blobToDataUrl(blob);
};

const sanitizeTextStyle = (style = {}) => {
  const merged = { ...DEFAULT_TEXT_STYLE, ...(style || {}) };
  const alignments = ['left', 'center', 'right', 'justify'];
  return {
    fontSize: toFiniteNumber(merged.fontSize, DEFAULT_TEXT_STYLE.fontSize),
    color: typeof merged.color === 'string' ? merged.color : DEFAULT_TEXT_STYLE.color,
    fontFamily:
      typeof merged.fontFamily === 'string' && merged.fontFamily
        ? merged.fontFamily
        : DEFAULT_TEXT_STYLE.fontFamily,
    align: alignments.includes(merged.align) ? merged.align : DEFAULT_TEXT_STYLE.align,
    bold: !!merged.bold,
    italic: !!merged.italic,
    underline: !!merged.underline,
  };
};

const sanitizeGraphOverrides = (overrides) => {
  if (!overrides || typeof overrides !== 'object') return undefined;
  const cleaned = stripUndefined({
    ...overrides,
    colors: Array.isArray(overrides.colors)
      ? overrides.colors.filter((clr) => typeof clr === 'string' && clr)
      : undefined,
    settings: overrides.settings && typeof overrides.settings === 'object'
      ? stripUndefined(overrides.settings)
      : undefined,
  });
  return Object.keys(cleaned).length ? cleaned : undefined;
};

const sanitizeSlideItem = (item, fallbackId = null) => {
  if (!item || typeof item !== 'object') return null;
  const type = item.type || 'text';
  const base = {
    id: typeof item.id === 'string' && item.id ? item.id : fallbackId || uuid(),
    type,
    x: clamp(toFiniteNumber(item.x, 0), 0, CANVAS_W),
    y: clamp(toFiniteNumber(item.y, 0), 0, CANVAS_H),
    w: clamp(toFiniteNumber(item.w, GRID_SIZE * 4), GRID_SIZE, CANVAS_W),
    h: clamp(toFiniteNumber(item.h, GRID_SIZE * 4), GRID_SIZE, CANVAS_H),
  };

  switch (type) {
    case 'text': {
      const rawText = typeof item.text === 'string' ? item.text : '';
      const html = ensureRichTextValue(item.richText, rawText);
      return {
        ...base,
        richText: html,
        text: extractPlainTextFromRichText(html) || rawText,
        style: sanitizeTextStyle(item.style),
      };
    }
    case 'graph': {
      const graphIndex = toFiniteNumber(item.graphIndex, -1);
      const cleaned = {
        ...base,
        graphIndex: Number.isInteger(graphIndex) ? graphIndex : -1,
      };
      const overrides = sanitizeGraphOverrides(item.overrides);
      if (overrides) cleaned.overrides = overrides;
      return cleaned;
    }
    case 'table': {
      const tableLink = sanitizeTableLink(item.tableLink);
      return {
        ...base,
        table: decodeTableFromFirestore(item.table),
        ...(tableLink ? { tableLink } : {}),
      };
    }
    case 'shape':
      return {
        ...base,
        rotation: normalizeRotation(item.rotation),
        shape: ensureShapeConfig(item.shape),
      };
    case 'icon': {
      const rawName = typeof item.icon === 'string' ? item.icon : '';
      const iconName = LUCIDE_ICON_SET.has(rawName) ? rawName : LUCIDE_ICON_FALLBACK;
      const color =
        typeof item.color === 'string' && item.color ? item.color : DEFAULT_ICON_COLOR;
      const strokeWidthRaw = toFiniteNumber(item.strokeWidth, DEFAULT_ICON_STROKE);
      const strokeWidth = clamp(
        Number.isFinite(strokeWidthRaw) ? strokeWidthRaw : DEFAULT_ICON_STROKE,
        ICON_STROKE_MIN,
        ICON_STROKE_MAX
      );
      const sizeBase = Math.min(base.w, base.h);
      const snappedSize = Math.max(
        GRID_SIZE * 2,
        Math.round(sizeBase / GRID_SIZE) * GRID_SIZE
      );
      const finalSize = clamp(
        snappedSize,
        GRID_SIZE * 2,
        Math.min(CANVAS_W, CANVAS_H)
      );
      return {
        ...base,
        type: 'icon',
        w: finalSize,
        h: finalSize,
        icon: iconName,
        color,
        strokeWidth,
      };
    }
    case 'image': {
      const src = typeof item.src === 'string' && item.src ? item.src : '';
      if (!src) return null;
      const opacityRaw = toFiniteNumber(item.opacity, 1);
      const metaSource = item.meta && typeof item.meta === 'object' ? item.meta : null;
      const meta = metaSource
        ? stripUndefined({
            source:
              typeof metaSource.source === 'string' && metaSource.source
                ? metaSource.source
                : 'pexels',
            photographer:
              typeof metaSource.photographer === 'string' ? metaSource.photographer : undefined,
            photographerUrl:
              typeof metaSource.photographerUrl === 'string' ? metaSource.photographerUrl : undefined,
            url: typeof metaSource.url === 'string' ? metaSource.url : undefined,
            pexelsId:
              metaSource.pexelsId != null ? String(metaSource.pexelsId) : undefined,
          })
        : undefined;
      return {
        ...base,
        type: 'image',
        src,
        opacity: clamp(Number.isFinite(opacityRaw) ? opacityRaw : 1, 0, 1),
        ...(meta ? { meta } : {}),
      };
    }
    default:
      return base;
  }
};

const normalizeSlides = (rawSlides) => {
  if (!Array.isArray(rawSlides) || rawSlides.length === 0) {
    return [createDefaultSlide(0)];
  }

  return rawSlides.map((slide, idx) => {
    const layout = SLIDE_LAYOUT_MAP[slide?.layout] ? slide.layout : DEFAULT_LAYOUT;
    const id = typeof slide?.id === 'string' && slide.id ? slide.id : uuid();
    const name =
      typeof slide?.name === 'string' && slide.name.trim()
        ? slide.name
        : `Sayfa ${idx + 1}`;
    const itemsSrc = Array.isArray(slide?.items) ? slide.items : [];
    const items = itemsSrc
      .map((item, itemIdx) => sanitizeSlideItem(item, `${id}:${itemIdx}`))
      .filter(Boolean);

    return {
      id,
      name,
      layout,
      items,
    };
  });
};

const prepareSlidesForSave = (slides) => {
  const normalized = normalizeSlides(slides);
  return normalized.map((slide) => ({
    ...slide,
    items: slide.items.map((item) => {
      if (item.type === 'table') {
        return {
          ...item,
          table: encodeTableForFirestore(item.table),
        };
      }
      return item;
    }),
  }));
};

/* ---------------- Grafik varsayılan ayarları ------------------ */
const DEFAULT_SETTINGS = {
  legend: true,
  dataLabels: false,
  dataLabelContent: 'auto',
  dataLabelPosition: 'inside',
  dataLabelFontFamily: FONTS[0],
  dataLabelFontSize: 11,
  dataLabelAngle: 0,
  dataLabelDigits: null,
  gridLines: true,
  autoScale: true,
  visibleAnswers: null,
  baseType: 'none',
  axisFontFamily: FONTS[0],
  axisFontSize: 11,
  axisAngle: 0,
};

const ORIENTABLE_TYPES = ['bar', 'histogram', 'barStacked'];

const ROTATION_OPTIONS = [
  { value: 0, label: '0°' },
  { value: -45, label: '-45°' },
  { value: -90, label: '-90°' },
];

/* ---------------- Yardımcı fonksiyonlar ----------------------- */
const sortData = (data, key, order) => {
  if (order === 'none') return data;
  const factor = order === 'asc' ? 1 : -1;
  return [...data].sort((a, b) => (a[key] - b[key]) * factor);
};

const isCompatible = (type, d) => {
  switch (type) {
    case 'histogram':
      return d?.histogram?.bins && d.histogram?.counts;
    case 'bar':
    case 'line':
    case 'area':
    case 'pie':
    case 'scatter':
    case 'radar':
      return Array.isArray(d?.labels) && Array.isArray(d?.counts);
    case 'barStacked':
      return Array.isArray(d?.records) && Array.isArray(d?.series) && d.series.length > 0;
    case 'table':
      return Array.isArray(d?.labels) && Array.isArray(d?.counts);
    default:
      return false;
  }
};

const coerceTrimmed = (val) => {
  if (val == null) return '';
  const str = typeof val === 'string' ? val : String(val);
  return str.trim();
};

const extractGraphMeta = (graph) => {
  if (!graph || typeof graph !== 'object') {
    return { analysisName: '', groupName: '' };
  }

  const analysisName =
    coerceTrimmed(graph.__analysisName) ||
    coerceTrimmed(graph.analysisName) ||
    coerceTrimmed(graph.meta?.analysisName) ||
    coerceTrimmed(graph.meta?.analysis_name) ||
    coerceTrimmed(graph.__analysisId) ||
    coerceTrimmed(graph.analysisId) ||
    coerceTrimmed(graph.meta?.analysisId) ||
    coerceTrimmed(graph.meta?.analysis_id);

  const groupName =
    coerceTrimmed(graph.__groupName) ||
    coerceTrimmed(graph.groupName) ||
    coerceTrimmed(graph.group_name) ||
    coerceTrimmed(graph.meta?.groupName) ||
    coerceTrimmed(graph.meta?.group_name) ||
    coerceTrimmed(graph.__groupId) ||
    coerceTrimmed(graph.groupId) ||
    coerceTrimmed(graph.group_id) ||
    coerceTrimmed(graph.meta?.groupId) ||
    coerceTrimmed(graph.meta?.group_id);

  return {
    analysisName,
    groupName,
  };
};

/* ---------------- GLOBAL BRAND COLOR DIALOG ------------------ */
function BrandColorDialog({ open, onClose, value = [], onChange }) {
  const [brand, setBrand] = useState('');
  const [color, setColor] = useState('#6366f1');

  const norm = (s) => String(s || '').trim();
  const normKey = (s) => norm(s).toLowerCase();

  const addOrUpdate = () => {
    const b = norm(brand);
    if (!b) return;
    const key = normKey(b);
    const existsIdx = value.findIndex((x) => normKey(x.name) === key);
    if (existsIdx >= 0) {
      const next = value.map((x, i) => (i === existsIdx ? { ...x, color } : x));
      onChange(next);
    } else {
      onChange([...(value || []), { name: b, color }]);
    }
    setBrand('');
  };

  const updateColorAt = (i, c) => {
    const next = value.map((x, idx) => (idx === i ? { ...x, color: c } : x));
    onChange(next);
  };

  const deleteAt = (i) => {
    const next = value.filter((_, idx) => idx !== i);
    onChange(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth disableScrollLock>
      <DialogTitle>Global Marka Rengi</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            label="Marka adı"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            size="small"
            fullWidth
          />
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            style={{ width: 44, height: 36, border: 'none', padding: 0 }}
            title="Renk seç"
          />
          <Button variant="contained" onClick={addOrUpdate}>Ekle/Güncelle</Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1}>
          {value.length === 0 && (
            <Typography variant="body2" color="text.secondary">Henüz marka eklenmedi.</Typography>
          )}
          {value.map((bc, idx) => (
            <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 16, height: 16, bgcolor: bc.color, border: '1px solid #e5e7eb', borderRadius: 0.5 }} />
                  <Typography variant="body2">{bc.name}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <input
                    type="color"
                    value={bc.color}
                    onChange={(e) => updateColorAt(idx, e.target.value)}
                    style={{ width: 32, height: 28, border: 'none', padding: 0 }}
                    title="Rengi değiştir"
                  />
                  <IconButton size="small" onClick={() => deleteAt(idx)}>
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------- SETTINGS DIALOG ------------------------ */
function ChartSettingsDialog({ open, onClose, settings, onSave }) {
  const [local, setLocal] = useState(settings);
  useEffect(() => setLocal(settings), [settings]);
  const handleChange = (key) => (e) => setLocal((p) => ({ ...p, [key]: e.target.checked }));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth disableScrollLock>
      <DialogTitle>Grafik Ayarları</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox checked={local.legend} onChange={handleChange('legend')} />
            <Typography>Legend</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox checked={local.dataLabels} onChange={handleChange('dataLabels')} />
            <Typography>Veri Etiketleri</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox checked={local.gridLines} onChange={handleChange('gridLines')} />
            <Typography>Izgara Çizgileri</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Checkbox checked={local.autoScale} onChange={handleChange('autoScale')} />
            <Typography>Otomatik Ölçek</Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Vazgeç</Button>
        <Button variant="contained" onClick={() => onSave(local)}>
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}
/* ---------------- CEVAP GÖSTER/GİZLE DLG --------------------- */
function AnswerSelectDialog({ open, onClose, options = [], selected = [], onSave }) {
  const [local, setLocal] = useState(selected);
  useEffect(() => setLocal(selected), [selected]);
  const toggle = (opt) =>
    setLocal((p) => (p.includes(opt) ? p.filter((o) => o !== opt) : [...p, opt]));
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth disableScrollLock>
      <DialogTitle>Cevapları Göster/Gizle</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: 400 }}>
        {options.map((opt) => (
          <Stack key={opt} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Checkbox checked={local.includes(opt)} onChange={() => toggle(opt)} />
            <Typography variant="body2">{opt}</Typography>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Vazgeç</Button>
        <Button variant="contained" onClick={() => onSave(local.length === options.length ? null : local)}>
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------- TEMPLATE (GLOBAL BACKGROUND) DIALOG -------- */
function TemplateDialog({ open, initial, onClose }) {
  const [dataUrl, setDataUrl] = useState(initial?.dataUrl || '');
  const [mode, setMode] = useState(initial?.mode || 'cover'); // cover | contain | stretch
  const [opacity, setOpacity] = useState(
    typeof initial?.opacity === 'number' ? initial.opacity : 1
  );
  const [meta, setMeta] = useState(initial?.meta || null);

  const [queryInput, setQueryInput] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setDataUrl(initial?.dataUrl || '');
    setMode(initial?.mode || 'cover');
    setOpacity(typeof initial?.opacity === 'number' ? initial.opacity : 1);
    setMeta(initial?.meta || null);
    setQueryInput('');
    setActiveQuery('');
    setResults([]);
    setPage(1);
    setHasMore(false);
    setError('');
    setDownloadingId(null);
  }, [open, initial]);

  const loadPhotos = useCallback(async (queryValue, nextPage = 1, append = false) => {
    const q = typeof queryValue === 'string' ? queryValue : '';
    const trimmed = q.trim();
    setLoading(true);
    if (!append) {
      setError('');
    }
    try {
      const { photos, hasMore: nextHasMore, page: returnedPage } = await fetchPexelsPhotos({
        query: trimmed,
        page: nextPage,
      });
      setResults((prev) => (append ? [...prev, ...photos] : photos));
      setHasMore(nextHasMore);
      setPage(returnedPage);
      setActiveQuery(trimmed);
    } catch (err) {
      setError(err.message || 'Pexels görselleri yüklenemedi.');
      if (!append) {
        setResults([]);
        setHasMore(false);
        setPage(1);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      if (results.length) return;
      try {
        if (!cancelled) {
          await loadPhotos('', 1, false);
        }
      } catch {
        // state already handled
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, loadPhotos, results.length]);

  const handleSearchSubmit = useCallback(
    (event) => {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      loadPhotos(queryInput, 1, false);
    },
    [loadPhotos, queryInput]
  );

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    loadPhotos(activeQuery, page + 1, true);
  }, [loadPhotos, loading, hasMore, activeQuery, page]);

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setDataUrl(String(e.target.result || ''));
      setMeta(null);
    };
    reader.readAsDataURL(file);
  };

  const handleUsePexelsPhoto = async (photo) => {
    if (!photo || !photo.srcLarge) return;
    const key = photo.id || photo.srcLarge;
    setDownloadingId(key);
    setError('');
    try {
      const imageDataUrl = await fetchImageAsDataUrl(photo.srcLarge);
      setDataUrl(String(imageDataUrl || ''));
      setMeta({
        source: 'pexels',
        photographer: photo.photographer,
        photographerUrl: photo.photographerUrl,
        url: photo.url,
        pexelsId: photo.id,
      });
    } catch (err) {
      setError(err.message || 'Görsel indirilemedi.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleClear = () => {
    setDataUrl('');
    setMeta(null);
  };

  return (
    <Dialog open={open} onClose={() => onClose(null)} maxWidth="md" fullWidth disableScrollLock>
      <DialogTitle>Şablon (Tüm Sayfalar İçin Arka Plan)</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Button variant="outlined" component="label">
              Görsel Yükle
              <input hidden type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} />
            </Button>
            {dataUrl && (
              <Button color="error" onClick={handleClear}>
                Temizle
              </Button>
            )}
          </Stack>

          {dataUrl && (
            <Box sx={{ border: '1px solid #e5e7eb', p: 1, borderRadius: 1 }}>
              <Box
                component="img"
                src={dataUrl}
                alt="Şablon önizleme"
                sx={{ width: '100%', maxHeight: 260, objectFit: 'contain', borderRadius: 1 }}
              />
              {meta?.photographer && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Fotoğraf: {meta.photographer}
                </Typography>
              )}
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <FormControl size="small" sx={{ width: { xs: '100%', sm: 220 } }}>
              <InputLabel>Yerleşim</InputLabel>
              <Select
                label="Yerleşim"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                MenuProps={{ disableScrollLock: true }}
              >
                <MenuItem value="cover">Kapla (cover)</MenuItem>
                <MenuItem value="contain">Sığdır (contain)</MenuItem>
                <MenuItem value="stretch">Esnet (stretch)</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption">Opaklık: {Math.round(opacity * 100)}%</Typography>
              <Slider
                size="small"
                value={opacity}
                onChange={(_, v) => setOpacity(Array.isArray(v) ? v[0] : v)}
                step={0.05}
                min={0.2}
                max={1}
              />
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Pexels'ten Görsel Ara</Typography>
            <Stack
              component="form"
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              onSubmit={handleSearchSubmit}
            >
              <TextField
                size="small"
                placeholder="Örn. zeytin, doğa, şehir"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<ImageSearchIcon fontSize="small" />}
                disabled={loading}
              >
                Ara
              </Button>
            </Stack>
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 1.5,
                  maxHeight: 320,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {results.map((photo) => {
                  const key = photo.id || photo.srcLarge;
                  const busy = downloadingId && downloadingId === key;
                  return (
                    <Paper key={key} variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
                      <Box
                        component="img"
                        src={photo.srcMedium}
                        alt={photo.photographer || 'Pexels görseli'}
                        sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                      />
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                          {photo.photographer || 'Pexels'}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleUsePexelsPhoto(photo)}
                          disabled={busy}
                          sx={{ minWidth: 88 }}
                        >
                          {busy ? <CircularProgress size={16} color="inherit" /> : 'Kullan'}
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: results.length ? 'auto 0 0 0' : '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: results.length ? 1.5 : 4,
                    backgroundColor: results.length ? 'rgba(255,255,255,0.72)' : 'transparent',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
            {!loading && !results.length && (
              <Typography variant="caption" color="text.secondary">
                Henüz görsel bulunamadı. Yeni bir arama yapabilirsiniz.
              </Typography>
            )}
            {hasMore && (
              <Button onClick={handleLoadMore} disabled={loading} variant="outlined" sx={{ alignSelf: 'flex-start' }}>
                Daha Fazla Yükle
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Vazgeç</Button>
        <Button
          variant="contained"
          onClick={() =>
            onClose(
              dataUrl
                ? {
                    dataUrl,
                    mode,
                    opacity,
                    ...(meta ? { meta } : {}),
                  }
                : null
            )
          }
          disabled={!dataUrl}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ---------------- IMAGE PICKER DIALOG ----------------------- */
function ImagePickerDialog({ open, onClose, title = 'Sunuma Görsel Ekle', existingItem = null }) {
  const [queryInput, setQueryInput] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [results, setResults] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQueryInput('');
    setActiveQuery('');
    setResults([]);
    setPage(1);
    setHasMore(false);
    setError('');
    setDownloadingId(null);
  }, [open]);

  const loadPhotos = useCallback(async (queryValue, nextPage = 1, append = false) => {
    const q = typeof queryValue === 'string' ? queryValue : '';
    const trimmed = q.trim();
    setLoading(true);
    if (!append) {
      setError('');
    }
    try {
      const { photos, hasMore: nextHasMore, page: returnedPage } = await fetchPexelsPhotos({
        query: trimmed,
        page: nextPage,
      });
      setResults((prev) => (append ? [...prev, ...photos] : photos));
      setHasMore(nextHasMore);
      setPage(returnedPage);
      setActiveQuery(trimmed);
    } catch (err) {
      setError(err.message || 'Pexels görselleri yüklenemedi.');
      if (!append) {
        setResults([]);
        setHasMore(false);
        setPage(1);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      if (results.length) return;
      try {
        if (!cancelled) {
          await loadPhotos('', 1, false);
        }
      } catch {
        // already handled via error state
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, loadPhotos, results.length]);

  const handleSearchSubmit = useCallback(
    (event) => {
      if (event && typeof event.preventDefault === 'function') event.preventDefault();
      loadPhotos(queryInput, 1, false);
    },
    [loadPhotos, queryInput]
  );

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    loadPhotos(activeQuery, page + 1, true);
  }, [loadPhotos, loading, hasMore, activeQuery, page]);

  const handleUsePexelsPhoto = async (photo) => {
    if (!photo || !photo.srcLarge) return;
    const key = photo.id || photo.srcLarge;
    setDownloadingId(key);
    setError('');
    try {
      const imageDataUrl = await fetchImageAsDataUrl(photo.srcLarge);
      setDownloadingId(null);
      onClose({
        dataUrl: String(imageDataUrl || ''),
        width: photo.width,
        height: photo.height,
        meta: {
          source: 'pexels',
          photographer: photo.photographer,
          photographerUrl: photo.photographerUrl,
          url: photo.url,
          pexelsId: photo.id,
        },
      });
    } catch (err) {
      setError(err.message || 'Görsel indirilemedi.');
      setDownloadingId(null);
    }
  };

  const handleUpload = (file) => {
    if (!file) return;
    setDownloadingId('upload');
    setError('');
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const dataUrl = String(e.target.result || '');
        setDownloadingId(null);
        onClose({
          dataUrl,
          width: null,
          height: null,
          meta: {
            source: 'upload',
            name: file.name,
          },
        });
      } catch (err) {
        setError(err.message || 'Görsel eklenemedi.');
        setDownloadingId(null);
      }
    };
    reader.onerror = () => {
      setError('Dosya okunamadı.');
      setDownloadingId(null);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(null)}
      maxWidth="md"
      fullWidth
      disableScrollLock
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <Typography variant="body2" color="text.secondary">
            Pexels araması veya yerel dosya yüklemesi ile sunumunuza yüksek kaliteli görseller ekleyin.
          </Typography>

          {existingItem?.src && (
            <Box sx={{ border: '1px dashed #cbd5f5', p: 1, borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Mevcut görsel:
              </Typography>
              <Box
                component="img"
                src={existingItem.src}
                alt="Seçili görsel"
                sx={{ width: '100%', maxHeight: 200, objectFit: 'contain', mt: 0.5, borderRadius: 1 }}
              />
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Button
              variant="outlined"
              component="label"
              disabled={downloadingId === 'upload'}
            >
              Yerel Görsel Yükle
              <input
                hidden
                type="file"
                accept="image/*"
                onChange={(e) => handleUpload(e.target.files?.[0])}
              />
            </Button>
            {downloadingId === 'upload' && <CircularProgress size={20} />}
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            <Typography variant="subtitle2">Pexels Araması</Typography>
            <Stack
              component="form"
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              onSubmit={handleSearchSubmit}
            >
              <TextField
                size="small"
                placeholder="Örn. ofis, doğa, teknoloji"
                value={queryInput}
                onChange={(e) => setQueryInput(e.target.value)}
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                startIcon={<ImageSearchIcon fontSize="small" />}
                disabled={loading}
              >
                Ara
              </Button>
            </Stack>
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
            <Box sx={{ position: 'relative' }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
                  gap: 1.5,
                  maxHeight: 360,
                  overflowY: 'auto',
                  pr: 0.5,
                }}
              >
                {results.map((photo) => {
                  const key = photo.id || photo.srcLarge;
                  const busy = downloadingId && downloadingId === key;
                  return (
                    <Paper key={key} variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
                      <Box
                        component="img"
                        src={photo.srcMedium}
                        alt={photo.photographer || 'Pexels görseli'}
                        sx={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }}
                      />
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.75 }}>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                          {photo.photographer || 'Pexels'}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busy}
                          onClick={() => handleUsePexelsPhoto(photo)}
                          sx={{ minWidth: 88 }}
                        >
                          {busy ? <CircularProgress size={16} color="inherit" /> : 'Ekle'}
                        </Button>
                      </Stack>
                    </Paper>
                  );
                })}
              </Box>
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: results.length ? 'auto 0 0 0' : '0',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: results.length ? 1.5 : 4,
                    backgroundColor: results.length ? 'rgba(255,255,255,0.72)' : 'transparent',
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
            {!loading && !results.length && (
              <Typography variant="caption" color="text.secondary">
                Arama yapılmadı veya sonuç bulunamadı.
              </Typography>
            )}
            {hasMore && (
              <Button onClick={handleLoadMore} disabled={loading} variant="outlined" sx={{ alignSelf: 'flex-start' }}>
                Daha Fazla Yükle
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
}

/* -------------------- GRAPH VIEW ----------------------------- */
const GraphView = memo(function GraphView({ baseGraph, overrides, brandColorMap = {}, fallbackPalette = null }) {
  // 1) base + (deferred) overrides
  const deferredOverrides = useDeferredValue(overrides);
  const graph = useMemo(
    () => ({ ...baseGraph, ...deferredOverrides, data: baseGraph.data }),
    [baseGraph, deferredOverrides]
  );

  // Capture explicit orientation override
  const explicitOrientation = deferredOverrides?.orientation;

  const chartType   = graph.graphType;
  const normalizeOrientation = (ori, type) => {
    if (!ORIENTABLE_TYPES.includes(type)) return undefined;
    const v = String(ori || '').toLowerCase();
    if (v.startsWith('h')) return 'horizontal';
    if (v.startsWith('v')) return 'vertical';
    return undefined;
  };
  const orientation = normalizeOrientation(explicitOrientation ?? graph.orientation, chartType)
    || (ORIENTABLE_TYPES.includes(chartType) ? 'vertical' : 'horizontal');
  const metric      = graph.metric || 'count';
  const sortOrder   = graph.sortOrder || 'none';
  const settings    = { ...DEFAULT_SETTINGS, ...(graph.settings || {}) };
  const primaryColor = graph.color || DEFAULT_COLORS[0];
  const palette      = graph.colors || fallbackPalette || DEFAULT_COLORS;

  const colorForLabel = useCallback((lbl) => {
    const key = String(lbl ?? '').trim().toLowerCase();
    return brandColorMap[key] || null;
  }, [brandColorMap]);

  const visibleSet = useMemo(() => {
    if (!settings.visibleAnswers || settings.visibleAnswers === null) return null;
    return new Set(settings.visibleAnswers);
  }, [settings.visibleAnswers]);

  const filterByVisibility = useCallback((records, getLabel) => {
    if (!visibleSet) return records;
    return records.filter((r) => visibleSet.has(getLabel(r)));
  }, [visibleSet]);

  const processed = useMemo(() => {
    if (!isCompatible(chartType, graph.data)) return [];

    if (chartType === 'barStacked') {
      const seriesKeys = Array.isArray(graph?.data?.series)
        ? graph.data.series.filter((s) => s != null)
        : [];
      if (!seriesKeys.length) return [];

      const labelFields = Array.isArray(graph?.data?.rowFields) && graph.data.rowFields.length
        ? graph.data.rowFields
        : [];
      const pickLabel = (rec = {}) => {
        for (const key of labelFields) {
          if (rec[key] != null) return coerceTrimmed(rec[key]);
        }
        for (const key of ['label', 'answer', 'name']) {
          if (rec[key] != null) return coerceTrimmed(rec[key]);
        }
        return '';
      };

      let rows = (Array.isArray(graph?.data?.records) ? graph.data.records : []).map((rec) => {
        const label = pickLabel(rec);
        const row = { label, value: 0, __totalRaw: 0 };
        seriesKeys.forEach((key) => {
          const raw = Number(rec?.[key] ?? 0) || 0;
          row[key] = raw;
          row[`__raw_${key}`] = raw;
          row.__totalRaw += raw;
        });
        row.value = row.__totalRaw;
        return row;
      });

      rows = filterByVisibility(rows, (r) => r.label);
      rows = sortData(rows, 'value', sortOrder);

      if (metric === 'percentage') {
        rows = rows.map((row) => {
          const total = row.__totalRaw || 0;
          const next = { ...row, value: 0 };
          seriesKeys.forEach((key) => {
            const raw = row[`__raw_${key}`] ?? 0;
            const perc = total ? (raw * 100) / total : 0;
            next[key] = perc;
            next[`__percent_${key}`] = perc;
            next[`__raw_${key}`] = raw;
            next.value += perc;
          });
          next.__totalRaw = row.__totalRaw;
          return next;
        });
      } else {
        rows = rows.map((row) => {
          const total = row.__totalRaw || 0;
          const next = { ...row };
          seriesKeys.forEach((key) => {
            const raw = row[key] ?? 0;
            next[`__raw_${key}`] = row[`__raw_${key}`];
            next[`__percent_${key}`] = total ? (raw * 100) / (total || 1) : 0;
          });
          return next;
        });
      }

      return rows;
    }

    const makeBase = () => {
      if (chartType === 'histogram') {
        const { bins, counts } = graph.data.histogram;
        return counts.map((cnt, i) => ({
          label: `${bins[i].toFixed(1)}-${bins[i + 1].toFixed(1)}`,
          value: cnt,
        }));
      }
      return graph.data.labels.map((lbl, i) => ({ label: lbl, value: graph.data.counts[i], index: i }));
    };

    let base = makeBase();
    base = filterByVisibility(base, (r) => r.label);
    base = sortData(base, 'value', sortOrder);
    const totalRaw = base.reduce((sum, row) => sum + (Number(row.value) || 0), 0);
    base = base.map((row) => {
      const raw = Number(row.value) || 0;
      const percent = totalRaw ? (raw * 100) / totalRaw : 0;
      return {
        ...row,
        rawValue: raw,
        percentOfTotal: percent,
      };
    });
    if (metric === 'percentage') {
      return base.map((row) => ({ ...row, value: row.percentOfTotal }));
    }
    return base.map((row) => ({ ...row, value: row.rawValue }));
  }, [chartType, graph.data, sortOrder, metric, filterByVisibility]);

  const seriesKeys = useMemo(
    () => (Array.isArray(graph?.data?.series) ? graph.data.series.filter((s) => s != null) : []),
    [graph.data]
  );

  const isPercent = metric === 'percentage';
  const digitsInput = settings.dataLabelDigits;
  const digitsCandidate =
    digitsInput === null || digitsInput === undefined || digitsInput === ''
      ? null
      : Number(digitsInput);
  const resolvedLabelDigits =
    digitsCandidate != null && Number.isFinite(digitsCandidate)
      ? Math.min(6, Math.max(0, Math.round(digitsCandidate)))
      : isPercent
        ? 1
        : 0;
  const axisFontFamily = settings.axisFontFamily || FONTS[0];
  const axisFontSize = Number(settings.axisFontSize) || 11;
  const axisAngle = Number(settings.axisAngle) || 0;
  const axisTickStyle = { fontSize: axisFontSize, fontFamily: axisFontFamily };
  const axisAnchor = axisAngle < 0 ? 'end' : axisAngle > 0 ? 'start' : 'middle';
  const dataLabelFontFamily = settings.dataLabelFontFamily || FONTS[0];
  const dataLabelFontSize = Number(settings.dataLabelFontSize) || 11;
  const dataLabelAngle = Number(settings.dataLabelAngle) || 0;
  const dataLabelContentMode = settings.dataLabelContent || 'auto';
  const domainMax = settings.autoScale ? 'dataMax' : isPercent ? 100 : 'dataMax';
  const grid = settings.gridLines ? <CartesianGrid strokeDasharray="3 3" /> : null;
  const legend = settings.legend ? <Legend verticalAlign="bottom" height={24} /> : null;

  const formatNumber = useCallback(
    (val, digits = resolvedLabelDigits) => {
      if (val == null || Number.isNaN(Number(val))) return '';
      const safeDigits = Math.min(6, Math.max(0, Math.round(digits)));
      return Number(val).toLocaleString('tr-TR', {
        minimumFractionDigits: safeDigits,
        maximumFractionDigits: safeDigits,
      });
    },
    [resolvedLabelDigits],
  );

  const axisTickFormatter = useCallback(
    (val) => {
      if (val == null || Number.isNaN(Number(val))) return '';
      const formatted = formatNumber(val, resolvedLabelDigits);
      return isPercent ? `${formatted}%` : formatted;
    },
    [formatNumber, isPercent, resolvedLabelDigits],
  );

  const tooltipFormatter = useCallback(
    (value, name, entry) => {
      const payload = entry?.payload || {};
      const dataKey = entry?.dataKey;
      let raw = null;
      if (dataKey != null && Object.prototype.hasOwnProperty.call(payload, `__raw_${dataKey}`)) {
        raw = payload[`__raw_${dataKey}`];
      } else if (Object.prototype.hasOwnProperty.call(payload, 'rawValue')) {
        raw = payload.rawValue;
      } else if (!isPercent) {
        raw = value;
      }
      let percent = null;
      if (dataKey != null && Object.prototype.hasOwnProperty.call(payload, `__percent_${dataKey}`)) {
        percent = payload[`__percent_${dataKey}`];
      } else if (Object.prototype.hasOwnProperty.call(payload, 'percentOfTotal')) {
        percent = payload.percentOfTotal;
      } else if (isPercent) {
        percent = value;
      }
      const parts = [];
      if (raw != null && !Number.isNaN(Number(raw))) {
        parts.push(formatNumber(raw, resolvedLabelDigits));
      }
      if (percent != null && !Number.isNaN(Number(percent))) {
        parts.push(`${formatNumber(percent, resolvedLabelDigits)}%`);
      }
      return [parts.join(' • ') || formatNumber(value, resolvedLabelDigits), name];
    },
    [formatNumber, isPercent, resolvedLabelDigits],
  );

  const computeLabelText = useCallback(
    (value, props = {}) => {
      const payload = props.payload || {};
      const dataKey = props.dataKey;
      let raw = null;
      if (dataKey != null && Object.prototype.hasOwnProperty.call(payload, `__raw_${dataKey}`)) {
        raw = payload[`__raw_${dataKey}`];
      } else if (Object.prototype.hasOwnProperty.call(payload, 'rawValue')) {
        raw = payload.rawValue;
      } else if (!isPercent) {
        raw = value;
      }
      let percent = null;
      if (dataKey != null && Object.prototype.hasOwnProperty.call(payload, `__percent_${dataKey}`)) {
        percent = payload[`__percent_${dataKey}`];
      } else if (Object.prototype.hasOwnProperty.call(payload, 'percentOfTotal')) {
        percent = payload.percentOfTotal;
      } else if (isPercent) {
        percent = value;
      }
      const formattedRaw =
        raw != null && !Number.isNaN(Number(raw)) ? formatNumber(raw, resolvedLabelDigits) : null;
      const formattedPercent =
        percent != null && !Number.isNaN(Number(percent))
          ? `${formatNumber(percent, resolvedLabelDigits)}%`
          : null;
      const mode =
        dataLabelContentMode === 'auto'
          ? isPercent
            ? 'percent'
            : 'value'
          : dataLabelContentMode;
      if (mode === 'percent') {
        return formattedPercent || '';
      }
      if (mode === 'value') {
        if (isPercent) {
          return formattedPercent || formattedRaw || '';
        }
        return formattedRaw || '';
      }
      if (mode === 'both') {
        const pieces = [formattedRaw, formattedPercent].filter(Boolean);
        if (pieces.length) return pieces.join(' • ');
        return formattedRaw || formattedPercent || '';
      }
      return formattedRaw || formattedPercent || '';
    },
    [dataLabelContentMode, formatNumber, isPercent, resolvedLabelDigits],
  );

  const renderDataLabel = useCallback(
    (props) => {
      const text = computeLabelText(props.value, props);
      if (!text) return null;
      const posX = (props.x ?? 0) + (props.dx ?? 0);
      const posY = (props.y ?? 0) + (props.dy ?? 0);
      const anchor =
        props.textAnchor || (dataLabelAngle < 0 ? 'end' : dataLabelAngle > 0 ? 'start' : 'middle');
      const dominant = props.dominantBaseline || 'middle';
      const transform = dataLabelAngle
        ? `rotate(${dataLabelAngle}, ${posX}, ${posY})`
        : undefined;
      return (
        <text
          x={posX}
          y={posY}
          fill="#111827"
          fontFamily={dataLabelFontFamily}
          fontSize={dataLabelFontSize}
          textAnchor={anchor}
          dominantBaseline={dominant}
          pointerEvents="none"
          transform={transform}
        >
          {text}
        </text>
      );
    },
    [computeLabelText, dataLabelAngle, dataLabelFontFamily, dataLabelFontSize],
  );

  const renderPieLabel = useCallback(
    (props) => {
      const text = computeLabelText(props.value, { payload: props.payload });
      if (!text) return null;
      const labelText = props.payload?.label != null ? `${props.payload.label}: ${text}` : text;
      const transform = dataLabelAngle
        ? `rotate(${dataLabelAngle}, ${props.x}, ${props.y})`
        : undefined;
      return (
        <text
          x={props.x}
          y={props.y}
          fill="#111827"
          fontFamily={dataLabelFontFamily}
          fontSize={dataLabelFontSize}
          textAnchor="middle"
          dominantBaseline="middle"
          pointerEvents="none"
          transform={transform}
        >
          {labelText}
        </text>
      );
    },
    [computeLabelText, dataLabelAngle, dataLabelFontFamily, dataLabelFontSize],
  );

  const resolveLabelPosition = () => {
    const pos = settings.dataLabelPosition || 'inside';
    if (chartType === 'bar' || chartType === 'histogram' || chartType === 'barStacked') {
      if (orientation === 'horizontal') {
        if (pos === 'outside' || pos === 'top') return 'right';
        return pos;
      }
      if (pos === 'outside') return 'top';
      return pos;
    }
    if (chartType === 'line' || chartType === 'area' || chartType === 'scatter') {
      return pos === 'outside' ? 'top' : pos;
    }
    return pos;
  };

  const makeLabelList = () =>
    settings.dataLabels ? (
      <LabelList dataKey="value" position={resolveLabelPosition()} content={renderDataLabel} />
    ) : null;

  if (!isCompatible(chartType, graph.data)) {
    return <Typography variant="body2" color="warning.main">Veri bu grafik türü ile uyumsuz</Typography>;
  }

  // BAR STACKED
  if (chartType === 'barStacked') {
    if (!seriesKeys.length) {
      return <Typography variant="body2" color="warning.main">Seri verisi bulunamadı</Typography>;
    }
    if (!processed.length) {
      return <Typography variant="body2" color="text.secondary">Gösterilecek veri yok</Typography>;
    }
    const labelPosition = resolveLabelPosition();
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={processed} layout={orientation === 'horizontal' ? 'vertical' : 'horizontal'}>
          {grid}
          {orientation === 'horizontal' ? (
            <>
              <YAxis
                type="category"
                dataKey="label"
                width={140}
                interval={0}
                tick={{ ...axisTickStyle }}
                angle={axisAngle}
                textAnchor={axisAnchor}
                tickLine={false}
              />
              <XAxis
                type="number"
                domain={[0, domainMax]}
                tick={axisTickStyle}
                tickFormatter={axisTickFormatter}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="label"
                interval={0}
                tick={{ ...axisTickStyle }}
                angle={axisAngle}
                textAnchor={axisAnchor}
                tickLine={false}
              />
              <YAxis
                domain={[0, domainMax]}
                tick={axisTickStyle}
                tickFormatter={axisTickFormatter}
              />
            </>
          )}
          <RTooltip formatter={tooltipFormatter} />
          {legend}
          {seriesKeys.map((key, idx) => (
            <Bar
              key={key}
              dataKey={key}
              stackId="total"
              fill={colorForLabel(key) || palette[idx % palette.length]}
              isAnimationActive={false}
            >
              {settings.dataLabels && (
                <LabelList dataKey={key} position={labelPosition} content={renderDataLabel} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // BAR & HISTOGRAM
  if (chartType === 'bar' || chartType === 'histogram') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={processed} layout={orientation === 'horizontal' ? 'vertical' : 'horizontal'}>
          {grid}
          {orientation === 'horizontal' ? (
            <>
              <YAxis
                type="category"
                dataKey="label"
                width={140}
                interval={0}
                tick={{ ...axisTickStyle }}
                angle={axisAngle}
                textAnchor={axisAnchor}
                tickLine={false}
              />
              <XAxis
                type="number"
                domain={[0, domainMax]}
                tick={axisTickStyle}
                tickFormatter={axisTickFormatter}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="label"
                interval={0}
                tick={{ ...axisTickStyle }}
                angle={axisAngle}
                textAnchor={axisAnchor}
                tickLine={false}
              />
              <YAxis
                domain={[0, domainMax]}
                tick={axisTickStyle}
                tickFormatter={axisTickFormatter}
              />
            </>
          )}
          <RTooltip formatter={tooltipFormatter} />
          {legend}
          <Bar dataKey="value" isAnimationActive={false}>
            {processed.map((r, i) => (
              <Cell key={i} fill={colorForLabel(r.label) || palette[i % palette.length]} />
            ))}
            {makeLabelList()}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // LINE
  if (chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={processed}>
          {grid}
          <XAxis
            dataKey="label"
            interval={0}
            tick={{ ...axisTickStyle }}
            angle={axisAngle}
            textAnchor={axisAnchor}
            tickLine={false}
          />
          <YAxis
            domain={[0, domainMax]}
            tick={axisTickStyle}
            tickFormatter={axisTickFormatter}
          />
          <RTooltip formatter={tooltipFormatter} />
          {legend}
          <Line type="monotone" dataKey="value" stroke={primaryColor} dot isAnimationActive={false}>
            {makeLabelList()}
          </Line>
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // AREA
  if (chartType === 'area') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={processed}>
          {grid}
          <XAxis
            dataKey="label"
            interval={0}
            tick={{ ...axisTickStyle }}
            angle={axisAngle}
            textAnchor={axisAnchor}
            tickLine={false}
          />
          <YAxis
            domain={[0, domainMax]}
            tick={axisTickStyle}
            tickFormatter={axisTickFormatter}
          />
          <RTooltip formatter={tooltipFormatter} />
          {legend}
          <Area type="monotone" dataKey="value" fill={primaryColor} stroke={primaryColor} fillOpacity={0.6} isAnimationActive={false}>
            {makeLabelList()}
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  // PIE
  if (chartType === 'pie') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <RTooltip formatter={tooltipFormatter} />
          {legend}
          <Pie
            data={processed}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="50%"
            outerRadius="80%"
            isAnimationActive={false}
            labelLine={false}
            label={settings.dataLabels ? renderPieLabel : undefined}
          >
            {processed.map((row, i) => (
              <Cell key={i} fill={colorForLabel(row.label) || palette[i % palette.length]} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }

  // SCATTER
  if (chartType === 'scatter') {
    const scat = processed.map((d) => ({
      x: d.index,
      y: d.value,
      label: d.label,
      value: d.value,
      rawValue: d.rawValue,
      percentOfTotal: d.percentOfTotal,
    }));
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart>
          {grid}
          <XAxis
            type="number"
            dataKey="x"
            domain={['dataMin', 'dataMax']}
            tick={axisTickStyle}
            tickFormatter={axisTickFormatter}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[0, domainMax]}
            tick={axisTickStyle}
            tickFormatter={axisTickFormatter}
          />
          <RTooltip formatter={tooltipFormatter} cursor={{ strokeDasharray: '3 3' }} />
          {legend}
          <Scatter data={scat} fill={primaryColor} isAnimationActive={false}>
            {makeLabelList()}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    );
  }

  // RADAR
  if (chartType === 'radar') {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={processed}>
          {grid}
          <PolarGrid />
          <PolarAngleAxis dataKey="label" tick={{ fill: '#0f172a', fontFamily: axisFontFamily, fontSize: axisFontSize }} />
          <PolarRadiusAxis
            tickFormatter={axisTickFormatter}
            domain={[0, domainMax]}
            tick={{ fill: '#0f172a', fontFamily: axisFontFamily, fontSize: axisFontSize }}
          />
          <Radar dataKey="value" fill={primaryColor} fillOpacity={0.6} isAnimationActive={false} />
          <RTooltip formatter={tooltipFormatter} />
          {legend}
        </RadarChart>
      </ResponsiveContainer>
    );
  }

  // TABLE
  if (chartType === 'table') {
    return (
      <Box sx={{ width: '100%', height: '100%', overflow: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>Label</th>
              <th style={{ border: '1px solid #ccc', padding: 4 }}>{isPercent ? 'Value %' : 'Value'}</th>
            </tr>
          </thead>
          <tbody>
            {processed.map((r, i) => (
              <tr key={i}>
                <td style={{ border: '1px solid #ccc', padding: 4 }}>{r.label}</td>
                <td style={{ border: '1px solid #ccc', padding: 4, textAlign: 'right' }}>
                  {isPercent
                    ? `${formatNumber(r.value, resolvedLabelDigits)}%`
                    : formatNumber(r.value, resolvedLabelDigits)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  }
  return null;
}, (prevProps, nextProps) => {
  const shallowEqual = (a, b) => {
    if (a === b) return true;
    if (!a || !b) return !a && !b;
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    for (const k of ka) { if (a[k] !== b[k]) return false; }
    return true;
  };
  return (
    prevProps.baseGraph === nextProps.baseGraph &&
    shallowEqual(prevProps.overrides, nextProps.overrides) &&
    prevProps.brandColorMap === nextProps.brandColorMap
  );
});

const TableCellEditor = memo(function TableCellEditor({
  itemId,
  html,
  rowIndex,
  colIndex,
  fontFamily,
  fontSize,
  textColor,
  textAlign,
  cellPadding,
  isActive,
  onCommit,
  onActivate,
  onDeactivate,
  onRequestSelect,
  readOnly = false,
}) {
  const editorRef = useRef(null);
  const latestHtmlRef = useRef(html || EMPTY_RICH_TEXT_HTML);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const nextHtml = !isRichTextHtmlEmpty(html) ? html : '';
    if (el.innerHTML !== nextHtml) {
      el.innerHTML = nextHtml;
    }
    latestHtmlRef.current = !isRichTextHtmlEmpty(html) ? html : EMPTY_RICH_TEXT_HTML;
  }, [html]);

  const emitChange = useCallback(() => {
    if (readOnly) return;
    const el = editorRef.current;
    if (!el) return;
    const raw = el.innerHTML;
    let sanitized = sanitizeRichTextHtml(raw);
    if (!sanitized || !sanitized.trim()) {
      sanitized = EMPTY_RICH_TEXT_HTML;
    } else if (!/(<\s*(div|p|br)\b)/i.test(sanitized)) {
      sanitized = `<div>${sanitized}</div>`;
    }
    const displayHtml = !isRichTextHtmlEmpty(sanitized) ? sanitized : '';
    if (el.innerHTML !== displayHtml) {
      el.innerHTML = displayHtml;
    }
    if (latestHtmlRef.current === sanitized) return;
    latestHtmlRef.current = sanitized;
    const plain = extractPlainTextFromRichText(sanitized);
    onCommit(rowIndex, colIndex, { html: sanitized, plain });
  }, [onCommit, rowIndex, colIndex, readOnly]);

  const handleInput = useCallback(() => {
    if (!readOnly) {
      emitChange();
    }
  }, [emitChange, readOnly]);

  const handleBlur = useCallback(() => {
    if (readOnly) return;
    emitChange();
    onDeactivate?.(rowIndex, colIndex, editorRef.current);
  }, [emitChange, onDeactivate, rowIndex, colIndex, readOnly]);

  const handleFocus = useCallback(() => {
    if (readOnly) return;
    onActivate?.(rowIndex, colIndex, editorRef.current);
  }, [onActivate, rowIndex, colIndex, readOnly]);

  const handlePointerDown = useCallback((event) => {
    onRequestSelect?.();
    if (!readOnly) {
      onActivate?.(rowIndex, colIndex, editorRef.current);
    }
    event.stopPropagation();
  }, [onActivate, onRequestSelect, rowIndex, colIndex, readOnly]);

  const handlePaste = useCallback((event) => {
    if (readOnly) return;
    if (!event.clipboardData) return;
    event.preventDefault();
    const htmlData = event.clipboardData.getData('text/html');
    const textData = event.clipboardData.getData('text/plain');
    const payload = htmlData && htmlData.trim()
      ? sanitizeRichTextHtml(htmlData)
      : plainTextToRichTextHtml(textData);
    document.execCommand('insertHTML', false, payload || '');
  }, [readOnly]);

  return (
    <Box
      ref={editorRef}
      component="div"
      className="rich-text-editable table-cell-editor"
      contentEditable={!readOnly}
      suppressContentEditableWarning={!readOnly}
      spellCheck={false}
      data-item-id={itemId}
      data-table-row={rowIndex}
      data-table-col={colIndex}
      data-table-cell-key={`${itemId}::${rowIndex}-${colIndex}`}
      onInput={handleInput}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onPointerDown={handlePointerDown}
      onPaste={handlePaste}
      sx={{
        width: '100%',
        height: '100%',
        outline: isActive ? '2px solid rgba(99,102,241,0.45)' : 'none',
        borderRadius: 1,
        padding: `${cellPadding}px`,
        boxSizing: 'border-box',
        fontFamily,
        fontSize,
        color: textColor,
        textAlign,
        backgroundColor: 'transparent',
        lineHeight: 1.3,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        cursor: readOnly ? 'default' : 'text',
      }}
    />
  );
});

const TableCanvas = memo(function TableCanvas({
  table,
  itemId,
  onChange,
  onRequestSelect,
  onRequestResize,
  activeCell,
  onActivateCell,
  onDeactivateCell,
  isLinked = false,
}) {
  const data = useMemo(() => ensureTableData(table), [table]);
  const readOnly = !!isLinked;
  const containerRef = useRef(null);
  const columnPercents = useMemo(
    () => data.columnSizes.map((val) => val * 100),
    [data.columnSizes],
  );
  const rowPercents = useMemo(
    () => data.rowSizes.map((val) => val * 100),
    [data.rowSizes],
  );
  const columnBoundaries = useMemo(() => {
    const res = [];
    let acc = 0;
    for (let i = 0; i < data.columnSizes.length - 1; i += 1) {
      acc += data.columnSizes[i];
      res.push(acc);
    }
    return res;
  }, [data.columnSizes]);
  const rowBoundaries = useMemo(() => {
    const res = [];
    let acc = 0;
    for (let i = 0; i < data.rowSizes.length - 1; i += 1) {
      acc += data.rowSizes[i];
      res.push(acc);
    }
    return res;
  }, [data.rowSizes]);
  const borderStyle = useMemo(
    () => `${Math.max(1, data.border.width)}px ${cssLineStyle(data.border.style)} ${data.border.color}`,
    [data.border.color, data.border.style, data.border.width],
  );
  const activeCellForTable = activeCell && activeCell.itemId === itemId ? activeCell : null;
  const [hoverRow, setHoverRow] = useState(null);
  const [hoverColumn, setHoverColumn] = useState(null);
  const rowEdges = useMemo(() => {
    const edges = [0];
    let acc = 0;
    data.rowSizes.forEach((fraction) => {
      acc += fraction;
      edges.push(acc);
    });
    return edges;
  }, [data.rowSizes]);
  const columnEdges = useMemo(() => {
    const edges = [0];
    let acc = 0;
    data.columnSizes.forEach((fraction) => {
      acc += fraction;
      edges.push(acc);
    });
    return edges;
  }, [data.columnSizes]);

  const handleCellCommit = useCallback(
    (rowIdx, colIdx, { html }) => {
      if (readOnly) return;
      if (!Array.isArray(data.cells[rowIdx])) return;
      if (data.cells[rowIdx][colIdx] === html) return;
      const nextCells = data.cells.map((row, r) =>
        row.map((cell, c) => (r === rowIdx && c === colIdx ? html : cell)),
      );
      onChange({ ...data, cells: nextCells });
    },
    [data, onChange, readOnly],
  );

  const handleCellActivate = useCallback(
    (rowIdx, colIdx, element) => {
      if (readOnly) return;
      onActivateCell?.({ itemId, rowIndex: rowIdx, colIndex: colIdx, element });
    },
    [itemId, onActivateCell, readOnly],
  );

  const handleCellDeactivate = useCallback(
    (rowIdx, colIdx, element) => {
      if (readOnly) return;
      onDeactivateCell?.({ itemId, rowIndex: rowIdx, colIndex: colIdx, element });
    },
    [itemId, onDeactivateCell, readOnly],
  );

  const startColumnResize = useCallback(
    (index, startClientX) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const widthPx = rect.width;
      if (widthPx <= 0) return;
      const fractions = data.columnSizes;
      if (index < 0 || index >= fractions.length - 1) return;
      const initialPx = fractions.map((f) => f * widthPx);
      const leftInitial = initialPx[index];
      const rightInitial = initialPx[index + 1];
      const total = leftInitial + rightInitial;
      const minPx = Math.max(6, Math.min(total / 2 - 4, data.cellPadding * 1.5 || 12));
      if (minPx * 2 >= total) return;
      const originalCursor = document.body.style.cursor;
      document.body.style.cursor = 'col-resize';

      const handleMove = (ev) => {
        ev.preventDefault();
        const delta = ev.clientX - startClientX;
        let newLeft = clamp(leftInitial + delta, minPx, total - minPx);
        let newRight = total - newLeft;
        const totalFraction = fractions[index] + fractions[index + 1];
        const newLeftFraction = newLeft / widthPx;
        const newRightFraction = totalFraction - newLeftFraction;
        if (
          Math.abs(newLeftFraction - fractions[index]) < 0.0005 &&
          Math.abs(newRightFraction - fractions[index + 1]) < 0.0005
        ) {
          return;
        }
        const nextFractions = fractions.map((val, idx) => {
          if (idx === index) return Math.max(newLeftFraction, 0.001);
          if (idx === index + 1) return Math.max(newRightFraction, 0.001);
          return val;
        });
        onChange({ ...data, columnSizes: normaliseFractions(nextFractions) });
      };

      const handleUp = () => {
        document.body.style.cursor = originalCursor;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp, { once: true });
    },
    [data, onChange],
  );

  const startRowResize = useCallback(
    (index, startClientY) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const heightPx = rect.height;
      if (heightPx <= 0) return;
      const fractions = data.rowSizes;
      if (index < 0 || index >= fractions.length - 1) return;
      const initialPx = fractions.map((f) => f * heightPx);
      const topInitial = initialPx[index];
      const bottomInitial = initialPx[index + 1];
      const total = topInitial + bottomInitial;
      const minPx = Math.max(6, Math.min(total / 2 - 4, data.cellPadding * 1.5 || 12));
      if (minPx * 2 >= total) return;
      const originalCursor = document.body.style.cursor;
      document.body.style.cursor = 'row-resize';

      const handleMove = (ev) => {
        ev.preventDefault();
        const delta = ev.clientY - startClientY;
        let newTop = clamp(topInitial + delta, minPx, total - minPx);
        let newBottom = total - newTop;
        const totalFraction = fractions[index] + fractions[index + 1];
        const newTopFraction = newTop / heightPx;
        const newBottomFraction = totalFraction - newTopFraction;
        if (
          Math.abs(newTopFraction - fractions[index]) < 0.0005 &&
          Math.abs(newBottomFraction - fractions[index + 1]) < 0.0005
        ) {
          return;
        }
        const nextFractions = fractions.map((val, idx) => {
          if (idx === index) return Math.max(newTopFraction, 0.001);
          if (idx === index + 1) return Math.max(newBottomFraction, 0.001);
          return val;
        });
        onChange({ ...data, rowSizes: normaliseFractions(nextFractions) });
      };

      const handleUp = () => {
        document.body.style.cursor = originalCursor;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
      };

      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp, { once: true });
    },
    [data, onChange],
  );

  const insertRowAtEdge = useCallback(
    (edgeIndex) => {
      if (readOnly) return;
      const colCount = data.cells[0]?.length || 1;
      const newRow = Array.from({ length: colCount }, () => EMPTY_RICH_TEXT_HTML);
      const nextCells = [
        ...data.cells.slice(0, edgeIndex),
        newRow,
        ...data.cells.slice(edgeIndex),
      ];
      let nextRowSizes;
      if (!data.rowSizes.length) {
        const frac = 1 / nextCells.length;
        nextRowSizes = Array.from({ length: nextCells.length }, () => frac);
      } else {
        const targetIndex = edgeIndex === 0 ? 0 : edgeIndex - 1;
        const targetSize = data.rowSizes[targetIndex] ?? (1 / data.rowSizes.length);
        const insertedSize = Math.max(targetSize / 2, MIN_TABLE_FRACTION / (data.rowSizes.length + 1));
        const adjustedSizes = data.rowSizes.map((size, idx) =>
          idx === targetIndex ? Math.max(size - insertedSize, MIN_TABLE_FRACTION * 0.5) : size,
        );
        nextRowSizes = normaliseFractions([
          ...adjustedSizes.slice(0, edgeIndex),
          insertedSize,
          ...adjustedSizes.slice(edgeIndex),
        ]);
      }
      const nextHeaderRows =
        edgeIndex <= data.headerRows
          ? Math.min(data.headerRows + 1, nextCells.length)
          : Math.min(data.headerRows, nextCells.length);
      onChange({
        ...data,
        cells: nextCells,
        rowSizes: nextRowSizes,
        headerRows: nextHeaderRows,
      });
      const prevRowCount = data.cells.length || 1;
      const nextRowCount = nextCells.length || prevRowCount;
      const factor = prevRowCount > 0 ? nextRowCount / prevRowCount : 1;
      if (onRequestResize && Math.abs(factor - 1) > 0.01) {
        onRequestResize({ heightFactor: factor });
      }
    },
    [data, onChange, onRequestResize, readOnly],
  );

  const removeRowAt = useCallback(
    (rowIndex) => {
      if (readOnly) return;
      if (data.cells.length <= 1) return;
      const nextCells = data.cells.filter((_, idx) => idx !== rowIndex);
      const nextRowSizes = normaliseFractions(
        data.rowSizes.filter((_, idx) => idx !== rowIndex),
      );
      const nextHeaderRows =
        rowIndex < data.headerRows
          ? Math.max(0, Math.min(data.headerRows - 1, nextCells.length))
          : Math.min(data.headerRows, nextCells.length);
      onChange({
        ...data,
        cells: nextCells,
        rowSizes: nextRowSizes,
        headerRows: nextHeaderRows,
      });
      const prevRowCount = data.cells.length || 1;
      const nextRowCount = nextCells.length || prevRowCount;
      const factor = prevRowCount > 0 ? nextRowCount / prevRowCount : 1;
      if (onRequestResize && Math.abs(factor - 1) > 0.01) {
        onRequestResize({ heightFactor: factor });
      }
    },
    [data, onChange, onRequestResize, readOnly],
  );

  const insertColumnAtEdge = useCallback(
    (edgeIndex) => {
      if (readOnly) return;
      const nextCells = data.cells.map((row) => [
        ...row.slice(0, edgeIndex),
        EMPTY_RICH_TEXT_HTML,
        ...row.slice(edgeIndex),
      ]);
      let nextColumnSizes;
      if (!data.columnSizes.length) {
        const colCount = nextCells[0]?.length || 1;
        const frac = 1 / colCount;
        nextColumnSizes = Array.from({ length: colCount }, () => frac);
      } else {
        const targetIndex = edgeIndex === 0 ? 0 : edgeIndex - 1;
        const targetSize = data.columnSizes[targetIndex] ?? (1 / data.columnSizes.length);
        const insertedSize = Math.max(targetSize / 2, MIN_TABLE_FRACTION / (data.columnSizes.length + 1));
        const adjustedSizes = data.columnSizes.map((size, idx) =>
          idx === targetIndex ? Math.max(size - insertedSize, MIN_TABLE_FRACTION * 0.5) : size,
        );
        nextColumnSizes = normaliseFractions([
          ...adjustedSizes.slice(0, edgeIndex),
          insertedSize,
          ...adjustedSizes.slice(edgeIndex),
        ]);
      }
      onChange({
        ...data,
        cells: nextCells,
        columnSizes: nextColumnSizes,
      });
      const prevColCount = data.cells[0]?.length || 1;
      const nextColCount = nextCells[0]?.length || prevColCount;
      const factor = prevColCount > 0 ? nextColCount / prevColCount : 1;
      if (onRequestResize && Math.abs(factor - 1) > 0.01) {
        onRequestResize({ widthFactor: factor });
      }
    },
    [data, onChange, onRequestResize, readOnly],
  );

  const removeColumnAt = useCallback(
    (colIndex) => {
      if (readOnly) return;
      const colCount = data.cells[0]?.length || 0;
      if (colCount <= 1) return;
      const nextCells = data.cells.map((row) => row.filter((_, idx) => idx !== colIndex));
      const nextColumnSizes = normaliseFractions(
        data.columnSizes.filter((_, idx) => idx !== colIndex),
      );
      onChange({
        ...data,
        cells: nextCells,
        columnSizes: nextColumnSizes,
      });
      const prevColCount = data.cells[0]?.length || 1;
      const nextColCount = nextCells[0]?.length || prevColCount;
      const factor = prevColCount > 0 ? nextColCount / prevColCount : 1;
      if (onRequestResize && Math.abs(factor - 1) > 0.01) {
        onRequestResize({ widthFactor: factor });
      }
    },
    [data, onChange, onRequestResize, readOnly],
  );

  const actionIconSize = 18;
  const iconFontSize = 12;
  const buttonInset = Math.max(actionIconSize / 2 + 2, data.cellPadding * 0.6);
  const rowButtonOffset = buttonInset;
  const columnButtonOffset = buttonInset;
  const withPercentOffset = (percent, offsetPx) => {
    if (!offsetPx) return `${percent}%`;
    const sign = offsetPx > 0 ? '+' : '-';
    return `calc(${percent}% ${sign} ${Math.abs(offsetPx)}px)`;
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        bgcolor: '#ffffff',
      }}
      onMouseLeave={() => {
        setHoverRow(null);
        setHoverColumn(null);
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: columnPercents.map((p) => `${p}%`).join(' '),
          gridTemplateRows: rowPercents.map((p) => `${p}%`).join(' '),
          width: '100%',
          height: '100%',
        }}
      >
        {data.cells.map((row, rIdx) =>
          row.map((cell, cIdx) => {
            const isHeader = rIdx < data.headerRows;
            const isActiveCell =
              !!activeCellForTable &&
              activeCellForTable.rowIndex === rIdx &&
              activeCellForTable.colIndex === cIdx;
            return (
              <Box
                key={`${rIdx}-${cIdx}`}
                onMouseEnter={() => {
                  setHoverRow(rIdx);
                  setHoverColumn(cIdx);
                }}
                sx={{
                  position: 'relative',
                  borderTop: borderStyle,
                  borderLeft: borderStyle,
                  borderRight: cIdx === row.length - 1 ? borderStyle : 'none',
                  borderBottom: rIdx === data.cells.length - 1 ? borderStyle : 'none',
                  boxSizing: 'border-box',
                  backgroundColor: isHeader ? data.headerFill : '#ffffff',
                  display: 'flex',
                  alignItems: 'stretch',
                  boxShadow: isActiveCell ? 'inset 0 0 0 1.5px rgba(99,102,241,0.35)' : 'none',
                }}
              >
                <TableCellEditor
                  itemId={itemId}
                  html={cell}
                  rowIndex={rIdx}
                  colIndex={cIdx}
                  fontFamily={data.fontFamily}
                  fontSize={data.fontSize}
                  textColor={data.textColor}
                  textAlign={data.align}
                  cellPadding={data.cellPadding}
                  isActive={isActiveCell}
                  onCommit={handleCellCommit}
                  onActivate={handleCellActivate}
                  onDeactivate={handleCellDeactivate}
                  onRequestSelect={onRequestSelect}
                  readOnly={readOnly}
                />
              </Box>
            );
          }),
        )}
      </Box>
      {columnBoundaries.map((fraction, idx) => (
        <Box
          key={`col-boundary-${idx}`}
          onMouseDown={(e) => {
            onRequestSelect?.();
            e.preventDefault();
            e.stopPropagation();
            startColumnResize(idx, e.clientX);
          }}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${fraction * 100}%`,
            width: 10,
            transform: 'translateX(-50%)',
            cursor: 'col-resize',
            zIndex: 5,
            '&:hover::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: '50%',
              width: '1px',
              transform: 'translateX(-50%)',
              backgroundColor: '#6366f1',
            },
          }}
        />
      ))}
      {rowBoundaries.map((fraction, idx) => (
        <Box
          key={`row-boundary-${idx}`}
          onMouseDown={(e) => {
            onRequestSelect?.();
            e.preventDefault();
            e.stopPropagation();
            startRowResize(idx, e.clientY);
          }}
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${fraction * 100}%`,
            height: 10,
            transform: 'translateY(-50%)',
            cursor: 'row-resize',
            zIndex: 5,
            '&:hover::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: '1px',
              transform: 'translateY(-50%)',
              backgroundColor: '#6366f1',
            },
          }}
        />
      ))}
      {/* Row delete handles (appear on hover) */}
      {!readOnly && data.cells.map((_, rIdx) => {
        const start = rowEdges[rIdx];
        const end = rowEdges[rIdx + 1];
        const centerPercent = ((start + end) / 2) * 100;
        const visible = hoverRow === rIdx;
        return (
          <IconButton
            key={`row-delete-${rIdx}`}
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              removeRowAt(rIdx);
            }}
            onMouseEnter={() => setHoverRow(rIdx)}
            sx={{
              position: 'absolute',
              left: `${rowButtonOffset}px`,
              top: `${centerPercent}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 7,
              width: actionIconSize,
              height: actionIconSize,
              bgcolor: 'rgba(239,68,68,0.92)',
              color: '#ffffff',
              borderRadius: '50%',
              boxShadow: '0 4px 10px rgba(15,23,42,0.18)',
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? 'auto' : 'none',
              transition: 'opacity 0.15s ease',
              '& .MuiSvgIcon-root': {
                fontSize: iconFontSize,
              },
              '&:hover': {
                bgcolor: 'rgba(220,38,38,0.98)',
              },
            }}
          >
            <RemoveIcon fontSize="inherit" />
          </IconButton>
        );
      })}
      {/* Row insert handles between existing rows */}
      {(!readOnly) && rowEdges.map((edge, edgeIdx) => {
        if (edgeIdx === 0 || edgeIdx === rowEdges.length - 1) {
          const closestRow = edgeIdx === 0 ? 0 : rowEdges.length - 2;
          const visible = hoverRow === closestRow;
          const edgeOffset = edgeIdx === 0 ? 10 : edgeIdx === rowEdges.length - 1 ? -10 : 0;
          const edgeTop = withPercentOffset(edge * 100, edgeOffset);
          return (
            <IconButton
              key={`row-insert-edge-${edgeIdx}`}
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                insertRowAtEdge(edgeIdx);
              }}
              onMouseEnter={() => setHoverRow(closestRow)}
              sx={{
                position: 'absolute',
                left: `${rowButtonOffset}px`,
                top: edgeTop,
                transform: 'translate(-50%, -50%)',
                zIndex: 7,
                width: actionIconSize,
                height: actionIconSize,
                bgcolor: 'rgba(99,102,241,0.95)',
                color: '#ffffff',
                borderRadius: '50%',
                boxShadow: '0 4px 10px rgba(15,23,42,0.18)',
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? 'auto' : 'none',
                transition: 'opacity 0.15s ease',
                '& .MuiSvgIcon-root': {
                  fontSize: iconFontSize,
                },
                '&:hover': {
                  bgcolor: 'rgba(79,70,229,1)',
                },
              }}
            >
              <AddIcon fontSize="inherit" />
            </IconButton>
          );
        }
        const previousRow = edgeIdx - 1;
        const nextRow = edgeIdx;
        const visible = hoverRow === previousRow || hoverRow === nextRow;
        return (
          <IconButton
            key={`row-insert-edge-${edgeIdx}`}
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              insertRowAtEdge(edgeIdx);
            }}
            onMouseEnter={() => setHoverRow(previousRow)}
            sx={{
              position: 'absolute',
              left: `${rowButtonOffset}px`,
              top: `${edge * 100}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 7,
              width: actionIconSize,
              height: actionIconSize,
              bgcolor: 'rgba(99,102,241,0.95)',
              color: '#ffffff',
              borderRadius: '50%',
              boxShadow: '0 4px 10px rgba(15,23,42,0.18)',
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? 'auto' : 'none',
              transition: 'opacity 0.15s ease',
              '& .MuiSvgIcon-root': {
                fontSize: iconFontSize,
              },
              '&:hover': {
                bgcolor: 'rgba(79,70,229,1)',
              },
            }}
          >
            <AddIcon fontSize="inherit" />
          </IconButton>
        );
      })}
      {/* Column delete handles (appear on hover) */}
      {!readOnly && data.cells[0]?.map((_, cIdx) => {
        const start = columnEdges[cIdx];
        const end = columnEdges[cIdx + 1];
        const centerPercent = ((start + end) / 2) * 100;
        const visible = hoverColumn === cIdx;
        return (
          <IconButton
            key={`col-delete-${cIdx}`}
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              removeColumnAt(cIdx);
            }}
            onMouseEnter={() => setHoverColumn(cIdx)}
            sx={{
              position: 'absolute',
              top: `${columnButtonOffset}px`,
              left: `${centerPercent}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 7,
              width: actionIconSize,
              height: actionIconSize,
              bgcolor: 'rgba(239,68,68,0.92)',
              color: '#ffffff',
              borderRadius: '50%',
              boxShadow: '0 4px 10px rgba(15,23,42,0.18)',
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? 'auto' : 'none',
              transition: 'opacity 0.15s ease',
              '& .MuiSvgIcon-root': {
                fontSize: iconFontSize,
              },
              '&:hover': {
                bgcolor: 'rgba(220,38,38,0.98)',
              },
            }}
          >
            <RemoveIcon fontSize="inherit" />
          </IconButton>
        );
      })}
      {/* Column insert handles between existing columns */}
      {!readOnly && columnEdges.map((edge, edgeIdx) => {
        if (edgeIdx === 0 || edgeIdx === columnEdges.length - 1) {
          const closestCol = edgeIdx === 0 ? 0 : columnEdges.length - 2;
          const visible = hoverColumn === closestCol;
          const edgeLeft =
            edgeIdx === 0
              ? `${columnButtonOffset}px`
              : edgeIdx === columnEdges.length - 1
              ? withPercentOffset(100, -columnButtonOffset)
              : `${edge * 100}%`;
          return (
            <IconButton
              key={`col-insert-edge-${edgeIdx}`}
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                insertColumnAtEdge(edgeIdx);
              }}
              onMouseEnter={() => setHoverColumn(closestCol)}
              sx={{
                position: 'absolute',
                top: `${columnButtonOffset}px`,
                left: edgeLeft,
                transform: 'translate(-50%, -50%)',
                zIndex: 7,
                width: actionIconSize,
                height: actionIconSize,
                bgcolor: 'rgba(99,102,241,0.95)',
                color: '#ffffff',
                borderRadius: '50%',
                boxShadow: '0 4px 10px rgba(15,23,42,0.18)',
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? 'auto' : 'none',
                transition: 'opacity 0.15s ease',
                '& .MuiSvgIcon-root': {
                  fontSize: iconFontSize,
                },
                '&:hover': {
                  bgcolor: 'rgba(79,70,229,1)',
                },
              }}
            >
              <AddIcon fontSize="inherit" />
            </IconButton>
          );
        }
        const previousCol = edgeIdx - 1;
        const nextCol = edgeIdx;
        const visible = hoverColumn === previousCol || hoverColumn === nextCol;
        return (
          <IconButton
            key={`col-insert-edge-${edgeIdx}`}
            size="small"
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              insertColumnAtEdge(edgeIdx);
            }}
            onMouseEnter={() => setHoverColumn(previousCol)}
            sx={{
              position: 'absolute',
              top: `${columnButtonOffset}px`,
              left: `${edge * 100}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 7,
              width: actionIconSize,
              height: actionIconSize,
              bgcolor: 'rgba(99,102,241,0.95)',
              color: '#ffffff',
              borderRadius: '50%',
              boxShadow: '0 4px 10px rgba(15,23,42,0.18)',
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? 'auto' : 'none',
              transition: 'opacity 0.15s ease',
              '& .MuiSvgIcon-root': {
                fontSize: iconFontSize,
              },
              '&:hover': {
                bgcolor: 'rgba(79,70,229,1)',
              },
            }}
          >
            <AddIcon fontSize="inherit" />
          </IconButton>
        );
      })}
      {readOnly && (
        <Chip
          size="small"
          icon={<LinkIcon sx={{ fontSize: 16 }} />}
          label="E-tablo"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: 'rgba(15,118,110,0.9)',
            color: '#ecfdf5',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(15,23,42,0.25)',
          }}
        />
      )}
    </Box>
  );
});

const TableThumbnailView = memo(function TableThumbnailView({ table }) {
  const data = useMemo(() => ensureTableData(table), [table]);
  const columnPercents = data.columnSizes.map((v) => v * 100);
  const rowPercents = data.rowSizes.map((v) => v * 100);
  const borderStyle = `${Math.max(0.5, data.border.width * 0.6)}px ${cssLineStyle(data.border.style)} ${data.border.color}`;
  const fontSize = Math.max(6, data.fontSize * 0.35);

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: columnPercents.map((p) => `${p}%`).join(' '),
        gridTemplateRows: rowPercents.map((p) => `${p}%`).join(' '),
        backgroundColor: '#ffffff',
        fontFamily: data.fontFamily,
        fontSize,
        color: data.textColor,
      }}
    >
      {data.cells.map((row, rIdx) =>
        row.map((cell, cIdx) => (
          <Box
            key={`${rIdx}-${cIdx}`}
            sx={{
              borderTop: borderStyle,
              borderLeft: borderStyle,
              borderRight: cIdx === row.length - 1 ? borderStyle : 'none',
              borderBottom: rIdx === data.cells.length - 1 ? borderStyle : 'none',
              overflow: 'hidden',
              backgroundColor: rIdx < data.headerRows ? data.headerFill : '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: tableAlignToFlex(data.align),
              px: 0.3,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
            dangerouslySetInnerHTML={{ __html: !isRichTextHtmlEmpty(cell) ? cell : '&nbsp;' }}
          />
        )),
      )}
    </Box>
  );
});

/* ---------------- Graph → pptxgenjs ------------------------- */
function graphToPptx(g) {
  const seriesColor = toPptxColor(g.color || DEFAULT_COLORS[0]);
  const defaultSeries = [
    {
      name: g.header || 'Veri',
      labels: g.data?.labels ?? [],
      values: g.data?.counts ?? [],
      color: seriesColor,
    },
  ];
  const map = {
    bar: 'bar',
    barStacked: 'bar',
    histogram: 'bar',
    line: 'line',
    area: 'area',
    pie: 'pie',
    scatter: 'scatter',
    radar: 'radar',
  };
  const isPercent = g.metric === 'percentage';
  if (g.graphType === 'table') {
    const headerRow = [
      { text: 'Label', options: { bold: true } },
      { text: isPercent ? 'Value %' : 'Value', options: { bold: true } },
    ];
    const bodyRows = g.data.labels.map((lbl, i) => [lbl, g.data.counts[i]]);
    return { chartType: 'table', rows: [headerRow, ...bodyRows] };
  }
  if (!map[g.graphType]) return { chartType: null };

  const opts = {};
  let series = defaultSeries;

  if (g.graphType === 'barStacked') {
    const seriesKeys = Array.isArray(g.data?.series) ? g.data.series.filter((s) => s != null) : [];
    const records = Array.isArray(g.data?.records) ? g.data.records : [];
    const labelFields = Array.isArray(g.data?.rowFields) && g.data.rowFields.length ? g.data.rowFields : [];
    const resolveLabel = (rec = {}) => {
      for (const key of labelFields) {
        if (rec[key] != null) return coerceTrimmed(rec[key]);
      }
      for (const key of ['label', 'answer', 'name']) {
        if (rec[key] != null) return coerceTrimmed(rec[key]);
      }
      return '';
    };
    const labels = records.map(resolveLabel);
    if (seriesKeys.length) {
      series = seriesKeys.map((key, idx) => ({
        name: key,
        labels,
        values: records.map((rec) => Number(rec?.[key] ?? 0)),
        color: toPptxColor((g.colors && g.colors[idx]) || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]),
      }));
      opts.barGrouping = 'stacked';
      opts.chartColors = series.map((s) => s.color);
    }
  }

  if (g.graphType === 'bar' || g.graphType === 'histogram' || g.graphType === 'barStacked') {
    const ori = g.orientation === 'horizontal' ? 'horizontal' : 'vertical';
    opts.barDir = ori === 'horizontal' ? 'bar' : 'col';
    if (!opts.chartColors) {
      opts.chartColors = (g.colors || [g.color || DEFAULT_COLORS[0]]).map(toPptxColor);
    }
  }
  // NEW: propagate colour palette to line, area and radar charts
  if (['line', 'area', 'radar'].includes(g.graphType)) {
    opts.chartColors = (g.colors || [g.color || DEFAULT_COLORS[0]]).map(toPptxColor);
  }
  if (g.graphType === 'pie') {
    opts.colors = (g.colors || DEFAULT_COLORS).map(toPptxColor);
  }
  if (g.graphType === 'scatter') {
    const values = g.data.labels.map((_, i) => ({ x: i, y: g.data.counts[i] }));
    // NEW: custom colours for scatter chart
    opts.chartColors = (g.colors || [g.color || DEFAULT_COLORS[0]]).map(toPptxColor);
    return {
      chartType: 'scatter',
      series: [{ name: g.header || 'Veri', values, color: seriesColor }],
      opts,
    };
  }
  return { chartType: map[g.graphType], series, opts };
}

const ShapeCanvas = memo(function ShapeCanvas({ shape, width, height }) {
  const cfg = ensureShapeConfig(shape);
  const w = Math.max(1, width || 1);
  const h = Math.max(1, height || 1);
  const strokeWidth = Math.min(Math.min(w, h), Math.max(0, cfg.strokeWidth || 0));
  const halfStroke = strokeWidth / 2;
  const dashArray = shapeDashArray(cfg.strokeStyle, Math.max(strokeWidth, 1));
  const hasStroke = strokeWidth > 0;
  const effectiveStroke = hasStroke ? cfg.stroke : 'none';
  const hasFill = cfg.fillOpacity > 0.001 && !isLineLikeShape(cfg);
  const effectiveFill = hasFill ? cfg.fill : 'none';
  const fillOpacity = hasFill ? clamp(cfg.fillOpacity, 0, 1) : 0;

  const commonProps = {
    stroke: effectiveStroke,
    strokeWidth,
    strokeDasharray: dashArray,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    fill: effectiveFill,
    fillOpacity,
  };

  let element = null;
  switch (cfg.kind) {
    case 'rectangle':
    case 'roundedRect': {
      const radiusMax = Math.min(cfg.borderRadius || 0, Math.min(w, h) / 2);
      element = (
        <rect
          x={halfStroke}
          y={halfStroke}
          width={Math.max(w - strokeWidth, 0)}
          height={Math.max(h - strokeWidth, 0)}
          rx={radiusMax}
          ry={radiusMax}
          {...commonProps}
        />
      );
      break;
    }
    case 'ellipse':
    case 'circle': {
      element = (
        <ellipse
          cx={w / 2}
          cy={h / 2}
          rx={Math.max((w - strokeWidth) / 2, 0)}
          ry={Math.max((h - strokeWidth) / 2, 0)}
          {...commonProps}
        />
      );
      break;
    }
    case 'triangle': {
      const topY = halfStroke;
      const bottomY = Math.max(h - halfStroke, halfStroke);
      element = (
        <polygon
          points={`${w / 2},${topY} ${Math.max(w - halfStroke, halfStroke)},${bottomY} ${halfStroke},${bottomY}`}
          {...commonProps}
        />
      );
      break;
    }
    case 'diamond': {
      const topY = halfStroke;
      const bottomY = Math.max(h - halfStroke, halfStroke);
      element = (
        <polygon
          points={`${w / 2},${topY} ${Math.max(w - halfStroke, halfStroke)},${h / 2} ${w / 2},${bottomY} ${halfStroke},${h / 2}`}
          {...commonProps}
        />
      );
      break;
    }
    case 'pentagon': {
      const points = regularPolygonPoints(5, w, h, strokeWidth);
      element = <polygon points={points} {...commonProps} />;
      break;
    }
    case 'hexagon': {
      const points = regularPolygonPoints(6, w, h, strokeWidth);
      element = <polygon points={points} {...commonProps} />;
      break;
    }
    case 'star': {
      const points = starPolygonPoints(5, w, h, strokeWidth, 0.5);
      element = <polygon points={points} {...commonProps} />;
      break;
    }
    case 'arrowRight': {
      const headWidth = Math.min(w * 0.35, Math.min(w, h));
      const padding = Math.min(h * 0.2, Math.max(halfStroke, strokeWidth * 2));
      const shaftTop = Math.max(halfStroke + padding, halfStroke);
      const shaftBottom = Math.max(
        Math.min(h - halfStroke - padding, h - halfStroke),
        shaftTop,
      );
      const headStartX = Math.max(w - headWidth, halfStroke);
      const tipX = Math.max(w - halfStroke, headStartX);
      element = (
        <polygon
          points={[
            `${halfStroke},${shaftTop}`,
            `${headStartX},${shaftTop}`,
            `${headStartX},${halfStroke}`,
            `${tipX},${h / 2}`,
            `${headStartX},${h - halfStroke}`,
            `${headStartX},${shaftBottom}`,
            `${halfStroke},${shaftBottom}`,
          ].join(' ')}
          {...commonProps}
        />
      );
      break;
    }
    case 'arrowLeft': {
      const headWidth = Math.min(w * 0.35, Math.min(w, h));
      const padding = Math.min(h * 0.2, Math.max(halfStroke, strokeWidth * 2));
      const shaftTop = Math.max(halfStroke + padding, halfStroke);
      const shaftBottom = Math.max(
        Math.min(h - halfStroke - padding, h - halfStroke),
        shaftTop,
      );
      const headStartX = Math.min(headWidth + halfStroke, w - halfStroke);
      const tailX = Math.max(w - halfStroke, headStartX);
      element = (
        <polygon
          points={[
            `${tailX},${shaftTop}`,
            `${headStartX},${shaftTop}`,
            `${headStartX},${halfStroke}`,
            `${halfStroke},${h / 2}`,
            `${headStartX},${h - halfStroke}`,
            `${headStartX},${shaftBottom}`,
            `${tailX},${shaftBottom}`,
          ].join(' ')}
          {...commonProps}
        />
      );
      break;
    }
    case 'arrowUp': {
      const headHeight = Math.min(h * 0.35, Math.min(w, h));
      const padding = Math.min(w * 0.2, Math.max(halfStroke, strokeWidth * 2));
      const shaftLeft = Math.max(halfStroke + padding, halfStroke);
      const shaftRight = Math.max(
        Math.min(w - halfStroke - padding, w - halfStroke),
        shaftLeft + 1,
      );
      const headStartY = Math.min(headHeight + halfStroke, h - halfStroke);
      const baseY = Math.max(h - halfStroke, headStartY);
      element = (
        <polygon
          points={[
            `${shaftLeft},${headStartY}`,
            `${shaftLeft},${baseY}`,
            `${shaftRight},${baseY}`,
            `${shaftRight},${headStartY}`,
            `${w - halfStroke},${headStartY}`,
            `${w / 2},${halfStroke}`,
            `${halfStroke},${headStartY}`,
          ].join(' ')}
          {...commonProps}
        />
      );
      break;
    }
    case 'arrowDown': {
      const headHeight = Math.min(h * 0.35, Math.min(w, h));
      const padding = Math.min(w * 0.2, Math.max(halfStroke, strokeWidth * 2));
      const shaftLeft = Math.max(halfStroke + padding, halfStroke);
      const shaftRight = Math.max(
        Math.min(w - halfStroke - padding, w - halfStroke),
        shaftLeft + 1,
      );
      const headStartY = Math.max(h - headHeight - halfStroke, halfStroke);
      const topY = halfStroke;
      element = (
        <polygon
          points={[
            `${shaftLeft},${headStartY}`,
            `${shaftLeft},${topY}`,
            `${shaftRight},${topY}`,
            `${shaftRight},${headStartY}`,
            `${w - halfStroke},${headStartY}`,
            `${w / 2},${h - halfStroke}`,
            `${halfStroke},${headStartY}`,
          ].join(' ')}
          {...commonProps}
        />
      );
      break;
    }
    case 'line':
    default: {
      const horizontal = w >= h;
      if (horizontal) {
        element = (
          <line
            x1={halfStroke}
            y1={h / 2}
            x2={Math.max(w - halfStroke, halfStroke)}
            y2={h / 2}
            {...commonProps}
          />
        );
      } else {
        element = (
          <line
            x1={w / 2}
            y1={halfStroke}
            x2={w / 2}
            y2={Math.max(h - halfStroke, halfStroke)}
            {...commonProps}
          />
        );
      }
      break;
    }
  }

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ display: 'block', pointerEvents: 'none' }}
      aria-hidden="true"
    >
      {element}
    </svg>
  );
});

/* ---------------- Slide thumbnail (left panel preview) -------- */
const SlideThumbnail = memo(function SlideThumbnail({ slide, allGraphs, brandColorMap, template, fallbackPalette = null }) {
  const SCALE = 120 / CANVAS_H; // height sabit 120 px

  return (
    <Box
      sx={{
        width: '100%',
        height: 120,
        position: 'relative',
        bgcolor: '#ffffff',
        overflow: 'hidden',
      }}
    >
      {template?.dataUrl && (
        <Box
          component="img"
          src={template.dataUrl}
          alt=""
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: template.mode === 'stretch' ? 'fill' : template.mode,
            opacity: typeof template.opacity === 'number' ? template.opacity : 1,
            pointerEvents: 'none'
          }}
        />
      )}
      {slide.items.map((it) => {
        const common = {
          position: 'absolute',
          left: it.x * SCALE,
          top: it.y * SCALE,
          width: it.w * SCALE,
          height: it.h * SCALE,
          overflow: 'hidden',
        };

        if (it.type === 'text') {
          const fs = Math.max(6, (it.style?.fontSize || 16) * SCALE);
          const previewHtml =
            (typeof it.richText === 'string' && it.richText.trim())
              ? it.richText
              : plainTextToRichTextHtml(it.text);
          return (
            <Box
              key={it.id}
              sx={{
                ...common,
                display: 'flex',
                alignItems: 'center',
                justifyContent:
                  it.style?.align === 'right'
                    ? 'flex-end'
                    : it.style?.align === 'center'
                    ? 'center'
                    : 'flex-start',
                px: 0.5,
              }}
            >
              <Box
                component="div"
                sx={{
                  width: '100%',
                  fontSize: fs,
                  fontFamily: it.style?.fontFamily || 'Arial',
                  fontWeight: it.style?.bold ? 'bold' : 'normal',
                  fontStyle: it.style?.italic ? 'italic' : 'normal',
                  textDecoration: it.style?.underline ? 'underline' : 'none',
                  color: it.style?.color || '#000',
                  textAlign: it.style?.align || 'left',
                  lineHeight: 1.4,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                }}
                dangerouslySetInnerHTML={{ __html: previewHtml }}
              />
            </Box>
          );
        }

        if (it.type === 'graph') {
          // Her zaman hafif (statik) önizleme göster
          const baseGraph = allGraphs[it.graphIndex];
          const ori = (it.overrides?.orientation) || (ORIENTABLE_TYPES.includes(baseGraph?.graphType) ? 'vertical' : 'horizontal');
          return (
            <Box key={it.id} sx={{ ...common, p: 0.5 }}>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: 0.5,
                  bgcolor: '#f8fafc',
                  position: 'relative',
                }}
              >
                <Box sx={{ position: 'absolute', left: 6, right: 6, bottom: 6, top: 6 }}>
                  {ori === 'horizontal'
                    ? [0.2, 0.35, 0.5, 0.4, 0.6].map((w, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            position: 'absolute',
                            left: 8,
                            top: `${10 + idx * 16}%`,
                            height: '12%',
                            width: `${w * 80}%`,
                            bgcolor: '#e5e7eb',
                            border: '1px solid #d1d5db',
                          }}
                        />
                      ))
                    : [0.2, 0.35, 0.5, 0.4, 0.6].map((h, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            width: `${100 / 7}%`,
                            left: `${(idx + 1) * (100 / 7)}%`,
                            height: `${h * 100}%`,
                            bgcolor: '#e5e7eb',
                            border: '1px solid #d1d5db',
                          }}
                        />
                      ))}
                </Box>
              </Box>
            </Box>
          );
        }

        if (it.type === 'table') {
          const tableData = ensureTableData(it.table);
          return (
            <Box key={it.id} sx={{ ...common, p: 0.5 }}>
              <Paper
                elevation={0}
                sx={{
                  width: '100%',
                  height: '100%',
                  border: '1px solid #e5e7eb',
                  borderRadius: 0.5,
                  overflow: 'hidden',
                }}
              >
                <TableThumbnailView table={tableData} />
              </Paper>
            </Box>
          );
        }

        if (it.type === 'image') {
          return (
            <Box key={it.id} sx={{ ...common }}>
              <Box
                component="img"
                src={it.src}
                alt={it.meta?.photographer || 'Sunum görseli'}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: typeof it.opacity === 'number' ? clamp(it.opacity, 0, 1) : 1,
                  display: 'block',
                }}
              />
            </Box>
          );
        }

        if (it.type === 'shape') {
          return (
            <Box key={it.id} sx={{ ...common }}>
              <ShapeCanvas shape={it.shape} width={it.w} height={it.h} />
            </Box>
          );
        }

        return null;
      })}
    </Box>
  );
}, (prev, next) =>
  prev.slide === next.slide &&
  prev.allGraphs === next.allGraphs &&
  prev.brandColorMap === next.brandColorMap &&
  prev.template === next.template &&
  prev.fallbackPalette === next.fallbackPalette
);

const RichTextCanvasEditor = memo(function RichTextCanvasEditor({
  item,
  onContentChange,
  onActivate,
  isActive,
}) {
  const editorRef = useRef(null);
  const latestHtmlRef = useRef(item.richText || EMPTY_RICH_TEXT_HTML);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const nextHtml = item.richText && item.richText.trim() ? item.richText : EMPTY_RICH_TEXT_HTML;
    if (latestHtmlRef.current !== nextHtml && el.innerHTML !== nextHtml) {
      el.innerHTML = nextHtml;
      latestHtmlRef.current = nextHtml;
    }
  }, [item.richText]);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const raw = el.innerHTML;
    let sanitized = sanitizeRichTextHtml(raw);
    if (!sanitized || !sanitized.trim()) {
      sanitized = EMPTY_RICH_TEXT_HTML;
    }
    if (sanitized !== raw) {
      el.innerHTML = sanitized;
    }
    latestHtmlRef.current = sanitized;
    const plain = extractPlainTextFromRichText(sanitized);
    const measuredHeight = el.scrollHeight;
    onContentChange({
      html: sanitized,
      plain,
      measuredHeight,
    });
  }, [onContentChange]);

  const handleInput = useCallback(() => {
    emitChange();
  }, [emitChange]);

  const handleBlur = useCallback(() => {
    emitChange();
    if (onActivate) {
      onActivate(null);
    }
  }, [emitChange, onActivate]);

  const handlePaste = useCallback((event) => {
    if (!event.clipboardData) return;
    event.preventDefault();
    const htmlData = event.clipboardData.getData('text/html');
    const textData = event.clipboardData.getData('text/plain');
    const payload = htmlData && htmlData.trim()
      ? sanitizeRichTextHtml(htmlData)
      : plainTextToRichTextHtml(textData);
    document.execCommand('insertHTML', false, payload || '');
  }, []);

  const handleFocus = useCallback(() => {
    if (onActivate && editorRef.current) {
      onActivate(editorRef.current);
    }
  }, [onActivate]);

  const handlePointerDown = useCallback(() => {
    if (onActivate && editorRef.current) {
      onActivate(editorRef.current);
    }
  }, [onActivate]);

  const baseStyle = item.style || DEFAULT_TEXT_STYLE;

  return (
    <Box
      ref={editorRef}
      component="div"
      className="rich-text-editable"
      contentEditable
      suppressContentEditableWarning
      data-item-id={item.id}
      onInput={handleInput}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onPointerDown={handlePointerDown}
      onPaste={handlePaste}
      spellCheck={false}
      sx={{
        width: '100%',
        height: '100%',
        outline: isActive ? '2px solid rgba(99,102,241,0.35)' : 'none',
        borderRadius: 2,
        px: 0,
        py: 0,
        fontSize: `${baseStyle.fontSize}px`,
        color: baseStyle.color,
        fontFamily: baseStyle.fontFamily,
        fontWeight: baseStyle.bold ? 'bold' : 'normal',
        fontStyle: baseStyle.italic ? 'italic' : 'normal',
        textDecoration: baseStyle.underline ? 'underline' : 'none',
        textAlign: baseStyle.align,
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        backgroundColor: 'transparent',
        cursor: 'text',
        userSelect: 'text',
        minHeight: '100%',
      }}
    />
  );
});

/* ---------------- Rename Dialog ------------------------------ */
function RenameDialog({ open, initial, onClose }) {
  const [val, setVal] = useState(initial);
  useEffect(() => setVal(initial), [initial]);
  return (
    <Dialog open={open} onClose={() => onClose(null)} disableScrollLock>
      <DialogTitle>Sayfa adını değiştir</DialogTitle>
      <DialogContent>
        <TextField autoFocus fullWidth value={val} onChange={(e) => setVal(e.target.value)} label="Sayfa adı" />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)}>İptal</Button>
        <Button variant="contained" onClick={() => onClose(val)}>
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
}



/* -------------------------------------------------------------------- */
/*  ANA BİLEŞEN                                                        */
/* -------------------------------------------------------------------- */
export default function PresentationBuilder({ graphs: extGraphs = [] }) {
  const canvasContainerRef = useRef(null);
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);

  // Compute canvas scale to fit width responsively (stable + overlay-safe)
  const scaleRef = useRef(1);
  const overlayOpenRef = useRef(false);

  useEffect(() => {
    if (!canvasContainerRef.current) return;
    const el = canvasContainerRef.current;

    let rafId = 0;

    const compute = () => {
      // Overlay/dialog açıksa ölçekleme yapma
      if (overlayOpenRef.current) return;

      // rAF ile tek frame'de stabilize et
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const cs = getComputedStyle(el);
        const pl = parseFloat(cs.paddingLeft)  || 0;
        const pr = parseFloat(cs.paddingRight) || 0;
        const pt = parseFloat(cs.paddingTop)   || 0;
        const pb = parseFloat(cs.paddingBottom)|| 0;

        // İçeriğe kalan gerçek alan (padding hariç)
        const availW = Math.max(0, el.clientWidth  - pl - pr);
        const availH = Math.max(0, el.clientHeight - pt - pb);

        // Hem genişlik hem yükseklik kısıtına göre ölçek; asla büyütme (<=1)
        const sRaw = Math.min(1, availW / CANVAS_W, availH / CANVAS_H);
        const s = Number.isFinite(sRaw) && sRaw > 0 ? sRaw : 1;

        // Küçük dalgalanmayı (scrollbar vs.) yoksay
        if (Math.abs(s - scaleRef.current) >= 0.005) {
          scaleRef.current = s;
          setScale(s);
        }
      });
    };

    const ro = new ResizeObserver(compute);
    ro.observe(el);

    // İlk hesap
    compute();

    // Bazı cihazlarda orientation / viewport değişimleri
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
    // CANVAS_W/H sabit değilse dependency'e ekle
  }, [CANVAS_W, CANVAS_H]);
  const { organisation } = useAuth();
  const { presentationID } = useParams();
  const [slides, setSlides] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [savedAssets, setSavedAssets] = useState([]);
  const [selectedGraphHeader, setSelectedGraphHeader] = useState('');
  const [renameIdx, setRenameIdx] = useState(null);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [rotatingItemId, setRotatingItemId] = useState(null);
  const [marqueeRect, setMarqueeRect] = useState(null);
  const [brandDlg, setBrandDlg] = useState(false);
  const [itemMenu, setItemMenu] = useState({ anchorEl: null, itemId: null });
  const [brandColors, setBrandColors] = useState([]); // array of { name, color }
  const [templateDlg, setTemplateDlg] = useState(false);
  const [template, setTemplate] = useState(null); // { dataUrl, mode, opacity }
  const [imageDialogState, setImageDialogState] = useState({ open: false, targetId: null });
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const dragStateRef = useRef(null);
  const marqueeStateRef = useRef(null);
  const rotationStateRef = useRef(null);
  const lastSavedPayloadRef = useRef('');
  const slidesRef = useRef(slides);
  const activeIdxRef = useRef(activeIdx);
  const selectedItemIdsRef = useRef(selectedItemIds);
  const linkedTableCacheRef = useRef(new Map());

  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  useEffect(() => {
    activeIdxRef.current = activeIdx;
  }, [activeIdx]);

  useEffect(() => {
    selectedItemIdsRef.current = selectedItemIds;
  }, [selectedItemIds]);

  const mutateSlides = useCallback(
    (updater, options = {}) => {
      const { recordHistory = true, resetHistory = false } = options;
      setSlides((prev) => {
        const proposed = typeof updater === 'function' ? updater(prev) : updater;
        const nextSlides = Array.isArray(proposed) ? proposed : prev;
        const hasChanged = prev !== nextSlides;
        if (resetHistory) {
          setHistory([]);
          setFuture([]);
        } else if (recordHistory && hasChanged) {
          const snapshot = createHistorySnapshot(
            prev,
            activeIdxRef.current,
            selectedItemIdsRef.current
          );
          setHistory((hist) => {
            const appended = [...hist, snapshot];
            return appended.length > HISTORY_LIMIT
              ? appended.slice(appended.length - HISTORY_LIMIT)
              : appended;
          });
          setFuture([]);
        }
        return hasChanged || resetHistory ? nextSlides : prev;
      });
    },
    []
  );

  const canUndo = history.length > 0;
  const canRedo = future.length > 0;

  const handleUndo = useCallback(() => {
    setHistory((hist) => {
      if (!hist.length) return hist;
      const snapshot = hist[hist.length - 1];
      setFuture((futureStack) => {
        const currentSnapshot = createHistorySnapshot(
          slidesRef.current,
          activeIdxRef.current,
          selectedItemIdsRef.current
        );
        const nextFuture = [...futureStack, currentSnapshot];
        return nextFuture.length > HISTORY_LIMIT
          ? nextFuture.slice(nextFuture.length - HISTORY_LIMIT)
          : nextFuture;
      });
      setSlides(snapshot.slides);
      const boundedIdx = Math.min(
        Math.max(snapshot.activeIdx ?? 0, 0),
        snapshot.slides.length ? snapshot.slides.length - 1 : 0
      );
      setActiveIdx(boundedIdx);
      setSelectedItemIds(Array.isArray(snapshot.selectedItemIds) ? snapshot.selectedItemIds : []);
      return hist.slice(0, -1);
    });
  }, []);

  const handleRedo = useCallback(() => {
    setFuture((futureStack) => {
      if (!futureStack.length) return futureStack;
      const snapshot = futureStack[futureStack.length - 1];
      setHistory((hist) => {
        const currentSnapshot = createHistorySnapshot(
          slidesRef.current,
          activeIdxRef.current,
          selectedItemIdsRef.current
        );
        const nextHistory = [...hist, currentSnapshot];
        return nextHistory.length > HISTORY_LIMIT
          ? nextHistory.slice(nextHistory.length - HISTORY_LIMIT)
          : nextHistory;
      });
      setSlides(snapshot.slides);
      const boundedIdx = Math.min(
        Math.max(snapshot.activeIdx ?? 0, 0),
        snapshot.slides.length ? snapshot.slides.length - 1 : 0
      );
      setActiveIdx(boundedIdx);
      setSelectedItemIds(Array.isArray(snapshot.selectedItemIds) ? snapshot.selectedItemIds : []);
      return futureStack.slice(0, -1);
    });
  }, []);
  const selectedItemId = selectedItemIds[0] ?? null;
  const setSelectedItemId = useCallback((id) => {
    setSelectedItemIds(id ? [id] : []);
  }, [setSelectedItemIds]);
  const handleItemMenuOpen = useCallback(
    (event, id) => {
      event.stopPropagation();
      setItemMenu({ anchorEl: event.currentTarget, itemId: id });
      setSelectedItemId(id);
    },
    [setSelectedItemId]
  );
  const handleItemMenuClose = useCallback(() => {
    setItemMenu({ anchorEl: null, itemId: null });
  }, []);

  // Analysis import dialog state
  const [analysisDlg, setAnalysisDlg] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisList, setAnalysisList] = useState([]);
  const [analysisFetchError, setAnalysisFetchError] = useState('');
  const [selectedAnalysisId, setSelectedAnalysisId] = useState('');
  const [analysisItems, setAnalysisItems] = useState([]);
  const [analysisSearch, setAnalysisSearch] = useState('');
  const [analysisSelectedKeys, setAnalysisSelectedKeys] = useState([]);
  const [analysisGraphsLoading, setAnalysisGraphsLoading] = useState(false);
  const [analysisSaving, setAnalysisSaving] = useState(false);
  const [analysisSaveError, setAnalysisSaveError] = useState('');
  const analysisCacheRef = useRef(new Map());
  const selectedAnalysis = useMemo(
    () => analysisList.find((a) => a.id === selectedAnalysisId) || null,
    [analysisList, selectedAnalysisId]
  );
  const filteredAnalysisItems = useMemo(() => {
    const queryStr = analysisSearch.trim().toLowerCase();
    if (!queryStr) return analysisItems;
    return analysisItems.filter((item) => {
      const haystack = [
        item.header,
        item.groupName,
        item.type,
        item.graphType,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(queryStr);
    });
  }, [analysisItems, analysisSearch]);

  const ensureAnalysesLoaded = useCallback(async () => {
    if (!organisation) return;
    setAnalysisLoading(true);
    try {
      const collRef = collection(db, 'organisations', organisation, 'analyses');
      let snap;
      try {
        snap = await getDocs(query(collRef, orderBy('createDate', 'desc')));
      } catch (err) {
        console.warn('analysis orderBy(createDate) failed, falling back without sort', err);
        snap = await getDocs(collRef);
      }
      setAnalysisList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('analysis list load error', err);
      setAnalysisFetchError(err.message || 'Analizler yüklenemedi.');
    } finally {
      setAnalysisLoading(false);
    }
  }, [organisation]);

  const fetchReportingRunTables = useCallback(
    async (analysis, ctx = {}) => {
      if (!organisation || !analysis) return [];
      const projectId = deriveProjectId(analysis, ctx);
      if (!projectId) return [];
      try {
        const runsRef = collection(db, 'reporting-results', projectId, 'runs');
        const getLatestRun = async (field) => {
          try {
            return await getDocs(query(runsRef, orderBy(field, 'desc'), limit(1)));
          } catch (err) {
            console.warn(`[PresentationBuilder] reporting run orderBy(${field}) failed`, err);
            return null;
          }
        };
        let runSnap = await getLatestRun('generatedAt');
        if (!runSnap || runSnap.empty) {
          runSnap = await getLatestRun('createdAt');
        }
        if (!runSnap || runSnap.empty) {
          runSnap = await getLatestRun('createDate');
        }
        if (!runSnap || runSnap.empty) {
          runSnap = await getDocs(query(runsRef, limit(1)));
        }
        if (!runSnap || runSnap.empty) return [];
        const runDoc = runSnap.docs[0];
        const tablesSnap = await getDocs(
          collection(db, 'reporting-results', projectId, 'runs', runDoc.id, 'tables')
        );
        if (tablesSnap.empty) return [];
        const ctxWithOrg = { ...(ctx || {}), organisationId: organisation, projectId };
        const items = [];
        tablesSnap.forEach((tableSnap) => {
          const data = tableSnap.data() || {};
          const columns = normalizeReportingTableColumns(data);
          if (!columns.length) return;
          const rows = normalizeReportingRows(data, columns);
          const datasetTable = buildDatasetTable({ columns }, rows);
          if (!datasetTable || datasetTable.rowCount <= 1) return;
          const title =
            data.title ||
            data.name ||
            data.tableName ||
            data.sheet ||
            `Tablo ${items.length + 1}`;
          const groupName = data.groupName || data.group_name || data.sheet || '';
          const tableAsset = prepareTableForPresentation(
            {
              header: title,
              table: {
                cells: datasetTable.cells,
                headerRows: datasetTable.headerRows,
                align: 'left',
                fontFamily: FONTS[0],
                fontSize: 13,
                border: {
                  color: '#111827',
                  width: 1,
                  style: 'solid',
                },
              },
              tableId: data.tableId || tableSnap.id,
              groupName,
              groupId: data.groupId || data.group_id || null,
              tableLink: {
                kind: TABLE_LINK_KIND,
                docPath: tableSnap.ref.path,
                tableId: data.tableId || tableSnap.id,
                organisationId: organisation,
                projectId,
                analysisId: analysis.id || null,
              },
            },
            ctxWithOrg
          );
          if (!tableAsset) return;
          items.push({
            uid: `reportingRun::${projectId}::${runDoc.id}::${tableSnap.id}`,
            header: tableAsset.header,
            type: 'table',
            groupName: tableAsset.__groupName || groupName || '',
            rowCount: datasetTable.rowCount ? datasetTable.rowCount - 1 : 0,
            prepared: tableAsset,
          });
        });
        return items;
      } catch (err) {
        console.warn('reporting run tables load error', err);
        return [];
      }
    },
    [organisation]
  );

  const fetchTablesForAnalysis = useCallback(
    async (analysis, ctx) => {
      if (!organisation || !analysis) return [];
      try {
        const ctxWithOrg = { ...(ctx || {}), organisationId: organisation };
        const projectId = deriveProjectId(analysis, ctxWithOrg);
        const items = [];
        const baseSegments = analysis.id
          ? ['organisations', organisation, 'analyses', analysis.id, 'tableDatasets']
          : projectId
            ? ['organisations', organisation, 'projects', projectId, 'tableDatasets']
            : null;
        if (baseSegments) {
          const tablesRef = collection(db, ...baseSegments);
          const tablesSnap = await getDocs(tablesRef);

          for (const docSnap of tablesSnap.docs) {
            const meta = docSnap.data() || {};
            const pagesRef = collection(docSnap.ref, 'pages');
            const pagesSnap = await getDocs(pagesRef);
            const rows = [];
            pagesSnap.forEach((pageDoc) => {
              const data = pageDoc.data();
              if (Array.isArray(data?.rows)) {
                rows.push(...data.rows);
              }
            });
            const datasetTable = buildDatasetTable(meta, rows);
            if (!datasetTable || datasetTable.rowCount <= 1) continue;

            const title =
              meta.title ||
              meta.name ||
              meta.sheet ||
              `Tablo ${items.length + 1}`;
            const groupName = meta.groupName || meta.sheet || '';
            const tableAsset = prepareTableForPresentation(
              {
                header: title,
                table: {
                  cells: datasetTable.cells,
                  headerRows: datasetTable.headerRows,
                  align: 'left',
                  fontFamily: FONTS[0],
                  fontSize: 13,
                  border: {
                    color: '#111827',
                    width: 1,
                    style: 'solid',
                  },
                },
                tableId: docSnap.id,
                groupName,
                groupId: meta.groupId || null,
                tableLink: {
                  kind: TABLE_LINK_KIND,
                  docPath: docSnap.ref.path,
                  tableId: docSnap.id,
                  organisationId: organisation,
                  analysisId: analysis.id || null,
                  projectId: projectId || null,
                },
              },
              ctxWithOrg
            );
            if (!tableAsset) continue;
            const uid = `table::${analysis.id || projectId}::${docSnap.id}`;
            items.push({
              uid,
              header: tableAsset.header,
              type: 'table',
              groupName: tableAsset.__groupName || groupName || '',
              rowCount: datasetTable.rowCount ? datasetTable.rowCount - 1 : 0,
              prepared: tableAsset,
            });
          }
        }

        const reportingTables = await fetchReportingRunTables(analysis, ctxWithOrg);
        return [...items, ...reportingTables];
      } catch (err) {
        console.error('analysis tables load error', err);
        throw err;
      }
    },
    [organisation, fetchReportingRunTables]
  );

  const loadAnalysisAssets = useCallback(
    async (analysis) => {
      if (!organisation || !analysis) return;
      const cache = analysisCacheRef.current.get(analysis.id);
      if (cache && Array.isArray(cache.items)) {
        setAnalysisItems(cache.items);
        setAnalysisFetchError(cache.error || '');
        return;
      }

      setAnalysisGraphsLoading(true);
      setAnalysisFetchError('');
      try {
        const dataset = await loadLatestFullGraphDataset(
          organisation,
          analysis.projectID,
          analysis.id
        );

        let projectCode =
          analysis.projectCode ||
          dataset?.projectCode ||
          dataset?.meta?.projectCode ||
          dataset?.meta?.project_code ||
          dataset?.meta?.projectCodeName ||
          '';
        const projectId =
          analysis.projectID ||
          dataset?.projectID ||
          dataset?.meta?.projectID ||
          dataset?.meta?.project_id ||
          '';
        if (!projectCode && projectId) {
          try {
            const projSnap = await getDoc(
              doc(db, 'organisations', organisation, 'projects', projectId)
            );
            if (projSnap.exists()) {
              const projData = projSnap.data();
              projectCode =
                projData.projectCode ||
                projData.title ||
                projData.name ||
                projData.projectName ||
                '';
            }
          } catch (projErr) {
            console.warn('project doc read failed', projErr);
          }
        }

        const analysisName =
          analysis.title ||
          analysis.name ||
          dataset?.meta?.analysisTitle ||
          dataset?.meta?.analysisName ||
          analysis.id;

        let graphItems = [];
        let graphError = '';
        if (dataset) {
          const datasetDocPath = sanitizeFirestorePath(dataset.__docPath || '');
          const graphs = extractGraphsFromPayload(dataset);
          graphItems = graphs
            .map((graph, idx) => {
              const prepared = prepareGraphForPresentation(graph, {
                analysisId: analysis.id,
                analysisName,
                projectId,
                projectCode,
                groupId: graph.__groupId,
                groupName: graph.__groupName,
                organisationId: organisation,
                graphDatasetDocPath: datasetDocPath,
                graphIndex: idx,
              });
              if (!prepared) return null;
              const header = prepared.header || `Grafik ${idx + 1}`;
              const graphType = prepared.graphType || graph.graphType || 'graph';
              const uid = `${analysis.id}::${graph.header || header}::${idx}`;
              return {
                uid,
                header,
                type: 'graph',
                graphType,
                groupName: prepared.__groupName || '',
                prepared,
              };
            })
            .filter(Boolean);
          if (!graphItems.length) {
            graphError = 'Analizde görüntülenecek grafik bulunamadı.';
          }
        } else {
          graphError = 'Seçilen analiz için kayıtlı grafik bulunamadı.';
        }

        let tableItems = [];
        let tableError = '';
        try {
          tableItems = await fetchTablesForAnalysis(analysis, {
            analysisId: analysis.id,
            analysisName,
            projectId,
            projectCode,
          });
        } catch (err) {
          tableError = err.message || 'Tablolar yüklenemedi.';
        }

        const combinedItems = [...graphItems, ...tableItems];
        const combinedError = [graphError, tableError].filter(Boolean).join(' ').trim();

        analysisCacheRef.current.set(analysis.id, { items: combinedItems, error: combinedError });
        setAnalysisItems(combinedItems);
        setAnalysisFetchError(combinedError);
      } catch (err) {
        console.error('analysis assets load error', err);
        const message = err.message || 'Analize ait içerikler yüklenemedi.';
        setAnalysisItems([]);
        setAnalysisFetchError(message);
        analysisCacheRef.current.set(analysis.id, { items: [], error: message });
      } finally {
        setAnalysisGraphsLoading(false);
      }
    },
    [organisation, fetchTablesForAnalysis]
  );

  const toggleAnalysisItem = useCallback((uid) => {
    setAnalysisSelectedKeys((prev) =>
      prev.includes(uid) ? prev.filter((key) => key !== uid) : [...prev, uid]
    );
  }, []);

  const handleSelectAllFiltered = useCallback(() => {
    setAnalysisSelectedKeys((prev) => {
      const filteredIds = filteredAnalysisItems.map((item) => item.uid);
      if (!filteredIds.length) return prev;
      const allSelected = filteredIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !filteredIds.includes(id));
      }
      const merged = new Set(prev);
      filteredIds.forEach((id) => merged.add(id));
      return Array.from(merged);
    });
  }, [filteredAnalysisItems]);

  const allFilteredSelected = useMemo(() => {
    if (!filteredAnalysisItems.length) return false;
    return filteredAnalysisItems.every((item) => analysisSelectedKeys.includes(item.uid));
  }, [filteredAnalysisItems, analysisSelectedKeys]);

  const selectedCount = analysisSelectedKeys.length;

  const reloadSelectedAnalysis = useCallback(() => {
    if (!selectedAnalysis) return;
    analysisCacheRef.current.delete(selectedAnalysis.id);
    loadAnalysisAssets(selectedAnalysis);
  }, [selectedAnalysis, loadAnalysisAssets]);

  const handleImportSelected = useCallback(async () => {
    if (!organisation || !presentationID) return;
    if (!selectedAnalysis) return;
    const selectedItems = analysisItems.filter((item) => analysisSelectedKeys.includes(item.uid));
    if (!selectedItems.length) return;
    setAnalysisSaving(true);
    setAnalysisSaveError('');
    try {
      const colRef = collection(
        db,
        'organisations',
        organisation,
        'presentations',
        presentationID,
        'savedGraphs'
      );
      await Promise.all(
        selectedItems.map((item) => {
          const prepared = item.prepared || {};
          let payload = prepared;
          if ((prepared.assetType || item.type) === 'table') {
            const { table, ...rest } = prepared;
            payload = {
              ...rest,
              assetType: 'table',
              tableJSON: table ? JSON.stringify(table) : null,
            };
            if (prepared.tableLink) {
              payload.tableLink = prepared.tableLink;
            }
          }
          return addDoc(colRef, {
            graph: payload,
            addedAt: serverTimestamp(),
            source: {
              analysisId: selectedAnalysis.id,
              analysisTitle:
                selectedAnalysis.title ||
                selectedAnalysis.name ||
                selectedAnalysis.analysisTitle ||
                selectedAnalysis.id,
              itemType: item.type || prepared.assetType || 'graph',
            },
          });
        })
      );
      setAnalysisDlg(false);
      setAnalysisSelectedKeys([]);
    } catch (err) {
      console.error('analysis import error', err);
      setAnalysisSaveError(err.message || 'Öğeler eklenemedi.');
    } finally {
      setAnalysisSaving(false);
    }
  }, [organisation, presentationID, selectedAnalysis, analysisItems, analysisSelectedKeys]);

  useEffect(() => {
    if (!analysisDlg) return;
    if (!analysisList.length && !analysisLoading) {
      ensureAnalysesLoaded();
    }
  }, [analysisDlg, analysisList.length, analysisLoading, ensureAnalysesLoaded]);

useEffect(() => {
  if (analysisDlg) {
    setAnalysisFetchError('');
    setAnalysisSaveError('');
  }
}, [analysisDlg]);

  const applyLinkedTableUpdate = useCallback(
    (linkKey, incoming) => {
      if (!linkKey || !incoming || !Array.isArray(incoming.cells)) return;
      const serialized = JSON.stringify(incoming);
      if (linkedTableCacheRef.current.get(linkKey) === serialized) return;
      linkedTableCacheRef.current.set(linkKey, serialized);
      mutateSlides(
        (prevSlides) => {
          let mutated = false;
          const nextSlides = prevSlides.map((slide) => {
            if (!slide || !Array.isArray(slide.items)) return slide;
            let slideChanged = false;
            const nextItems = slide.items.map((item) => {
              if (!item || item.type !== 'table') return item;
              const link = sanitizeTableLink(item.tableLink);
              if (!link || getTableLinkKey(link) !== linkKey) return item;
              slideChanged = true;
              return {
                ...item,
                table: mergeLinkedTableData(item.table, incoming),
              };
            });
            if (slideChanged) {
              mutated = true;
              return { ...slide, items: nextItems };
            }
            return slide;
          });
          return mutated ? nextSlides : prevSlides;
        },
        { recordHistory: false }
      );
    },
    [mutateSlides]
  );

  useEffect(() => {
    if (!analysisDlg) return;
    if (!selectedAnalysisId && analysisList.length) {
      setSelectedAnalysisId(analysisList[0].id);
    }
  }, [analysisDlg, analysisList, selectedAnalysisId]);

  useEffect(() => {
    if (!analysisDlg) return;
    setAnalysisSelectedKeys([]);
    setAnalysisSearch('');
    setAnalysisSaveError('');
    if (!selectedAnalysis) {
      setAnalysisItems([]);
      setAnalysisFetchError('');
      return;
    }
    const cache = analysisCacheRef.current.get(selectedAnalysis.id);
    if (cache && Array.isArray(cache.items)) {
      setAnalysisItems(cache.items);
      setAnalysisFetchError(cache.error || '');
      if (cache.items.length || cache.error) {
        return;
      }
    }
    loadAnalysisAssets(selectedAnalysis);
  }, [analysisDlg, selectedAnalysis, loadAnalysisAssets]);

// Grafik Ekle dialog state
const [graphDlg, setGraphDlg] = useState(false);
const [tempGraphHeader, setTempGraphHeader] = useState('');
const [shapeMenuAnchor, setShapeMenuAnchor] = useState(null);
const [tableMenuAnchor, setTableMenuAnchor] = useState(null);
const [linkedTableDlg, setLinkedTableDlg] = useState(false);
const [linkedTableSearch, setLinkedTableSearch] = useState('');
const [layoutMenuAnchor, setLayoutMenuAnchor] = useState(null);
const [iconDialogState, setIconDialogState] = useState({ open: false, targetId: null });
const [iconSearch, setIconSearch] = useState('');

useEffect(() => {
  if (!iconDialogState.open) {
    setIconSearch('');
  }
}, [iconDialogState.open]);

useEffect(() => {
  if (!linkedTableDlg) {
    setLinkedTableSearch('');
  }
}, [linkedTableDlg]);

const filteredIconOptions = useMemo(() => {
  const query = iconSearch.trim().toLowerCase();
  if (!query) return LUCIDE_ICON_OPTIONS;
  return LUCIDE_ICON_OPTIONS.filter((opt) => opt.keywords.includes(query));
}, [iconSearch]);

const [globalPaletteKey, setGlobalPaletteKey] = useState('default');

  // Bulk add graphs dialog state
  const [bulkDlg, setBulkDlg] = useState(false);
  const [bulkSelectedHeaders, setBulkSelectedHeaders] = useState([]);
  const [bulkType, setBulkType] = useState('bar');
  const [bulkOrientation, setBulkOrientation] = useState('vertical');
  const [bulkDataLabels, setBulkDataLabels] = useState(true);
  const [bulkLabelPos, setBulkLabelPos] = useState('inside'); // inside | outside | top
  const [bulkGrid, setBulkGrid] = useState(true);
  const [bulkPaletteKey, setBulkPaletteKey] = useState('default');
  const [bulkMetric, setBulkMetric] = useState('count'); // 'count' | 'percentage'
  const [bulkLegend, setBulkLegend] = useState(true);    // show legend?
  const [bulkBaseType, setBulkBaseType] = useState('none'); // 'none' | 'participants' | 'answers'
  const [bulkAutoScale, setBulkAutoScale] = useState(true); // auto y-ekseni ölçeği
  const [bulkSearch, setBulkSearch] = useState(''); // bulk list filter
  const [bulkPlacementMode, setBulkPlacementMode] = useState('separate'); // 'separate' | 'single'
  const [bulkTargetSlideId, setBulkTargetSlideId] = useState('');
  // Margins (px on the canvas)
  const [bulkMarginTop, setBulkMarginTop] = useState(20);
  const [bulkMarginRight, setBulkMarginRight] = useState(20);
  const [bulkMarginBottom, setBulkMarginBottom] = useState(20);
  const [bulkMarginLeft, setBulkMarginLeft] = useState(20);
  const bulkInitRef = useRef(false);

  /* ---------- Firestore listeners -------------------- */
  useEffect(() => {
    if (!presentationID || !organisation) return;
    return onSnapshot(doc(db, 'organisations', organisation, 'presentations', presentationID), (snap) => {
      const d = snap.data() || {};
      const incomingSlides = normalizeSlides(d.slides);
      const incomingBrandColors = Array.isArray(d.brandColors) ? d.brandColors : [];
      const incomingTemplate = d.hasOwnProperty('template') ? d.template ?? null : null;
      const incomingPaletteKey =
        typeof d.globalPaletteKey === 'string' && d.globalPaletteKey
          ? d.globalPaletteKey
          : 'default';

      mutateSlides(incomingSlides, { recordHistory: false, resetHistory: true });
      setBrandColors(incomingBrandColors);
      setTemplate(incomingTemplate);
      setGlobalPaletteKey(incomingPaletteKey);

      const serialized = JSON.stringify({
        slides: prepareSlidesForSave(incomingSlides),
        brandColors: incomingBrandColors,
        template: incomingTemplate,
        globalPaletteKey: incomingPaletteKey ?? 'default',
      });
      lastSavedPayloadRef.current = serialized;
      setHasUnsavedChanges(false);
      setIsSaving(false);
      setSaveError('');
    });
  }, [presentationID, organisation, mutateSlides]);

  useEffect(() => {
    if (!organisation || !presentationID) return;
    return onSnapshot(collection(db, 'organisations', organisation, 'presentations', presentationID, 'savedGraphs'), (snap) => {
      const assets = snap.docs
        .map((d) => {
          const payload = d.data()?.graph ?? d.data();
          if (!payload) return null;
          const assetType =
            payload.assetType ||
            (payload.graphType === 'table' || payload.table ? 'table' : 'graph');
          let tableData = payload.table || null;
          if (assetType === 'table' && !tableData && typeof payload.tableJSON === 'string') {
            try {
              tableData = JSON.parse(payload.tableJSON);
            } catch (err) {
              console.warn('savedGraphs tableJSON parse failed', err);
            }
          }
          const graphLink = sanitizeGraphLink(payload.graphLink);
          const tableLink = sanitizeTableLink(payload.tableLink);
          return {
            ...payload,
            assetType,
            table: tableData || payload.table || null,
            ...(tableLink ? { tableLink } : {}),
            ...(graphLink ? { graphLink } : {}),
          };
        })
        .filter(Boolean);
      setSavedAssets(assets);
    });
  }, [organisation, presentationID]);

  const normalizedExternalGraphs = useMemo(
    () =>
      (extGraphs || []).map((graph) => {
        if (!graph) return null;
        const assetType =
          graph.assetType ||
          (graph.graphType === 'table' || graph.table ? 'table' : 'graph');
        let tableData = graph.table || null;
        if (!tableData && graph.data?.table) {
          tableData = graph.data.table;
        }
        if (!tableData && typeof graph.tableJSON === 'string') {
          try {
            tableData = JSON.parse(graph.tableJSON);
          } catch (err) {
            console.warn('ext graph tableJSON parse failed', err);
          }
        }
        const tableLink = sanitizeTableLink(graph.tableLink);
        const graphLink = sanitizeGraphLink(graph.graphLink);
        if (assetType === 'table') {
          return {
            ...graph,
            assetType,
            table: tableData,
            ...(tableLink ? { tableLink } : {}),
          };
        }
        return {
          ...graph,
          assetType,
          ...(graphLink ? { graphLink } : {}),
        };
      }).filter(Boolean),
    [extGraphs]
  );

  const savedGraphAssets = useMemo(
    () => savedAssets.filter((asset) => (asset.assetType || 'graph') !== 'table'),
    [savedAssets]
  );

  const savedTableAssets = useMemo(
    () => savedAssets.filter((asset) => (asset.assetType || 'graph') === 'table'),
    [savedAssets]
  );

  const savedLinkedTableAssets = useMemo(
    () => savedTableAssets.filter((asset) => sanitizeTableLink(asset.tableLink)),
    [savedTableAssets]
  );

  const allGraphs = useMemo(
    () => [...savedGraphAssets, ...normalizedExternalGraphs.filter((asset) => (asset.assetType || 'graph') !== 'table')],
    [savedGraphAssets, normalizedExternalGraphs]
  );

  // Filtered graphs for bulk dialog
  const bulkFilteredGraphs = useMemo(() => {
    const q = String(bulkSearch || '').trim().toLowerCase();
    if (!q) return allGraphs;
    return allGraphs.filter(g => String(g?.header || '').toLowerCase().includes(q));
  }, [allGraphs, bulkSearch]);

  const filteredLinkedTables = useMemo(() => {
    const query = linkedTableSearch.trim().toLowerCase();
    if (!query) return savedLinkedTableAssets;
    return savedLinkedTableAssets.filter((tbl) => {
      const haystack = [
        tbl.header,
        tbl.groupName,
        tbl.__groupName,
        tbl.__analysisName,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [linkedTableSearch, savedLinkedTableAssets]);

  const linkedTableEntries = useMemo(() => {
    const map = new Map();
    slides.forEach((slide) => {
      if (!slide || !Array.isArray(slide.items)) return;
      slide.items.forEach((item) => {
        if (!item || item.type !== 'table') return;
        const link = sanitizeTableLink(item.tableLink);
        if (!link) return;
        const key = getTableLinkKey(link);
        if (!key) return;
        if (!map.has(key)) {
          map.set(key, { link, itemIds: new Set() });
        }
        map.get(key).itemIds.add(item.id);
      });
    });
    return Array.from(map.entries()).map(([key, record]) => ({
      key,
      link: record.link,
      itemIds: Array.from(record.itemIds),
    }));
  }, [slides]);

  useEffect(() => {
    if (!organisation || !linkedTableEntries.length) return undefined;
    const unsubscribers = [];
    linkedTableEntries.forEach(({ key, link }) => {
      if (!key || !link) return;
      if (link.organisationId && link.organisationId !== organisation) return;
      const segments = link.docPath.split('/').filter(Boolean);
      if (!segments.length || segments.length % 2 !== 0) return;
      try {
        const docRef = doc(db, ...segments);
        const state = { meta: null, rows: [] };
        const emit = () => {
          if (!state.meta) return;
          const built = buildDatasetTable(state.meta, state.rows);
          if (!built) return;
          applyLinkedTableUpdate(key, built);
        };
        const unsubMeta = onSnapshot(
          docRef,
          (snapshot) => {
            state.meta = snapshot.exists() ? snapshot.data() : null;
            emit();
          },
          (err) => console.error('linked table meta error', err)
        );
        const unsubPages = onSnapshot(
          collection(docRef, 'pages'),
          (pagesSnap) => {
            const rows = [];
            pagesSnap.forEach((pageDoc) => {
              const data = pageDoc.data();
              if (Array.isArray(data?.rows)) {
                rows.push(...data.rows);
              }
            });
            state.rows = rows;
            emit();
          },
          (err) => console.error('linked table rows error', err)
        );
        unsubscribers.push(() => {
          unsubMeta();
          unsubPages();
        });
      } catch (err) {
        console.error('linked table subscription error', err);
      }
    });
    return () => {
      unsubscribers.forEach((fn) => {
        try {
          fn?.();
        } catch {
          // ignore cleanup errors
        }
      });
    };
  }, [organisation, linkedTableEntries, applyLinkedTableUpdate]);

  const globalPaletteColors = useMemo(
    () => COLOR_PALETTES[globalPaletteKey] || null,
    [globalPaletteKey]
  );

  const brandColorMap = useMemo(() => {
    const m = {};
    (brandColors || []).forEach((b) => {
      if (!b || !b.name) return;
      m[String(b.name).trim().toLowerCase()] = b.color || '#000000';
    });
    return m;
  }, [brandColors]);

  const surfaceBaseSx = {
    px: { xs: 2, md: 3 },
    py: { xs: 2, md: 2.5 },
  };

  const primaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    px: { xs: 2, md: 2.5 },
    py: 0.95,
    borderRadius: 999,
    boxShadow: '0 12px 24px rgba(148, 163, 184, 0.28)',
    border: '1px solid rgba(148, 163, 184, 0.25)',
    '&:hover': {
      boxShadow: '0 16px 28px rgba(148, 163, 184, 0.32)',
    },
  };

  const historyIconButtonSx = {
    borderRadius: 2,
    border: '1px solid rgba(148, 163, 184, 0.25)',
    backgroundColor: '#ffffff',
    color: '#1f2937',
    p: 0.75,
    boxShadow: '0 8px 18px rgba(148, 163, 184, 0.2)',
    transition: 'background-color 120ms ease, box-shadow 160ms ease',
    '&:hover': {
      backgroundColor: 'rgba(226, 232, 240, 0.85)',
      boxShadow: '0 12px 22px rgba(148, 163, 184, 0.28)',
    },
    '&.Mui-disabled': {
      color: 'rgba(15, 23, 42, 0.35)',
      boxShadow: 'none',
      backgroundColor: '#f8fafc',
    },
  };

  const heroChipSx = {
    borderColor: 'rgba(148, 163, 184, 0.4)',
    bgcolor: 'rgba(224, 231, 255, 0.6)',
    color: '#1f2937',
    fontWeight: 600,
    '& .MuiChip-icon': { color: '#3b82f6' },
  };

  const heroChips = [
    presentationID ? `Sunum ID: ${presentationID}` : null,
    slides.length ? `Slayt: ${slides.length}` : null,
    brandColors.length ? `Marka Renkleri: ${brandColors.length}` : null,
    template?.mode
      ? `Tema: ${
          template.mode === 'image'
            ? 'Görsel'
            : template.mode === 'color'
              ? 'Renkli'
            : template.mode
        }`
      : null,
    globalPaletteKey && globalPaletteKey !== 'none' ? `Palet: ${globalPaletteKey}` : null,
    hasUnsavedChanges ? 'Kaydedilmemiş değişiklikler var' : null,
  ].filter(Boolean);

  const heroSubtitle =
    'Slaytlarınızı düzenleyin, grafikleri ve tabloları yerleştirip sunumunuzu dakikalar içinde dışa aktarın.';

  useEffect(() => {
    if (bulkDlg && !bulkInitRef.current) {
      setBulkSelectedHeaders(allGraphs.map(g => g.header));
      const first = allGraphs[0];
      if (first) {
        setBulkType(first.graphType || 'bar');
      setBulkOrientation(first.orientation || (ORIENTABLE_TYPES.includes(first.graphType) ? 'vertical' : 'horizontal'));
      }
      setBulkPaletteKey(globalPaletteKey || 'default');
      setBulkMetric(first?.metric || 'count');
      const firstSettings = { ...DEFAULT_SETTINGS, ...(first?.settings || {}) };
      setBulkLegend(!!firstSettings.legend);
      setBulkBaseType(firstSettings.baseType || 'none');
      setBulkAutoScale(firstSettings.autoScale !== false);
      // default margins (keep last used if already set)
      setBulkMarginTop((v) => (typeof v === 'number' ? v : 20));
      setBulkMarginRight((v) => (typeof v === 'number' ? v : 20));
      setBulkMarginBottom((v) => (typeof v === 'number' ? v : 20));
      setBulkMarginLeft((v) => (typeof v === 'number' ? v : 20));
      setBulkPlacementMode('separate');
      setBulkTargetSlideId((slides[activeIdx] && slides[activeIdx].id) || (slides[0] && slides[0].id) || '');
      bulkInitRef.current = true;
    }
    if (!bulkDlg && bulkInitRef.current) {
      // allow re-initialization next time the dialog opens
      bulkInitRef.current = false;
    }
  }, [bulkDlg]);

  const buildSavePayload = useCallback(() => {
    return {
      slides: prepareSlidesForSave(slides),
      brandColors: Array.isArray(brandColors) ? brandColors : [],
      template: template ?? null,
      globalPaletteKey: globalPaletteKey ?? 'default',
    };
  }, [slides, brandColors, template, globalPaletteKey]);

  useEffect(() => {
    if (!presentationID || !organisation) {
      setHasUnsavedChanges(false);
      return;
    }
    const current = JSON.stringify(buildSavePayload());
    if (current !== lastSavedPayloadRef.current) {
      setHasUnsavedChanges(true);
    } else {
      setHasUnsavedChanges(false);
    }
  }, [buildSavePayload, organisation, presentationID]);

  const handleSave = useCallback(async () => {
    if (!organisation || !presentationID) return;
    const payload = buildSavePayload();
    const payloadString = JSON.stringify(payload);
    setIsSaving(true);
    setSaveError('');
    try {
      await setDoc(
        doc(db, 'organisations', organisation, 'presentations', presentationID),
        payload,
        { merge: true }
      );
      lastSavedPayloadRef.current = payloadString;
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('presentation save failed', err);
      setSaveError(err.message || 'Değişiklikler kaydedilemedi.');
    } finally {
      setIsSaving(false);
    }
  }, [organisation, presentationID, buildSavePayload]);

  /* ---------- Slide helpers --------------------------- */
  const addSlide = (layoutKey = DEFAULT_LAYOUT) => {
    const validLayout = SLIDE_LAYOUT_MAP[layoutKey] ? layoutKey : DEFAULT_LAYOUT;
    const layoutItems = createLayoutItems(validLayout);
    const newSlide = {
      id: uuid(),
      name: `Sayfa ${slides.length + 1}`,
      items: layoutItems,
      layout: validLayout,
    };
    mutateSlides((prev) => [...prev, newSlide]);
    setActiveIdx(slides.length);
    setSelectedItemId(layoutItems[0]?.id || null);
  };

  const deleteSlide = (idx) => {
    if (slides.length === 1) return;
    mutateSlides((prev) => prev.filter((_, i) => i !== idx));
    setActiveIdx((prev) => (idx === prev ? 0 : prev > idx ? prev - 1 : prev));
  };

  const renameSlide = (idx, newName) => {
    if (!newName) return;
    mutateSlides((prev) => prev.map((s, i) => (i === idx ? { ...s, name: newName } : s)));
  };

  /* ---------- Item helpers ---------------------------- */
  const activeSlide = slides[activeIdx] || { items: [], layout: DEFAULT_LAYOUT };
  const activeLayoutKey = SLIDE_LAYOUT_MAP[activeSlide.layout]
    ? activeSlide.layout
    : DEFAULT_LAYOUT;
  const iconDialogTarget = useMemo(
    () =>
      iconDialogState.open && iconDialogState.targetId
        ? activeSlide.items.find(
            (it) => it.id === iconDialogState.targetId && it.type === 'icon'
          ) || null
        : null,
    [activeSlide.items, iconDialogState.open, iconDialogState.targetId]
  );
  const selectedItem = activeSlide.items.find((it) => it.id === selectedItemId) || null;
  const isMultiSelection = selectedItemIds.length > 1;
  const activeMenuItem = useMemo(
    () => activeSlide.items.find((it) => it.id === itemMenu.itemId) || null,
    [activeSlide.items, itemMenu.itemId]
  );
  const [activeRichTextId, setActiveRichTextId] = useState(null);
  const activeRichTextElementRef = useRef(null);
  const activeRichTextContextRef = useRef(null);
  const [activeRichTextContext, setActiveRichTextContext] = useState(null);
  const savedRichTextSelectionRef = useRef(null);
  const [richTextSelectionState, setRichTextSelectionState] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [activeTableCell, setActiveTableCell] = useState(null);

  useEffect(() => {
    if (!Array.isArray(activeSlide.items) || !activeSlide.items.length) {
      if (selectedItemIds.length) {
        setSelectedItemIds([]);
      }
      return;
    }
    setSelectedItemIds((prev) => {
      if (!prev.length) return prev;
      const allowed = new Set(activeSlide.items.map((it) => it.id));
      const filtered = prev.filter((id) => allowed.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [activeSlide.items, selectedItemIds.length, setSelectedItemIds]);

  const multiSelectionBounds = useMemo(() => {
    if (!isMultiSelection) return null;
    const included = activeSlide.items.filter((it) => selectedItemIds.includes(it.id));
    if (!included.length) return null;
    const minX = Math.min(...included.map((it) => it.x));
    const minY = Math.min(...included.map((it) => it.y));
    const maxX = Math.max(...included.map((it) => it.x + it.w));
    const maxY = Math.max(...included.map((it) => it.y + it.h));
    return {
      x: minX,
      y: minY,
      w: maxX - minX,
      h: maxY - minY,
    };
  }, [activeSlide.items, isMultiSelection, selectedItemIds]);

useEffect(() => {
  const isRichTextTarget = selectedItem && (selectedItem.type === 'text' || selectedItem.type === 'table');
  if (!isRichTextTarget) {
    if (activeRichTextId !== null) {
      setActiveRichTextId(null);
    }
    activeRichTextElementRef.current = null;
    activeRichTextContextRef.current = null;
    if (activeRichTextContext !== null) {
      setActiveRichTextContext(null);
    }
  }
  if (!selectedItem || selectedItem.type !== 'table') {
    if (activeTableCell !== null) {
      setActiveTableCell(null);
    }
  } else {
    const ctx = activeRichTextContextRef.current;
    if (ctx && ctx.type === 'table' && ctx.itemId !== selectedItem.id) {
      activeRichTextElementRef.current = null;
      activeRichTextContextRef.current = null;
      setActiveRichTextContext((prev) => (prev && prev.type === 'table' ? null : prev));
      if (activeRichTextId !== null && typeof activeRichTextId === 'string' && activeRichTextId.startsWith(`${ctx.itemId}::`)) {
        setActiveRichTextId(null);
      }
      savedRichTextSelectionRef.current = null;
    }
  }
}, [activeRichTextContext, activeRichTextId, activeTableCell, selectedItem]);

useEffect(() => {
  if (!activeTableCell) return;
  if (!selectedItem || selectedItem.id !== activeTableCell.itemId) {
    setActiveTableCell(null);
  }
}, [activeTableCell, selectedItem]);

  useEffect(() => {
    if (!activeRichTextId) {
      savedRichTextSelectionRef.current = null;
      setRichTextSelectionState({ bold: false, italic: false, underline: false });
    }
  }, [activeRichTextId]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handleSelectionChange = () => {
      const editor = activeRichTextElementRef.current;
      if (!editor) return;
      const selection = document.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const range = selection.getRangeAt(0);
      if (!editor.contains(range.commonAncestorContainer)) return;
      savedRichTextSelectionRef.current = range.cloneRange();
      try {
        const bold = document.queryCommandState('bold');
        const italic = document.queryCommandState('italic');
        const underline = document.queryCommandState('underline');
        setRichTextSelectionState({
          bold: !!bold,
          italic: !!italic,
          underline: !!underline,
        });
      } catch (err) {
        // ignore unsupported command
      }
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [activeRichTextId]);

  const updateItems = useCallback(
    (updater, options) => {
      mutateSlides(
        (prev) =>
          prev.map((s, i) => {
            if (i !== activeIdx) return s;
            const currentItems = Array.isArray(s.items) ? s.items : [];
            const nextItems = typeof updater === 'function' ? updater(currentItems) : updater;
            return { ...s, items: nextItems };
          }),
        options
      );
    },
    [activeIdx, mutateSlides]
  );

  const getTableItemById = useCallback((itemId) => {
    if (!itemId) return null;
    const slidesCurrent = slidesRef.current || [];
    for (const slide of slidesCurrent) {
      if (!slide || !Array.isArray(slide.items)) continue;
      const match = slide.items.find((it) => it.id === itemId && it.type === 'table');
      if (match) return match;
    }
    return null;
  }, []);

  const updateTableItem = useCallback(
    (itemId, updater) => {
      updateItems((items) =>
        items.map((it) => {
          if (it.id !== itemId || it.type !== 'table') return it;
          const safe = ensureTableData(it.table);
          const working = {
            ...safe,
            cells: safe.cells.map((row) => [...row]),
            columnSizes: [...safe.columnSizes],
            rowSizes: [...safe.rowSizes],
            border: { ...safe.border },
          };
          const result = updater(working) || working;
          return { ...it, table: ensureTableData(result) };
        })
      );
    },
    [updateItems]
  );

  const detachLinkedTable = useCallback(
    (itemId) => {
      updateItems((items) =>
        items.map((it) => {
          if (it.id !== itemId || it.type !== 'table' || !it.tableLink) return it;
          const next = { ...it };
          delete next.tableLink;
          return next;
        })
      );
    },
    [updateItems]
  );

  const updateImageItem = useCallback(
    (itemId, patch) => {
      updateItems((items) =>
        items.map((it) => {
          if (it.id !== itemId || it.type !== 'image') return it;
          if (typeof patch === 'function') {
            return patch(it) || it;
          }
          return { ...it, ...(patch || {}) };
        })
      );
    },
    [updateItems]
  );

  const updateTextItemFromRichContent = useCallback(
    (itemId, { html, plain, measuredHeight }) => {
      updateItems((items) =>
        items.map((p) => {
          if (p.id !== itemId) return p;
          const fontSize = p.style?.fontSize ?? DEFAULT_TEXT_STYLE.fontSize;
          const baselineHeight = Math.max(
            MIN_TEXT_ITEM_HEIGHT,
            Math.round(fontSize * 1.35)
          );
          const safeMeasured = Number.isFinite(measuredHeight)
            ? Math.round(measuredHeight + TEXT_AUTO_GROW_PADDING)
            : p.h;
          const desiredHeight = Math.max(baselineHeight, safeMeasured);
          const next = {
            ...p,
            richText: html,
            text: plain,
          };
          if (desiredHeight > p.h + TEXT_AUTO_GROW_PADDING) {
            next.h = Math.max(p.h, desiredHeight);
          }
          return next;
        })
      );
    },
    [updateItems]
  );

  const updateTableCellFromRichContent = useCallback(
    (itemId, rowIndex, colIndex, { html }) => {
      const tableItem = getTableItemById(itemId);
      if (sanitizeTableLink(tableItem?.tableLink)) {
        return;
      }
      const nextHtml = html && html.trim() ? html : EMPTY_RICH_TEXT_HTML;
      updateTableItem(itemId, (table) => {
        if (!Array.isArray(table.cells[rowIndex])) return table;
        if (table.cells[rowIndex][colIndex] === nextHtml) return table;
        const nextCells = table.cells.map((row, r) =>
          row.map((cell, c) => (r === rowIndex && c === colIndex ? nextHtml : cell)),
        );
        return { ...table, cells: nextCells };
      });
    },
    [getTableItemById, updateTableItem]
  );

  const handleTableCellActivate = useCallback(
    ({ itemId, rowIndex, colIndex, element }) => {
      if (!element) return;
      setSelectedItemId(itemId);
      activeRichTextElementRef.current = element;
      const context = { type: 'table', itemId, rowIndex, colIndex };
      activeRichTextContextRef.current = context;
      setActiveRichTextContext(context);
      setActiveTableCell(context);
      const key = `${itemId}::${rowIndex}-${colIndex}`;
      setActiveRichTextId((prev) => (prev === key ? prev : key));
    },
    [setSelectedItemId]
  );

  const handleTableCellDeactivate = useCallback(
    ({ itemId, rowIndex, colIndex, element }) => {
      const context = activeRichTextContextRef.current;
      if (!context || context.type !== 'table') return;
      if (context.itemId !== itemId || context.rowIndex !== rowIndex || context.colIndex !== colIndex) return;
      if (activeRichTextElementRef.current === element) {
        activeRichTextElementRef.current = null;
      } else if (activeRichTextElementRef.current && activeRichTextElementRef.current.dataset?.itemId === itemId) {
        activeRichTextElementRef.current = null;
      }
      activeRichTextContextRef.current = null;
      setActiveRichTextContext(null);
      const key = `${itemId}::${rowIndex}-${colIndex}`;
      setActiveRichTextId((prev) => (prev === key ? null : prev));
      setActiveTableCell((prev) =>
        prev && prev.itemId === itemId && prev.rowIndex === rowIndex && prev.colIndex === colIndex ? null : prev
      );
      savedRichTextSelectionRef.current = null;
    },
    []
  );

  const commitRichTextEditorMutation = useCallback(
    (mutator, { refreshState = true } = {}) => {
      if (!activeRichTextElementRef.current || !activeRichTextId) return;
      const context = activeRichTextContextRef.current;
      if (!context) return;
      if (typeof document === 'undefined') return;
      const editor = activeRichTextElementRef.current;
      const selection = document.getSelection();
      editor.focus({ preventScroll: true });
      if (selection && savedRichTextSelectionRef.current) {
        try {
          selection.removeAllRanges();
          selection.addRange(savedRichTextSelectionRef.current);
        } catch (err) {
          // ignore invalid range
        }
      }
      mutator(editor, selection);
      const raw = editor.innerHTML;
      let sanitized = sanitizeRichTextHtml(raw);
      if (!sanitized || !sanitized.trim()) {
        sanitized = EMPTY_RICH_TEXT_HTML;
      }
      if (sanitized !== raw) {
        editor.innerHTML = sanitized;
      }
      const plain = extractPlainTextFromRichText(sanitized);
      const measuredHeight = editor.scrollHeight;
      if (context.type === 'text') {
        updateTextItemFromRichContent(context.itemId, { html: sanitized, plain, measuredHeight });
      } else if (context.type === 'table') {
        updateTableCellFromRichContent(context.itemId, context.rowIndex, context.colIndex, {
          html: sanitized,
          plain,
        });
      }
      if (refreshState) {
        try {
          const bold = document.queryCommandState('bold');
          const italic = document.queryCommandState('italic');
          const underline = document.queryCommandState('underline');
          setRichTextSelectionState({
            bold: !!bold,
            italic: !!italic,
            underline: !!underline,
          });
        } catch (err) {
          // ignore unsupported command
        }
      }
      if (selection && selection.rangeCount > 0) {
        try {
          savedRichTextSelectionRef.current = selection.getRangeAt(0).cloneRange();
        } catch (err) {
          savedRichTextSelectionRef.current = null;
        }
      }
    },
    [activeRichTextId, updateTableCellFromRichContent, updateTextItemFromRichContent]
  );

  const execCommandSafe = (command, value = null, showUI = false) => {
    if (typeof document === 'undefined' || typeof document.execCommand !== 'function') return false;
    try {
      return document.execCommand(command, showUI, value);
    } catch (err) {
      return false;
    }
  };

  const convertFontElementToSpan = (fontEl, overrides = {}) => {
    if (!fontEl || fontEl.nodeName.toLowerCase() !== 'font') return null;
    const span = document.createElement('span');
    const existingStyle = fontEl.getAttribute('style');
    if (existingStyle) {
      span.setAttribute('style', existingStyle);
    }

    const colorAttr = fontEl.getAttribute('color');
    if (colorAttr) span.style.color = colorAttr;

    const faceAttr = fontEl.getAttribute('face');
    if (faceAttr) span.style.fontFamily = faceAttr;

    const sizeAttr = fontEl.getAttribute('size');
    if (sizeAttr) {
      const mapped = FONT_SIZE_COMMAND_MAP[sizeAttr] || null;
      if (mapped) span.style.fontSize = `${mapped}px`;
    }

    Object.entries(overrides).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        span.style[key] = value;
      }
    });

    while (fontEl.firstChild) {
      span.appendChild(fontEl.firstChild);
    }
    fontEl.replaceWith(span);
    return span;
  };

  function applyFontFamily(fontFamily) {
    const context = activeRichTextContextRef.current;
    const isTextItem = selectedItem && selectedItem.type === 'text';
    const isTableCell = context && context.type === 'table';
    if (!isTextItem && !isTableCell) return;
    commitRichTextEditorMutation((editor, selection) => {
      if (!selection || selection.isCollapsed) return;
      execCommandSafe('styleWithCSS', true);
      execCommandSafe('fontName', fontFamily);
      execCommandSafe('styleWithCSS', false);
      const fontNodes = editor.querySelectorAll('font[face]');
      fontNodes.forEach((node) => {
        const face = node.getAttribute('face');
        if (face && face.toLowerCase() !== String(fontFamily).toLowerCase()) {
          convertFontElementToSpan(node);
        } else {
          convertFontElementToSpan(node, { fontFamily });
        }
      });
    }, { refreshState: false });
    if (isTextItem) {
      updateSelectedItem({
        style: { ...selectedItem.style, fontFamily },
      });
    }
  }

  function applyFontSize(size) {
    const context = activeRichTextContextRef.current;
    const isTextItem = selectedItem && selectedItem.type === 'text';
    const isTableCell = context && context.type === 'table';
    if (!isTextItem && !isTableCell) return;
    const numeric = Number(size);
    if (!Number.isFinite(numeric) || numeric <= 0) return;
    commitRichTextEditorMutation((editor, selection) => {
      if (!selection || selection.isCollapsed) return;
      execCommandSafe('styleWithCSS', true);
      execCommandSafe('fontSize', 7);
      execCommandSafe('styleWithCSS', false);
      const fontNodes = editor.querySelectorAll('font[size]');
      fontNodes.forEach((node) => {
        if (node.getAttribute('size') === '7') {
          convertFontElementToSpan(node, { fontSize: `${numeric}px` });
        }
      });
    }, { refreshState: false });
    if (isTextItem) {
      updateSelectedItem({
        style: { ...selectedItem.style, fontSize: numeric },
      });
    }
  }

  function toggleInlineFormat(command, styleKey) {
    const context = activeRichTextContextRef.current;
    const isTextItem = selectedItem && selectedItem.type === 'text';
    const isTableCell = context && context.type === 'table';
    if (!isTextItem && !isTableCell) return;
    commitRichTextEditorMutation((_, selection) => {
      const collapsed = !selection || selection.isCollapsed;
      execCommandSafe(command);
      if (!isTextItem || !styleKey || !collapsed) return;
      try {
        const state = document.queryCommandState(command);
        updateSelectedItem({
          style: { ...selectedItem.style, [styleKey]: !!state },
        });
      } catch (err) {
        updateSelectedItem({
          style: { ...selectedItem.style, [styleKey]: !selectedItem.style?.[styleKey] },
        });
      }
    });
  }

  function applyTextColor(color) {
    const context = activeRichTextContextRef.current;
    const isTextItem = selectedItem && selectedItem.type === 'text';
    const isTableCell = context && context.type === 'table';
    if (!isTextItem && !isTableCell) return;
    const fallbackColor = isTextItem ? selectedItem.style.color : undefined;
    const nextColor = typeof color === 'string' ? color : fallbackColor;
    if (!nextColor) return;
    commitRichTextEditorMutation((_, selection) => {
      execCommandSafe('foreColor', nextColor);
      if (isTextItem && (!selection || selection.isCollapsed)) {
        updateSelectedItem({
          style: { ...selectedItem.style, color: nextColor },
        });
      }
    });
  }

  function applyTextAlign(align) {
    const context = activeRichTextContextRef.current;
    const isTextItem = selectedItem && selectedItem.type === 'text';
    const isTableCell = context && context.type === 'table';
    if (!isTextItem && !isTableCell) return;
    const commandMap = {
      left: 'justifyLeft',
      center: 'justifyCenter',
      right: 'justifyRight',
      justify: 'justifyFull',
    };
    const command = commandMap[align] || 'justifyLeft';
    commitRichTextEditorMutation(() => {
      execCommandSafe(command, null);
    });
    if (isTextItem) {
      updateSelectedItem({
        style: { ...selectedItem.style, align },
      });
    }
  }

  const handleItemMouseDown = useCallback(
    (event, id) => {
      event.stopPropagation();
      if (event.button !== 0) return;
      const additive = event.shiftKey || event.metaKey || event.ctrlKey;
      setSelectedItemIds((prev) => {
        if (additive) {
          const exists = prev.includes(id);
          if (exists) {
            const next = prev.filter((pid) => pid !== id);
            return next;
          }
          const without = prev.filter((pid) => pid !== id);
          return [id, ...without];
        }
        if (prev.length === 1 && prev[0] === id) return prev;
        return [id];
      });
    },
    [setSelectedItemIds]
  );

  const handleItemRotationPointerMove = useCallback(
    (event) => {
      const state = rotationStateRef.current;
      if (!state || !state.activeId || !canvasRef.current) return;
      event.preventDefault();
      const rect = canvasRef.current.getBoundingClientRect();
      const pointerX = (event.clientX - rect.left) / scale;
      const pointerY = (event.clientY - rect.top) / scale;
      const angle = Math.atan2(pointerY - state.centerY, pointerX - state.centerX);
      const delta = angle - state.startAngle;
      let nextRotation = state.startRotation + (delta * 180) / Math.PI;
      if (event.shiftKey) {
        nextRotation = Math.round(nextRotation / 15) * 15;
      }
      const normalized = normalizeRotation(nextRotation);
      if (typeof state.lastRotation === 'number' && Math.abs(normalized - state.lastRotation) < 0.2) {
        return;
      }
      state.lastRotation = normalized;
      updateItems((items) =>
        items.map((it) => (it.id === state.activeId ? { ...it, rotation: normalized } : it))
      );
    },
    [scale, updateItems]
  );

  const handleItemRotationPointerUp = useCallback(() => {
    window.removeEventListener('pointermove', handleItemRotationPointerMove);
    window.removeEventListener('pointerup', handleItemRotationPointerUp);
    rotationStateRef.current = null;
    setRotatingItemId(null);
  }, [handleItemRotationPointerMove, setRotatingItemId]);

  const handleItemRotationPointerDown = useCallback(
    (event, item) => {
      event.stopPropagation();
      event.preventDefault();
      if (!canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const pointerX = (event.clientX - rect.left) / scale;
      const pointerY = (event.clientY - rect.top) / scale;
      const centerX = item.x + item.w / 2;
      const centerY = item.y + item.h / 2;
      const startAngle = Math.atan2(pointerY - centerY, pointerX - centerX);
      const startRotation = normalizeRotation(item.rotation);
      rotationStateRef.current = {
        activeId: item.id,
        centerX,
        centerY,
        startAngle,
        startRotation,
        lastRotation: startRotation,
      };
      setSelectedItemId(item.id);
      setRotatingItemId(item.id);
      window.addEventListener('pointermove', handleItemRotationPointerMove);
      window.addEventListener('pointerup', handleItemRotationPointerUp);
    },
    [handleItemRotationPointerMove, handleItemRotationPointerUp, scale, setRotatingItemId, setSelectedItemId]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handleItemRotationPointerMove);
      window.removeEventListener('pointerup', handleItemRotationPointerUp);
    };
  }, [handleItemRotationPointerMove, handleItemRotationPointerUp]);
  useEffect(() => {
    if (!rotatingItemId) return;
    const exists = activeSlide.items.some((item) => item.id === rotatingItemId);
    if (!exists) {
      handleItemRotationPointerUp();
    }
  }, [activeSlide.items, handleItemRotationPointerUp, rotatingItemId]);

  const handleMarqueePointerMove = useCallback(
    (event) => {
      const state = marqueeStateRef.current;
      if (!state || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const currentX = (event.clientX - rect.left) / scale;
      const currentY = (event.clientY - rect.top) / scale;
      const x = Math.min(state.startX, currentX);
      const y = Math.min(state.startY, currentY);
      const w = Math.abs(currentX - state.startX);
      const h = Math.abs(currentY - state.startY);
      state.hasMoved = state.hasMoved || w > 2 || h > 2;
      state.lastRect = { x, y, w, h };
      setMarqueeRect({ x, y, w, h });
    },
    [scale]
  );

  const handleMarqueePointerUp = useCallback(
    () => {
      window.removeEventListener('pointermove', handleMarqueePointerMove);
      const state = marqueeStateRef.current;
      marqueeStateRef.current = null;
      setMarqueeRect(null);
      if (!state) return;
      if (!state.hasMoved) {
        if (!state.additive) {
          setSelectedItemIds([]);
        }
        return;
      }
      const rect = state.lastRect;
      if (!rect) return;
      const hits = activeSlide.items
        .filter((item) => {
          const left = item.x;
          const top = item.y;
          const right = item.x + item.w;
          const bottom = item.y + item.h;
          return right >= rect.x && left <= rect.x + rect.w && bottom >= rect.y && top <= rect.y + rect.h;
        })
        .map((item) => item.id);
      if (!hits.length) {
        if (!state.additive) {
          setSelectedItemIds([]);
        }
        return;
      }
      setSelectedItemIds((prev) => {
        if (state.additive) {
          const set = new Set(prev);
          hits.forEach((id) => {
            if (set.has(id)) {
              set.delete(id);
            } else {
              set.add(id);
            }
          });
          return Array.from(set);
        }
        return hits;
      });
    },
    [activeSlide.items, handleMarqueePointerMove, setSelectedItemIds]
  );

  const handleCanvasPointerDown = useCallback(
    (event) => {
      if (event.button !== 0) return;
      if (!canvasRef.current) return;
      if (event.target && canvasRef.current !== event.target) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const startX = (event.clientX - rect.left) / scale;
      const startY = (event.clientY - rect.top) / scale;
      marqueeStateRef.current = {
        startX,
        startY,
        additive: event.shiftKey || event.metaKey || event.ctrlKey,
        hasMoved: false,
        lastRect: null,
      };
      setMarqueeRect({ x: startX, y: startY, w: 0, h: 0 });
      event.preventDefault();
      window.addEventListener('pointermove', handleMarqueePointerMove);
      window.addEventListener('pointerup', handleMarqueePointerUp, { once: true });
    },
    [handleMarqueePointerMove, handleMarqueePointerUp, scale]
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', handleMarqueePointerMove);
      window.removeEventListener('pointerup', handleMarqueePointerUp);
    },
    [handleMarqueePointerMove, handleMarqueePointerUp]
  );

  const handleItemDragStart = useCallback(
    (item, data) => {
      const selection = selectedItemIds.includes(item.id) ? selectedItemIds : [item.id];
      if (!selectedItemIds.includes(item.id)) {
        setSelectedItemIds(selection);
      }
      const initial = new Map();
      selection.forEach((sid) => {
        const target = activeSlide.items.find((it) => it.id === sid);
        if (target) {
          initial.set(sid, { x: target.x, y: target.y, w: target.w, h: target.h });
        }
      });
      dragStateRef.current = {
        ids: selection,
        startX: data.x,
        startY: data.y,
        initial,
      };
    },
    [activeSlide.items, selectedItemIds, setSelectedItemIds]
  );

  const handleItemDrag = useCallback(
    (item, data) => {
      const state = dragStateRef.current;
      if (!state || !state.ids || state.ids.length <= 1) return;
      const dx = data.x - state.startX;
      const dy = data.y - state.startY;
      if (!dx && !dy) return;
      const idSet = new Set(state.ids);
      updateItems((items) =>
        items.map((p) => {
          if (!idSet.has(p.id)) return p;
          const base = state.initial.get(p.id) || { x: p.x, y: p.y, w: p.w, h: p.h };
          const nextX = clamp(base.x + dx, 0, CANVAS_W - base.w);
          const nextY = clamp(base.y + dy, 0, CANVAS_H - base.h);
          if (p.x === nextX && p.y === nextY) return p;
          return { ...p, x: nextX, y: nextY };
        })
      );
    },
    [updateItems]
  );

  const handleItemDragStop = useCallback(
    (item, data) => {
      const state = dragStateRef.current;
      dragStateRef.current = null;
      const selection =
        state && Array.isArray(state.ids) && state.ids.length
          ? state.ids
          : selectedItemIds.includes(item.id)
          ? selectedItemIds
          : [item.id];
      if (!state || selection.length === 1) {
        updateItems((items) =>
          items.map((p) => (p.id === item.id ? { ...p, x: data.x, y: data.y } : p))
        );
        return;
      }
      const dx = data.x - state.startX;
      const dy = data.y - state.startY;
      if (!dx && !dy) return;
      const idSet = new Set(selection);
      updateItems((items) =>
        items.map((p) => {
          if (!idSet.has(p.id)) return p;
          const base = state.initial.get(p.id) || { x: p.x, y: p.y, w: p.w, h: p.h };
          const nextX = clamp(base.x + dx, 0, CANVAS_W - base.w);
          const nextY = clamp(base.y + dy, 0, CANVAS_H - base.h);
          return { ...p, x: nextX, y: nextY };
        })
      );
    },
    [selectedItemIds, updateItems]
  );

  const setActiveSlideLayout = (layoutKey, { reapply = false } = {}) => {
    const nextLayout = SLIDE_LAYOUT_MAP[layoutKey] ? layoutKey : DEFAULT_LAYOUT;
    let firstId = null;
    mutateSlides((prev) =>
      prev.map((slide, idx) => {
        if (idx !== activeIdx) return slide;
        const hasItems = Array.isArray(slide.items) && slide.items.length > 0;
        const shouldReset = reapply || !hasItems;
        const nextItems = shouldReset ? createLayoutItems(nextLayout) : slide.items || [];
        if (!firstId && nextItems.length) {
          firstId = nextItems[0].id;
        }
        return {
          ...slide,
          layout: nextLayout,
          items: nextItems,
        };
      })
    );
    if (firstId) {
      setSelectedItemId(firstId);
    } else if (reapply) {
      setSelectedItemId(null);
    }
  };

  const addGraph = (header) => {
    const targetHeader = header || selectedGraphHeader;
    if (!targetHeader) return;
    const gIdx = allGraphs.findIndex((g) => g.header === targetHeader);
    if (gIdx === -1) return;
    updateItems((items) => [
      ...items,
      {
        id: uuid(),
        type: 'graph',
        graphIndex: gIdx,
        overrides: {
          ...(globalPaletteColors ? { colors: globalPaletteColors, paletteKey: globalPaletteKey } : {}),
        },
        x: 20,
        y: 20,
        w: 320,
        h: 240,
      },
    ]);
  };

  const addImageItem = useCallback(
    (payload, options = {}) => {
      if (!payload || typeof payload.dataUrl !== 'string' || !payload.dataUrl) return;
      const replaceId = options.replaceId || null;
      const parseDimension = (value) => {
        const num = Number(value);
        return Number.isFinite(num) && num > 0 ? num : null;
      };
      const sourceWidth = parseDimension(payload.width);
      const sourceHeight = parseDimension(payload.height);
      const aspect = sourceWidth && sourceHeight ? sourceWidth / sourceHeight : null;
      const maxW = CANVAS_W - GRID_SIZE * 4;
      const maxH = CANVAS_H - GRID_SIZE * 4;
      let width = aspect && sourceWidth
        ? Math.min(maxW, Math.max(GRID_SIZE * 6, Math.round(sourceWidth * 0.75)))
        : Math.min(maxW, 480);
      if (!Number.isFinite(width) || width <= 0) {
        width = Math.min(maxW, 480);
      }
      let height = aspect ? width / aspect : width * 0.6;
      if (height > maxH) {
        height = maxH;
        width = aspect ? height * aspect : Math.min(width, maxW);
      }
      width = Math.max(GRID_SIZE * 6, Math.round(width / GRID_SIZE) * GRID_SIZE);
      height = Math.max(GRID_SIZE * 4, Math.round(height / GRID_SIZE) * GRID_SIZE);
      const centerX = Math.round(((CANVAS_W - width) / 2) / GRID_SIZE) * GRID_SIZE;
      const centerY = Math.round(((CANVAS_H - height) / 2) / GRID_SIZE) * GRID_SIZE;
      const opacity = typeof payload.opacity === 'number' ? clamp(payload.opacity, 0, 1) : 1;
      const meta = payload.meta && typeof payload.meta === 'object' ? payload.meta : null;

      if (replaceId) {
        updateItems((items) =>
          items.map((it) =>
            it.id === replaceId && it.type === 'image'
              ? {
                  ...it,
                  src: payload.dataUrl,
                  opacity,
                  ...(meta ? { meta } : it.meta ? { meta: it.meta } : {}),
                }
              : it
          )
        );
        setSelectedItemId(replaceId);
        return;
      }

      const id = uuid();
      updateItems((items) => [
        ...items,
        {
          id,
          type: 'image',
          src: payload.dataUrl,
          opacity,
          ...(meta ? { meta } : {}),
          x: clamp(centerX, 0, CANVAS_W - width),
          y: clamp(centerY, 0, CANVAS_H - height),
          w: clamp(width, GRID_SIZE, CANVAS_W),
          h: clamp(height, GRID_SIZE, CANVAS_H),
        },
      ]);
      setSelectedItemId(id);
    },
    [updateItems, setSelectedItemId]
  );

  const addIconItem = useCallback(
    (iconName, options = {}) => {
      if (!iconName || !LUCIDE_ICON_SET.has(iconName)) return;
      const color =
        typeof options.color === 'string' && options.color ? options.color : DEFAULT_ICON_COLOR;
      const strokeBase =
        typeof options.strokeWidth === 'number' ? options.strokeWidth : DEFAULT_ICON_STROKE;
      const strokeWidth = clamp(strokeBase, ICON_STROKE_MIN, ICON_STROKE_MAX);
      const replaceId =
        typeof options.replaceId === 'string' && options.replaceId ? options.replaceId : null;

      if (replaceId) {
        updateItems((items) =>
          items.map((it) =>
            it.id === replaceId
              ? { ...it, type: 'icon', icon: iconName, color, strokeWidth }
              : it
          )
        );
        setSelectedItemId(replaceId);
        return;
      }

      const snapSize = (value) =>
        Math.max(
          GRID_SIZE * 2,
          Math.round((Number.isFinite(value) ? value : DEFAULT_ICON_SIZE) / GRID_SIZE) *
            GRID_SIZE
        );
      const requestedSize = Number.isFinite(options.size)
        ? options.size
        : Number.isFinite(options.width) && Number.isFinite(options.height)
        ? Math.min(options.width, options.height)
        : Number.isFinite(options.width)
        ? options.width
        : Number.isFinite(options.height)
        ? options.height
        : DEFAULT_ICON_SIZE;
      const size = clamp(
        snapSize(requestedSize),
        GRID_SIZE * 2,
        Math.min(CANVAS_W, CANVAS_H)
      );
      const width = size;
      const height = size;
      const rawX = Number.isFinite(options.x) ? options.x : 80;
      const rawY = Number.isFinite(options.y) ? options.y : 80;
      const maxX = Math.max(0, CANVAS_W - width);
      const maxY = Math.max(0, CANVAS_H - height);
      const x = clamp(Math.round(rawX / GRID_SIZE) * GRID_SIZE, 0, maxX);
      const y = clamp(Math.round(rawY / GRID_SIZE) * GRID_SIZE, 0, maxY);
      const id = uuid();
      updateItems((items) => [
        ...items,
        {
          id,
          type: 'icon',
          icon: iconName,
          color,
          strokeWidth,
          x,
          y,
          w: width,
          h: height,
        },
      ]);
      setSelectedItemId(id);
    },
    [setSelectedItemId, updateItems]
  );

  const addShape = (kind = 'rectangle') => {
    const option = SHAPE_MAP[kind] || SHAPE_MAP.rectangle;
    const defaults = createShapeDefaults(kind);
    const snapSize = (size, fallback) => {
      const base = Number.isFinite(size) && size > 0 ? size : fallback;
      return Math.max(GRID_SIZE * 2, Math.round(base / GRID_SIZE) * GRID_SIZE);
    };
    const width = snapSize(option?.initialSize?.w ?? 240, 240);
    const height = snapSize(option?.initialSize?.h ?? 160, 160);
    const id = uuid();
    updateItems((items) => [
      ...items,
      {
        id,
        type: 'shape',
        shape: defaults,
        rotation: 0,
        x: 80,
        y: 80,
        w: width,
        h: height,
      },
    ]);
    setSelectedItemId(id);
  };

  const addText = () => {
    updateItems((items) => [
      ...items,
      {
        id: uuid(),
        type: 'text',
        text: 'Yeni metin…',
        style: {
          fontSize: 16,
          color: '#000000',
          align: 'left',
          fontFamily: FONTS[0],
          bold: false,
          italic: false,
          underline: false,
        },
        x: 40,
        y: 40,
        w: 240,
        h: 80,
      },
    ]);
  };

  const addTable = () => {
    updateItems((items) => [
      ...items,
      {
        id: uuid(),
        type: 'table',
        table: createDefaultTable(),
        x: 60,
        y: 60,
        w: 360,
        h: 220,
      },
    ]);
  };

  const addSavedTable = (tableAsset, options = {}) => {
    if (!tableAsset) return;
    const tableData = ensureTableData(tableAsset.table || tableAsset);
    const wantsLink =
      Object.prototype.hasOwnProperty.call(options, 'linked') ? options.linked : true;
    const tableLink =
      wantsLink && tableAsset.tableLink ? sanitizeTableLink(tableAsset.tableLink) : null;
    updateItems((items) => [
      ...items,
      {
        id: uuid(),
        type: 'table',
        table: tableData,
        x: 60,
        y: 60,
        w: 420,
        h: 260,
        ...(tableLink ? { tableLink } : {}),
      },
    ]);
  };

  const handleLinkedTablePick = useCallback(
    (tableAsset) => {
      if (!tableAsset) return;
      addSavedTable(tableAsset, { linked: true });
      setLinkedTableDlg(false);
    },
    [addSavedTable]
  );

  const handleLinkedTableDialogClose = useCallback(() => {
    setLinkedTableDlg(false);
  }, []);

  const openImageDialog = useCallback((targetId = null) => {
    setImageDialogState({ open: true, targetId });
  }, []);

  const openIconDialog = useCallback((targetId = null) => {
    setIconDialogState({ open: true, targetId });
  }, []);

  const handleIconDialogClose = useCallback(() => {
    setIconDialogState({ open: false, targetId: null });
  }, []);

  const handleImageDialogClose = useCallback(
    (payload) => {
      const targetId = imageDialogState.targetId;
      setImageDialogState({ open: false, targetId: null });
      if (payload) {
        addImageItem(payload, { replaceId: targetId });
      }
    },
    [addImageItem, imageDialogState.targetId]
  );

  const handleIconPick = useCallback(
    (iconName) => {
      if (!iconName) return;
      const targetId =
        typeof iconDialogState.targetId === 'string' && iconDialogState.targetId
          ? iconDialogState.targetId
          : null;
      if (targetId) {
        addIconItem(iconName, { replaceId: targetId });
      } else {
        addIconItem(iconName);
      }
      setIconDialogState({ open: false, targetId: null });
    },
    [addIconItem, iconDialogState.targetId]
  );

  // Bulk add selected graphs to slides (dialog "Ekle" button)
  const performBulkAddToSlides = () => {
    if (!bulkSelectedHeaders.length) return;
    const selectedSet = new Set(bulkSelectedHeaders);
    const chosen = allGraphs
      .map((graph, index) => ({ graph, index }))
      .filter(({ graph }) => selectedSet.has(graph.header));
    if (!chosen.length) return;

    const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
    const mt = clamp(Math.round(+bulkMarginTop || 0), 0, CANVAS_H - GRID_SIZE * 2);
    const mr = clamp(Math.round(+bulkMarginRight || 0), 0, CANVAS_W - GRID_SIZE * 2);
    const mb = clamp(Math.round(+bulkMarginBottom || 0), 0, CANVAS_H - GRID_SIZE * 2);
    const ml = clamp(Math.round(+bulkMarginLeft || 0), 0, CANVAS_W - GRID_SIZE * 2);
    const innerW = clamp(CANVAS_W - ml - mr, GRID_SIZE * 10, CANVAS_W - GRID_SIZE * 2);
    const innerH = clamp(CANVAS_H - mt - mb, GRID_SIZE * 8, CANVAS_H - GRID_SIZE * 2);
    const availableW = Math.round(innerW / GRID_SIZE) * GRID_SIZE;
    const availableH = Math.round(innerH / GRID_SIZE) * GRID_SIZE;
    const baseX = Math.round(ml / GRID_SIZE) * GRID_SIZE;
    const baseY = Math.round(mt / GRID_SIZE) * GRID_SIZE;

    const pal = COLOR_PALETTES[bulkPaletteKey] || null;
    const overridesBase = {
      graphType: bulkType,
      metric: bulkMetric,
      ...( ORIENTABLE_TYPES.includes(bulkType)
          ? { orientation: String(bulkOrientation || 'vertical').toLowerCase() }
          : {} ),
      ...(pal ? { colors: pal, paletteKey: bulkPaletteKey } : {}),
      settings: {
        ...DEFAULT_SETTINGS,
        legend: !!bulkLegend,
        baseType: bulkBaseType,
        dataLabels: !!bulkDataLabels,
        dataLabelPosition: bulkLabelPos,
        gridLines: !!bulkGrid,
        autoScale: !!bulkAutoScale,
      },
    };

    const shouldShareSlide = bulkPlacementMode === 'single' && slides.length;
    if (!shouldShareSlide) {
      const startIdx = slides.length;
      const newSlides = chosen.map(({ graph, index }) => ({
        id: uuid(),
        name: graph.header || 'Grafik',
        layout: 'full',
        items: [
          {
            id: uuid(),
            type: 'graph',
            graphIndex: index,
            overrides: { ...overridesBase },
            x: baseX,
            y: baseY,
            w: availableW,
            h: availableH,
          },
        ],
      }));

      mutateSlides((prev) => [...prev, ...newSlides]);
      setActiveIdx(startIdx);
      setBulkDlg(false);
      return;
    }

    const targetId =
      bulkTargetSlideId ||
      (slides[activeIdx] && slides[activeIdx].id) ||
      (slides[0] && slides[0].id) ||
      '';
    const targetIdx = slides.findIndex((s) => s.id === targetId);
    if (targetIdx === -1) {
      const startIdx = slides.length;
      const newSlides = chosen.map(({ graph, index }) => ({
        id: uuid(),
        name: graph.header || 'Grafik',
        layout: 'full',
        items: [
          {
            id: uuid(),
            type: 'graph',
            graphIndex: index,
            overrides: { ...overridesBase },
            x: baseX,
            y: baseY,
            w: availableW,
            h: availableH,
          },
        ],
      }));
      mutateSlides((prev) => [...prev, ...newSlides]);
      setActiveIdx(startIdx);
      setBulkDlg(false);
      return;
    }

    const gap = GRID_SIZE * 2;
    const maxColumns = 3;
    const columnGuess = Math.ceil(Math.sqrt(chosen.length));
    let colCount = Math.min(maxColumns, Math.max(1, columnGuess));
    const minW = GRID_SIZE * 10;
    const minH = GRID_SIZE * 8;
    while (colCount > 1) {
      const requiredWidth = colCount * minW + (colCount - 1) * gap;
      if (requiredWidth <= availableW) break;
      colCount -= 1;
    }
    const rowCount = Math.max(1, Math.ceil(chosen.length / colCount));
    const totalGapW = (colCount - 1) * gap;
    const totalGapH = (rowCount - 1) * gap;
    const cellW = Math.max(
      minW,
      Math.floor((availableW - totalGapW) / colCount / GRID_SIZE) * GRID_SIZE
    );
    const cellH = Math.max(
      minH,
      Math.floor((availableH - totalGapH) / rowCount / GRID_SIZE) * GRID_SIZE
    );
    const safeCellW = Math.min(cellW, CANVAS_W - GRID_SIZE * 2);
    const safeCellH = Math.min(cellH, CANVAS_H - GRID_SIZE * 2);
    const maxX = CANVAS_W - safeCellW;
    const maxY = CANVAS_H - safeCellH;
    const startX = Math.max(0, Math.min(baseX, maxX));
    const startY = Math.max(0, Math.min(baseY, maxY));

    const newItems = chosen.map(({ index }, order) => {
      const row = Math.floor(order / colCount);
      const col = order % colCount;
      const itemX = Math.min(startX + col * (safeCellW + gap), CANVAS_W - safeCellW);
      const itemY = Math.min(startY + row * (safeCellH + gap), CANVAS_H - safeCellH);
      return {
        id: uuid(),
        type: 'graph',
        graphIndex: index,
        overrides: { ...overridesBase },
        x: itemX,
        y: itemY,
        w: safeCellW,
        h: safeCellH,
      };
    });

    mutateSlides((prev) =>
      prev.map((slide, idx) =>
        idx === targetIdx ? { ...slide, items: [...slide.items, ...newItems] } : slide
      )
    );
    setActiveIdx(targetIdx);
    setBulkDlg(false);
  };

  const addAllGraphsToSlides = () => {
    if (!allGraphs.length) return;

    // Target size and centered position (snapped to grid)
    const W = Math.min(720, CANVAS_W - GRID_SIZE * 4);
    const H = Math.min(420, CANVAS_H - GRID_SIZE * 4);
    const x = Math.round(((CANVAS_W - W) / 2) / GRID_SIZE) * GRID_SIZE;
    const y = Math.round(((CANVAS_H - H) / 2) / GRID_SIZE) * GRID_SIZE;

  const startIdx = slides.length;

  const newSlides = allGraphs.map((g, idx) => ({
    id: uuid(),
    name: g.header || `Grafik ${idx + 1}`,
    layout: 'full',
    items: [
      {
        id: uuid(),
        type: 'graph',
        graphIndex: idx, // index in allGraphs
          overrides: {
            ...(globalPaletteColors ? { colors: globalPaletteColors, paletteKey: globalPaletteKey } : {}),
          },
          x,
          y,
          w: W,
          h: H,
        },
      ],
    }));
    mutateSlides((prev) => [...prev, ...newSlides]);
    setActiveIdx(startIdx);
  };

  const removeItem = (id) => {
    updateItems((items) => items.filter((it) => it.id !== id));
    setSelectedItemIds((prev) => prev.filter((selId) => selId !== id));
  };

  const duplicateItem = useCallback(
    (id) => {
      let newItemId = null;
      updateItems((items) => {
        const idx = items.findIndex((it) => it.id === id);
        if (idx === -1) return items;
        const original = items[idx];
        const offset = GRID_SIZE * 2;
        const nextX = clamp(original.x + offset, 0, Math.max(0, CANVAS_W - original.w));
        const nextY = clamp(original.y + offset, 0, Math.max(0, CANVAS_H - original.h));
        const deepClone = (obj) => {
          if (obj == null) return obj;
          try {
            return JSON.parse(JSON.stringify(obj));
          } catch {
            return Array.isArray(obj) ? [...obj] : { ...obj };
          }
        };
        const copy = {
          ...original,
          id: uuid(),
          x: nextX,
          y: nextY,
        };
        if (original.type === 'text') {
          copy.style = { ...original.style };
        }
        if (original.type === 'graph') {
          copy.overrides = deepClone(original.overrides);
        }
        if (original.type === 'table') {
          copy.table = deepClone(original.table);
        }
        if (original.type === 'shape') {
          copy.shape = { ...original.shape };
        }
        if (original.type === 'image') {
          copy.src = original.src;
          copy.opacity = original.opacity;
          copy.meta = original.meta ? { ...original.meta } : undefined;
        }
        if (original.type === 'icon') {
          copy.icon = original.icon;
          copy.color = original.color;
          copy.strokeWidth = original.strokeWidth;
        }
        newItemId = copy.id;
        const next = [...items];
        next.splice(idx + 1, 0, copy);
        return next;
      });
      if (newItemId) {
        setSelectedItemId(newItemId);
      }
    },
    [setSelectedItemId, updateItems]
  );

  const bringItemToFront = useCallback(
    (id) => {
      updateItems((items) => {
        const idx = items.findIndex((it) => it.id === id);
        if (idx === -1 || idx === items.length - 1) return items;
        const next = [...items];
        const [item] = next.splice(idx, 1);
        next.push(item);
        return next;
      });
      setSelectedItemId(id);
    },
    [setSelectedItemId, updateItems]
  );

  const sendItemToBack = useCallback(
    (id) => {
      updateItems((items) => {
        const idx = items.findIndex((it) => it.id === id);
        if (idx <= 0) return items;
        const next = [...items];
        const [item] = next.splice(idx, 1);
        next.unshift(item);
        return next;
      });
      setSelectedItemId(id);
    },
    [setSelectedItemId, updateItems]
  );

  const patchGraphOverrides = (patch) => {
    if (!selectedItem || selectedItem.type !== 'graph') return;
    updateItems((items) =>
      items.map((it) =>
        it.id === selectedItem.id ? { ...it, overrides: { ...it.overrides, ...patch } } : it
      )
    );
  };

const BASE_GRAPH_GROUP_ID = '__basegraphs__';

const countGraphsInPayload = (payload) => {
  if (!payload || typeof payload !== 'object') return 0;
  const baseCount = Array.isArray(payload.basegraphs) ? payload.basegraphs.length : 0;
  const groupedCount = Array.isArray(payload.groups)
    ? payload.groups.reduce(
        (sum, grp) => sum + (Array.isArray(grp?.graphs) ? grp.graphs.length : 0),
        0,
      )
    : 0;
  if (baseCount + groupedCount > 0) return baseCount + groupedCount;
  return Array.isArray(payload.graphs) ? payload.graphs.length : 0;
};

const extractGraphsFromPayload = (payload) => {
  const result = [];
  if (!payload || typeof payload !== 'object') return result;

  const pushGraph = (graph, meta = {}) => {
    if (!graph) return;
    result.push({ ...graph, ...meta });
  };

  const toArray = (val) => (Array.isArray(val) ? val : []);

  const baseGraphs = payload.basegraphs && payload.basegraphs.length
    ? toArray(payload.basegraphs)
    : toArray(payload.graphs);

  baseGraphs.forEach((graph, idx) =>
    pushGraph(graph, {
      __groupId: BASE_GRAPH_GROUP_ID,
      __groupName: 'Temel Grafikler',
      __groupIndex: idx,
      __groupFilters: [],
      __groupWeights: [],
      __isBaseGraph: true,
    }),
  );

  toArray(payload.groups).forEach((grp, grpIdx) => {
    if (!grp) return;
    const idRaw = grp.id != null ? String(grp.id).trim() : '';
    const nameRaw = grp.name != null ? String(grp.name).trim() : '';
    const id = idRaw || `grp_${grpIdx + 1}`;
    const name = nameRaw || `Grup ${grpIdx + 1}`;
    const filters = Array.isArray(grp.filtersApplied) ? grp.filtersApplied : [];
    const weights = Array.isArray(grp.weightsApplied) ? grp.weightsApplied : [];
    toArray(grp.graphs).forEach((graph, idx) =>
      pushGraph(graph, {
        __groupId: id,
        __groupName: name,
        __groupIndex: idx,
        __groupFilters: filters,
        __groupWeights: weights,
        __isBaseGraph: false,
      }),
    );
  });

  return result;
};

async function loadLatestFullGraphDataset(organisation, projectID, analysisID) {
  try {
    if (!organisation || !projectID) return null;
    const basePath = analysisID
      ? ['organisations', organisation, 'analyses', analysisID]
      : ['organisations', organisation, 'projects', projectID];
    const collRef = collection(db, ...basePath, 'graphDatasets');
    let qs = await getDocs(query(collRef, orderBy('createdAt', 'desc'), limit(1)));
    if (qs.empty) {
      qs = await getDocs(query(collRef, orderBy('createDate', 'desc'), limit(1)));
    }
    if (qs.empty) return null;
    const docSnap = qs.docs[0];
    const data = docSnap.data() || null;
    if (!data || countGraphsInPayload(data) === 0) return null;
    return {
      ...data,
      __docId: docSnap.id,
      __docPath: docSnap.ref.path,
      __organisationId: organisation,
      __analysisId: analysisID,
      __projectId: projectID,
    };
  } catch (err) {
    console.warn('loadLatestFullGraphDataset failed:', err);
    return null;
  }
}

const prepareGraphForPresentation = (graph, ctx = {}) => {
  if (!graph) return null;
  const enriched = { ...graph };
  const normalizeStr = (val) => {
    if (val == null) return '';
    const str = String(val).trim();
    return str;
  };

  const groupId = ctx.groupId ?? graph.__groupId ?? graph.groupId ?? graph.group_id;
  const groupName = ctx.groupName ?? graph.__groupName ?? graph.groupName ?? graph.group_name;

  if (!enriched.__groupId && groupId) {
    enriched.__groupId = normalizeStr(groupId);
  }
  if (!enriched.__groupName && groupName) {
    enriched.__groupName = normalizeStr(groupName);
  }

  if (!enriched.__analysisId && ctx.analysisId) {
    enriched.__analysisId = normalizeStr(ctx.analysisId);
  }
  if (!enriched.__analysisName && ctx.analysisName) {
    enriched.__analysisName = normalizeStr(ctx.analysisName);
  }
  if (!enriched.__projectId && ctx.projectId) {
    enriched.__projectId = normalizeStr(ctx.projectId);
  }
  if (!enriched.__projectCode && ctx.projectCode) {
    enriched.__projectCode = normalizeStr(ctx.projectCode);
  }
  if (!enriched.assetType) {
    enriched.assetType = 'graph';
  }

  const graphLinkSource =
    graph.graphLink ||
    ctx.graphLink ||
    (ctx.graphDatasetDocPath
      ? {
          kind: GRAPH_LINK_KIND,
          docPath: ctx.graphDatasetDocPath,
          organisationId: ctx.organisationId,
          analysisId: ctx.analysisId,
          projectId: ctx.projectId,
          groupId: ctx.groupId ?? graph.__groupId,
          graphId:
            graph.graphId ||
            graph.graphID ||
            graph.id ||
            graph.slug ||
            graph.header ||
            graph.title,
          graphIndex: ctx.graphIndex,
        }
      : null);
  const graphLink = sanitizeGraphLink(graphLinkSource, {
    organisationFallback: ctx.organisationId,
  });
  if (graphLink) {
    enriched.graphLink = graphLink;
  }

  return enriched;
};

  const updateSelectedItem = (patch) => {
    if (!selectedItem) return;
    updateItems((items) =>
      items.map((it) => (it.id === selectedItem.id ? { ...it, ...patch } : it))
    );
  };

  const resizeTableItem = (itemId, factors = {}) => {
    const { widthFactor = 1, heightFactor = 1 } = factors || {};
    const adjustWidth = Number.isFinite(widthFactor) && widthFactor > 0 && Math.abs(widthFactor - 1) > 0.01;
    const adjustHeight = Number.isFinite(heightFactor) && heightFactor > 0 && Math.abs(heightFactor - 1) > 0.01;
    if (!adjustWidth && !adjustHeight) return;
    const snapToGrid = (value) => Math.max(GRID_SIZE, Math.round(value / GRID_SIZE) * GRID_SIZE);

    updateItems((items) =>
      items.map((it) => {
        if (it.id !== itemId || it.type !== 'table') return it;
        let nextW = it.w;
        let nextH = it.h;
        let nextX = it.x;
        let nextY = it.y;

        if (adjustWidth) {
          let targetWidth = snapToGrid(it.w * widthFactor);
          targetWidth = clamp(targetWidth, MIN_TABLE_ITEM_WIDTH, CANVAS_W);
          if (targetWidth > CANVAS_W) targetWidth = CANVAS_W;
          if (nextX + targetWidth > CANVAS_W) {
            nextX = Math.max(0, CANVAS_W - targetWidth);
          }
          nextW = targetWidth;
        }

        if (adjustHeight) {
          let targetHeight = snapToGrid(it.h * heightFactor);
          targetHeight = clamp(targetHeight, MIN_TABLE_ITEM_HEIGHT, CANVAS_H);
          if (targetHeight > CANVAS_H) targetHeight = CANVAS_H;
          if (nextY + targetHeight > CANVAS_H) {
            nextY = Math.max(0, CANVAS_H - targetHeight);
          }
          nextH = targetHeight;
        }

        return { ...it, w: nextW, h: nextH, x: nextX, y: nextY };
      })
    );
  };

  const updateShapeItem = (itemId, updater) => {
    updateItems((items) =>
      items.map((it) => {
        if (it.id !== itemId || it.type !== 'shape') return it;
        const base = ensureShapeConfig(it.shape);
        const result = updater ? updater({ ...base }) : base;
        const merged = result && typeof result === 'object' ? { ...base, ...result } : base;
        return { ...it, shape: ensureShapeConfig(merged) };
      })
    );
  };

  const patchSelectedShape = (patchOrUpdater) => {
    if (!selectedItem || selectedItem.type !== 'shape') return;
    if (typeof patchOrUpdater === 'function') {
      updateShapeItem(selectedItem.id, patchOrUpdater);
    } else {
      updateShapeItem(selectedItem.id, (shape) => ({ ...shape, ...patchOrUpdater }));
    }
  };

  const patchSelectedIcon = useCallback(
    (patch) => {
      if (!selectedItem || selectedItem.type !== 'icon') return;
      updateItems((items) =>
        items.map((it) => {
          if (it.id !== selectedItem.id) return it;
          const base = {
            ...it,
            color:
              typeof it.color === 'string' && it.color ? it.color : DEFAULT_ICON_COLOR,
            strokeWidth:
              typeof it.strokeWidth === 'number' ? it.strokeWidth : DEFAULT_ICON_STROKE,
          };
          const applied =
            typeof patch === 'function' ? patch({ ...base }) : patch || {};
          const merged = { ...base, ...applied };
          if (!LUCIDE_ICON_SET.has(merged.icon)) {
            merged.icon = base.icon;
          }
          merged.color =
            typeof merged.color === 'string' && merged.color ? merged.color : DEFAULT_ICON_COLOR;
          const stroke =
            typeof merged.strokeWidth === 'number' ? merged.strokeWidth : base.strokeWidth;
          merged.strokeWidth = clamp(stroke, ICON_STROKE_MIN, ICON_STROKE_MAX);
          return merged;
        })
      );
    },
    [selectedItem, updateItems]
  );







  /* ---------- PowerPoint export ----------------------- */
  const handleExport = () => {
    const pptx = new PptxGenJS();
    pptx.defineLayout({ name: 'WIDESCREEN', width: 10, height: 5.625 });
    pptx.layout = 'WIDESCREEN';

    slides.forEach((sl) => {
      const slide = pptx.addSlide();
      if (template?.dataUrl) {
        slide.addImage({ data: template.dataUrl, x: 0, y: 0, w: 10, h: 5.625 });
      }
      slide.addText(sl.name, {
        x: 0.3,
        y: 0.2,
        fontSize: 14,
        bold: true,
      });

      sl.items.forEach((it) => {
        const x = it.x * PX_TO_IN;
        const y = it.y * PX_TO_IN;
        const w = it.w * PX_TO_IN;
        const h = it.h * PX_TO_IN;
        const rotation = normalizeRotation(it.rotation);

        if (it.type === 'text') {
          const runs = richTextHtmlToPptxRuns(it.richText, it.style);
          slide.addText(runs, {
            x,
            y,
            w,
            h,
            align: it.style.align,
            fontFace: it.style.fontFamily,
            fontSize: it.style.fontSize,
            color: toPptxColor(it.style.color),
          });
        } else if (it.type === 'table') {
          const table = ensureTableData(it.table);
          const borderWidthPt = Math.max(0, table.border.width * 0.75);
          const pptBorder =
            borderWidthPt <= 0.01
              ? { type: 'none', pt: 0, color: toPptxColor(table.border.color) }
              : {
                  pt: borderWidthPt,
                  color: toPptxColor(table.border.color),
                  type:
                    table.border.style === 'dashed' || table.border.style === 'dotted'
                      ? 'dash'
                      : 'solid',
                };
          const colW = table.columnSizes.map((fraction) => +(w * fraction).toFixed(4));
          const rowH = table.rowSizes.map((fraction) => +(h * fraction).toFixed(4));
          const cellMargin = Math.max(0.01, +(table.cellPadding * PX_TO_IN).toFixed(3));
          const rows = table.cells.map((row, rIdx) =>
            row.map((cell) => ({
              text: extractPlainTextFromRichText(cell) || '',
              options: {
                bold: rIdx < table.headerRows,
                fontFace: table.fontFamily,
                fontSize: table.fontSize,
                color: toPptxColor(table.textColor),
                align: table.align,
                margin: cellMargin,
                fill:
                  rIdx < table.headerRows
                    ? { color: toPptxColor(table.headerFill) }
                    : undefined,
                border: pptBorder,
              },
            })),
          );
          slide.addTable(rows, {
            x,
            y,
            w,
            h,
            colW,
            rowH,
            border: pptBorder,
            fontFace: table.fontFamily,
            fontSize: table.fontSize,
            align: table.align,
          });
        } else if (it.type === 'graph') {
          const gBase = { ...allGraphs[it.graphIndex], ...it.overrides };
          const g = (!gBase.colors && globalPaletteColors && globalPaletteColors.length)
            ? { ...gBase, colors: globalPaletteColors }
            : gBase;
          const { chartType, series, opts, rows } = graphToPptx(g);
          if (chartType === 'table') {
            slide.addTable(rows, { x, y, w, h, border: { pt: 1, color: '000000' }, fontSize: 10 });
          } else if (chartType) {
            slide.addChart(chartType, series, {
              x,
              y,
              w,
              h,
              ...opts,
            });
          }
        } else if (it.type === 'image') {
          slide.addImage({
            data: it.src,
            x,
            y,
            w,
            h,
            opacity: typeof it.opacity === 'number' ? clamp(it.opacity, 0, 1) : 1,
          });
        } else if (it.type === 'icon') {
          const iconName = LUCIDE_ICON_SET.has(it.icon) ? it.icon : LUCIDE_ICON_FALLBACK;
          const IconComponent = LucideIcons[iconName];
          if (IconComponent) {
            const svgMarkup = renderToStaticMarkup(
              <IconComponent
                color={it.color || DEFAULT_ICON_COLOR}
                strokeWidth={clamp(
                  typeof it.strokeWidth === 'number' ? it.strokeWidth : DEFAULT_ICON_STROKE,
                  ICON_STROKE_MIN,
                  ICON_STROKE_MAX
                )}
                size={512}
              />
            );
            const dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup)}`;
            slide.addImage({
              data: dataUrl,
              x,
              y,
              w,
              h,
            });
          }
        } else if (it.type === 'shape') {
          const shapeCfg = ensureShapeConfig(it.shape);
          const dashMap = { dashed: 'dash', dotted: 'dot' };
          if (shapeCfg.kind === 'line') {
            const horizontal = it.w >= it.h;
            const lineWidthPt = Math.max(0, shapeCfg.strokeWidth * 0.75);
            const lineOptions =
              lineWidthPt <= 0.01
                ? { type: 'none' }
                : {
                    color: toPptxColor(shapeCfg.stroke || '#000000'),
                    width: Math.max(lineWidthPt, 0.25),
                    dashType: dashMap[shapeCfg.strokeStyle] || 'solid',
                  };
            const startX = horizontal ? x : x + (it.w / 2) * PX_TO_IN;
            const startY = horizontal ? y + (it.h / 2) * PX_TO_IN : y;
            const deltaW = horizontal ? Math.max(w, 0.01) : 0;
            const deltaH = horizontal ? 0 : Math.max(h, 0.01);
            slide.addShape('line', {
              x: +startX.toFixed(4),
              y: +startY.toFixed(4),
              w: +deltaW.toFixed(4),
              h: +deltaH.toFixed(4),
              line: lineOptions,
              rotate: rotation,
            });
          } else {
            const pptShape =
              {
                rectangle: 'rect',
                roundedRect: 'roundRect',
                ellipse: 'ellipse',
                circle: 'ellipse',
                triangle: 'triangle',
                diamond: 'diamond',
                arrowRight: 'rightArrow',
                arrowLeft: 'leftArrow',
                arrowUp: 'upArrow',
                arrowDown: 'downArrow',
                pentagon: 'pentagon',
                hexagon: 'hexagon',
                star: 'star5',
              }[shapeCfg.kind] || 'rect';
            const fillOpacity = clamp(shapeCfg.fillOpacity, 0, 1);
            const transparency = Math.max(0, Math.min(100, Math.round((1 - fillOpacity) * 100)));
            const lineWidthPt = Math.max(0, shapeCfg.strokeWidth * 0.75);
            const line =
              lineWidthPt <= 0.01
                ? { type: 'none' }
                : {
                    color: toPptxColor(shapeCfg.stroke || '#000000'),
                    width: Math.max(lineWidthPt, 0.25),
                    dashType: dashMap[shapeCfg.strokeStyle] || 'solid',
                  };
            const options = {
              x,
              y,
              w,
              h,
              rotate: rotation,
              fill: {
                color: toPptxColor(shapeCfg.fill || '#ffffff'),
                transparency,
              },
              line,
            };
            if (shapeCfg.kind === 'roundedRect') {
              const radiusFactor =
                Math.max(0, Math.min(1, (shapeCfg.borderRadius * 2) / Math.max(1, Math.min(it.w, it.h))));
              options.rectRadius = +radiusFactor.toFixed(3);
            }
            slide.addShape(pptShape, options);
          }
        }
      });
    });

    pptx.writeFile(`${presentationID || 'presentation'}.pptx`);
  };

  /* ------------------ DLG state ----------------------- */
  const [settingsDlg, setSettingsDlg] = useState(false);
  const [answersDlg, setAnswersDlg] = useState(false);
  const [axisMenuAnchor, setAxisMenuAnchor] = useState(null);
  const [labelMenuAnchor, setLabelMenuAnchor] = useState(null);
  // Keep overlay state in a ref for the scale observer
  useEffect(() => {
    overlayOpenRef.current = !!(
      settingsDlg ||
      answersDlg ||
      brandDlg ||
      templateDlg ||
      imageDialogState.open ||
      iconDialogState.open ||
      bulkDlg ||
      analysisDlg ||
      renameIdx !== null
    );
  }, [settingsDlg, answersDlg, brandDlg, templateDlg, imageDialogState.open, iconDialogState.open, bulkDlg, analysisDlg, renameIdx]);

  // When overlays close, recompute scale once
  useEffect(() => {
    const anyOpen = !!(
      settingsDlg ||
      answersDlg ||
      brandDlg ||
      templateDlg ||
      imageDialogState.open ||
      iconDialogState.open ||
      bulkDlg ||
      analysisDlg ||
      renameIdx !== null
    );
    if (!anyOpen && canvasContainerRef.current) {
      const w = canvasContainerRef.current.clientWidth || 0;
      const availableWidth = Math.max(0, w);
      const sRaw = Math.min(1, availableWidth / CANVAS_W);
      const s = sRaw > 0 ? sRaw : 1;
      if (Math.abs(s - scaleRef.current) >= 0.01) {
        scaleRef.current = s;
        setScale(s);
      }
    }
  }, [settingsDlg, answersDlg, brandDlg, templateDlg, imageDialogState.open, iconDialogState.open, bulkDlg, analysisDlg, renameIdx]);

  /* --- active graph helpers --- */
  const activeBaseGraph = selectedItem?.type === 'graph' ? allGraphs[selectedItem.graphIndex] : null;
  const activeOverrides = selectedItem?.overrides || {};
  const activeSettings = { ...DEFAULT_SETTINGS, ...(activeOverrides.settings || {}) };
  const effectiveMetric = activeOverrides.metric || activeBaseGraph?.metric || 'count';
  const selectedTable = useMemo(
    () => (selectedItem?.type === 'table' ? ensureTableData(selectedItem.table) : null),
    [selectedItem],
  );
  const selectedTableLink = useMemo(
    () => (selectedItem?.type === 'table' ? sanitizeTableLink(selectedItem.tableLink) : null),
    [selectedItem],
  );
  const selectedShape = useMemo(
    () => (selectedItem?.type === 'shape' ? ensureShapeConfig(selectedItem.shape) : null),
    [selectedItem],
  );
  const selectedIcon = useMemo(
    () => (selectedItem?.type === 'icon' ? selectedItem : null),
    [selectedItem],
  );
  const selectedIconColor =
    selectedIcon && typeof selectedIcon.color === 'string' && selectedIcon.color
      ? selectedIcon.color
      : DEFAULT_ICON_COLOR;
  const selectedIconStroke = selectedIcon
    ? clamp(
        typeof selectedIcon.strokeWidth === 'number'
          ? selectedIcon.strokeWidth
          : DEFAULT_ICON_STROKE,
        ICON_STROKE_MIN,
        ICON_STROKE_MAX
      )
    : DEFAULT_ICON_STROKE;
  const isSelectedShapeLine = selectedShape ? isLineLikeShape(selectedShape) : false;
  const isSelectedShapeRect =
    selectedShape && ['rectangle', 'roundedRect'].includes(selectedShape.kind);
  const selectedShapeRadiusLimit =
    selectedItem?.type === 'shape'
      ? Math.max(0, Math.round(Math.min(selectedItem.w, selectedItem.h) / 2))
      : 0;
  const selectedImageOpacity =
    selectedItem?.type === 'image'
      ? clamp(
          typeof selectedItem.opacity === 'number' ? selectedItem.opacity : 1,
          0,
          1,
        )
      : 1;
  const imageDialogExistingItem = useMemo(
    () =>
      imageDialogState.targetId
        ? activeSlide.items.find(
            (it) => it.id === imageDialogState.targetId && it.type === 'image'
          ) || null
        : null,
    [activeSlide.items, imageDialogState.targetId]
  );

  const answerOptions = useMemo(() => {
    if (!activeBaseGraph) return [];
    if (activeBaseGraph.graphType === 'histogram') {
      const { bins } = activeBaseGraph.data.histogram ?? {};
      return bins ? bins.slice(0, -1).map((b, i) => `${b.toFixed(1)}-${bins[i + 1].toFixed(1)}`) : [];
    }
    return activeBaseGraph.data.labels || [];
  }, [activeBaseGraph]);

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                            */
  /* ------------------------------------------------------------------ */
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f8fbff 0%, #eef2ff 50%, #fdf2f8 100%)',
        py: { xs: 3, md: 4.5 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, md: 3 } }}>
        <Stack spacing={2.25}>
          <Box
            sx={{
              borderRadius: 4,
              color: '#0f172a',
              border: '1px solid rgba(148, 163, 184, 0.2)',
              background: 'linear-gradient(135deg, rgba(191, 219, 254, 0.9) 0%, rgba(244, 219, 255, 0.9) 100%)',
              boxShadow: '0 20px 40px rgba(148, 163, 184, 0.25)',
              backdropFilter: 'blur(8px)',
              px: { xs: 2.5, md: 3 },
              py: { xs: 2.5, md: 3 },
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  Sunum Tasarımcısı
                </Typography>
                <Typography variant="body1" sx={{ color: 'rgba(15,23,42,0.75)' }}>
                  {heroSubtitle}
                </Typography>
                {heroChips.length > 0 && (
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                    {heroChips.map((chip) => (
                      <Chip key={chip} label={chip} sx={heroChipSx} />
                    ))}
                  </Stack>
                )}
              </Box>
              <Stack
                spacing={0.75}
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="flex-end"
                sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap' }}
              >
                
                <Button
                  variant="contained"
                  startIcon={
                    isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                  }
                  onClick={handleSave}
                  disabled={isSaving || !hasUnsavedChanges || !organisation || !presentationID}
                  sx={{
                    ...primaryButtonSx,
                    background: hasUnsavedChanges
                      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                      : 'linear-gradient(135deg, #d1d5db 0%, #94a3b8 100%)',
                    color: hasUnsavedChanges ? '#022c22' : '#1f2937',
                    '&:hover': hasUnsavedChanges
                      ? { background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }
                      : undefined,
                  }}
                >
                  {isSaving
                    ? 'Kaydediliyor...'
                    : hasUnsavedChanges
                      ? 'Değişiklikleri Kaydet'
                      : 'Kaydedildi'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleExport}
                  sx={{
                    ...primaryButtonSx,
                    background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                    color: '#0f172a',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #9333ea 0%, #4f46e5 100%)',
                    },
                  }}
                >
                  PPT İndir
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() =>  setAnalysisDlg(true)}
                  sx={{
                    ...primaryButtonSx,
                    background: '#ffffff',
                  }}
                >
                  Veri Aktar
                </Button>
                {saveError && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      flexBasis: '100%',
                      textAlign: { xs: 'left', sm: 'right' },
                      pt: 0.5,
                    }}
                  >
                    {saveError}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </Box>

          <Paper
            sx={{
              borderRadius: 4,
              border: '1px solid rgba(148, 163, 184, 0.16)',
              boxShadow: '0 18px 32px rgba(148, 163, 184, 0.2)',
              backgroundColor: '#ffffff',
              ...surfaceBaseSx,
            }}
          >
            <Stack spacing={2}>              

              <Box
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 3,
                  bgcolor: 'rgba(255,255,255,0.96)',
                  border: '1px solid rgba(148,163,184,0.2)',
                  boxShadow: '0 20px 40px rgba(148, 163, 184, 0.2)',
                  backdropFilter: 'blur(12px)',
                }}
              >
              {/* --------- Toolbar ---------------------------------- */}
                <Stack spacing={1.5} mb={2}>
                  <ToolbarContainer>
                    <Tooltip title="Grafik Ekle">
                      <IconButton size="small" sx={toolbarIconButtonSx} onClick={() => setGraphDlg(true)}>
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Metin Kutusu">
                      <IconButton size="small" sx={toolbarIconButtonSx} onClick={addText}>
                        <TextFieldsIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Şekil Ekle">
                      <IconButton
                        size="small"
                        sx={toolbarIconButtonSx}
                        onClick={(e) => setShapeMenuAnchor(e.currentTarget)}
                      >
                        <CropSquareIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={shapeMenuAnchor}
                      open={Boolean(shapeMenuAnchor)}
                      onClose={() => setShapeMenuAnchor(null)}
                      MenuListProps={{ dense: true }}
                      disableScrollLock
                    >
                      {SHAPE_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          onClick={() => {
                            addShape(opt.value);
                            setShapeMenuAnchor(null);
                          }}
                        >
                          <ListItemIcon>
                            <opt.Icon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={opt.label} />
                        </MenuItem>
                      ))}
                    </Menu>

                    <Tooltip title="İkon Ekle">
                      <IconButton
                        size="small"
                        sx={toolbarIconButtonSx}
                        onClick={() =>
                          openIconDialog(selectedItem?.type === 'icon' ? selectedItem.id : null)
                        }
                      >
                        <CategoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Tablo Ekle">
                      <IconButton
                        size="small"
                        sx={toolbarIconButtonSx}
                        onClick={(e) => setTableMenuAnchor(e.currentTarget)}
                      >
                        <TableChartIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Görsel Ekle">
                      <IconButton size="small" sx={toolbarIconButtonSx} onClick={() => openImageDialog()}>
                        <ImageSearchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={tableMenuAnchor}
                      open={Boolean(tableMenuAnchor)}
                      onClose={() => setTableMenuAnchor(null)}
                      MenuListProps={{ dense: true }}
                      disableScrollLock
                    >
                      <MenuItem
                        onClick={() => {
                          addTable();
                          setTableMenuAnchor(null);
                        }}
                      >
                        <ListItemIcon>
                          <AddIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Boş tablo oluştur" />
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setLinkedTableDlg(true);
                          setTableMenuAnchor(null);
                        }}
                        disabled={!savedLinkedTableAssets.length}
                      >
                        <ListItemIcon>
                          <LinkIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary="E-tablo ekle"
                          secondary={
                            savedLinkedTableAssets.length
                              ? 'Kaynağa bağlı tablo seç'
                              : 'Kayıtlı e-tablo yok'
                          }
                        />
                      </MenuItem>
                      {savedTableAssets.length > 0 ? (
                        <>
                          <Divider />
                          {savedTableAssets.map((tbl, idx) => (
                            <MenuItem
                              key={`saved-table-${idx}-${tbl.header || tbl.__sourceTableId || idx}`}
                              onClick={() => {
                                addSavedTable(tbl);
                                setTableMenuAnchor(null);
                              }}
                            >
                              <ListItemText
                                primary={tbl.header || `Tablo ${idx + 1}`}
                                secondary={tbl.__groupName || tbl.groupName || null}
                                secondaryTypographyProps={{ noWrap: true }}
                              />
                            </MenuItem>
                          ))}
                        </>
                      ) : (
                        <MenuItem disabled>
                          <ListItemText primary="Kayıtlı tablo bulunmuyor" />
                        </MenuItem>
                      )}
                    </Menu>

                    <Tooltip title="Global Marka Rengi">
                      <IconButton size="small" sx={toolbarIconButtonSx} onClick={() => setBrandDlg(true)}>
                        <PaletteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <InputLabel>Global Palet</InputLabel>
                      <Select
                        label="Global Palet"
                        value={globalPaletteKey}
                        onChange={(e) => setGlobalPaletteKey(e.target.value)}
                        MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 300 } } }}
                      >
                        <MenuItem value="none">(Yok)</MenuItem>
                        {Object.entries(COLOR_PALETTES).map(([k, arr]) => (
                          <MenuItem key={k} value={k}>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {arr.slice(0, 4).map((clr, i) => (
                                <Box
                                  key={i}
                                  sx={{ width: 12, height: 12, bgcolor: clr, borderRadius: 0.5, border: '1px solid #e5e7eb' }}
                                />
                              ))}
                              <Typography variant="body2" ml={1}>
                                {k.charAt(0).toUpperCase() + k.slice(1)}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Tooltip title="Şablon (Arka Plan)">
                      <IconButton size="small" sx={toolbarIconButtonSx} onClick={() => setTemplateDlg(true)}>
                        <WallpaperIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Tüm Grafikleri Sayfalara Ekle">
                      <span style={{ display: 'inline-flex' }}>
                        <IconButton
                          size="small"
                          sx={toolbarIconButtonSx}
                          onClick={() => setBulkDlg(true)}
                          disabled={!allGraphs.length}
                        >
                          <PlaylistAddIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {/* ---------------- Bulk add graphs dialog -------------------- */}
                    <Dialog open={bulkDlg} onClose={() => setBulkDlg(false)} maxWidth="md" fullWidth disableScrollLock>
                      <DialogTitle>Tüm Grafikleri Sayfaya Ekle</DialogTitle>
                      <DialogContent dividers sx={{ maxHeight: '72vh' }}>
                        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                          {/* Left: selection list */}
                          <Box sx={{ flex: 1, minWidth: 280 }}>
                            <TextField
                              size="small"
                              fullWidth
                              placeholder="Ara (başlık)"
                              value={bulkSearch}
                              onChange={(e) => setBulkSearch(e.target.value)}
                              sx={{ mb: 1 }}
                              InputProps={{ sx: { height: 32, fontSize: 13 } }}
                            />
                            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1, m:0, p:0, lineHeight:1 }}>
                              <Checkbox
                                size="small"
                                checked={
                                  bulkFilteredGraphs.length > 0 &&
                                  bulkFilteredGraphs.every(g => bulkSelectedHeaders.includes(g.header))
                                }
                                indeterminate={
                                  bulkFilteredGraphs.some(g => bulkSelectedHeaders.includes(g.header)) &&
                                  !bulkFilteredGraphs.every(g => bulkSelectedHeaders.includes(g.header))
                                }
                                onChange={(e) => {
                                  const visibleHeaders = bulkFilteredGraphs.map(g => g.header);
                                  setBulkSelectedHeaders((prev) => {
                                    if (e.target.checked) {
                                      const set = new Set(prev);
                                      visibleHeaders.forEach(h => set.add(h));
                                      return Array.from(set);
                                    } else {
                                      return prev.filter(h => !visibleHeaders.includes(h));
                                    }
                                  });
                                }}
                              />
                              <Typography variant="body2">Tümünü Seç</Typography>
                            </Stack>
                            <Paper variant="outlined" sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                              <Stack>
                                {bulkFilteredGraphs.map((g, idx) => {
                                  const { analysisName, groupName } = extractGraphMeta(g);
                                  const secondary = [analysisName, groupName].filter(Boolean).join(' • ');
                                  return (
                                    <Stack
                                      key={`${g.header || 'graph'}-${idx}`}
                                      direction="row"
                                      alignItems="center"
                                      spacing={0.5}
                                      sx={{ px: 1, m:0, p:0, lineHeight:1 }}
                                    >
                                      <Checkbox
                                        size="small"
                                        checked={bulkSelectedHeaders.includes(g.header)}
                                        onChange={() => {
                                          setBulkSelectedHeaders((prev) => prev.includes(g.header)
                                            ? prev.filter(h => h !== g.header)
                                            : [...prev, g.header]);
                                        }}
                                      />
                                      <Box sx={{ flex: 1, overflow: 'hidden' }}>
                                        <Typography variant="body2" noWrap>{g.header}</Typography>
                                        {secondary && (
                                          <Typography variant="caption" color="text.secondary" noWrap>
                                            {secondary}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Stack>
                                  );
                                })}
                              </Stack>
                            </Paper>
                          </Box>

                          {/* Right: options */}
                          <Box sx={{ flex: 1, minWidth: 280 }}>
                            <Stack spacing={2}>
                              <Stack spacing={1}>
                                <Typography variant="body2">Yerleşim</Typography>
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={bulkPlacementMode}
                                  onChange={(e, v) => v && setBulkPlacementMode(v)}
                                >
                                  <ToggleButton value="separate">Her grafik ayrı sayfa</ToggleButton>
                                  <ToggleButton value="single" disabled={!slides.length}>Seçilen sayfaya hepsi</ToggleButton>
                                </ToggleButtonGroup>
                              </Stack>

                              {bulkPlacementMode === 'single' && (
                                <FormControl size="small" fullWidth>
                                  <InputLabel>Hedef Sayfa</InputLabel>
                                  <Select
                                    label="Hedef Sayfa"
                                    value={bulkTargetSlideId || ''}
                                    onChange={(e) => setBulkTargetSlideId(e.target.value)}
                                    MenuProps={{ disableScrollLock: true }}
                                    disabled={!slides.length}
                                  >
                                    {slides.map((slide, idx) => (
                                      <MenuItem key={slide.id} value={slide.id}>
                                        {`${idx + 1}. ${slide.name || 'Başlıksız Sayfa'}`}
                                      </MenuItem>
                                    ))}
                                    {!slides.length && (
                                      <MenuItem value="" disabled>
                                        Sayfa bulunamadı
                                      </MenuItem>
                                    )}
                                  </Select>
                                </FormControl>
                              )}

                              <FormControl size="small" fullWidth>
                                <InputLabel>Grafik Türü</InputLabel>
                                <Select label="Grafik Türü" value={bulkType} onChange={(e) => setBulkType(e.target.value)} MenuProps={{ disableScrollLock: true }}>
                                  {['bar','barStacked','histogram','line','area','pie','scatter','radar','table'].map(t => (
                                    <MenuItem key={t} value={t}>{t}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              {ORIENTABLE_TYPES.includes(bulkType) && (
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Typography variant="body2" sx={{ mr: 1 }}>Yatay/Dikey</Typography>
                                    <ToggleButtonGroup
                                      exclusive
                                      size="small"
                                      value={bulkOrientation}
                                      onChange={(e, v) => v && setBulkOrientation(v)}
                                    >
                                      <ToggleButton value="vertical" title="Dikey">
                                        <SwapVertIcon fontSize="inherit" />
                                      </ToggleButton>
                                      <ToggleButton value="horizontal" title="Yatay">
                                        <SwapHorizIcon fontSize="inherit" />
                                      </ToggleButton>
                                    </ToggleButtonGroup>
                                </Stack>
                              )}

                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ mr: 1 }}>Etiket Poz.</Typography>
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={bulkLabelPos}
                                  onChange={(e, v) => v && setBulkLabelPos(v)}
                                >
                                  <ToggleButton value="inside" title="İçeride">İç</ToggleButton>
                                  <ToggleButton value="top" title="Üstte">Üst</ToggleButton>
                                  <ToggleButton value="outside" title="Dışarıda">Dış</ToggleButton>
                                </ToggleButtonGroup>
                              </Stack>

                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ mr: 1 }}>Baz Ekle</Typography>
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={bulkBaseType}
                                  onChange={(e, v) => v && setBulkBaseType(v)}
                                >
                                  <ToggleButton value="none" title="Baz Yok">
                                    <NotInterestedIcon fontSize="inherit" />
                                  </ToggleButton>
                                  <ToggleButton value="participants" title="Katılımcı">
                                    <PeopleIcon fontSize="inherit" />
                                  </ToggleButton>
                                  <ToggleButton value="answers" title="Cevap">
                                    <FormatListNumberedIcon fontSize="inherit" />
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              </Stack>

                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ mr: 1 }}>Frekans/Yüzde</Typography>
                                <ToggleButtonGroup
                                  exclusive
                                  size="small"
                                  value={bulkMetric}
                                  onChange={(e, v) => v && setBulkMetric(v)}
                                >
                                  <ToggleButton value="count" title="Sayı">
                                    <LooksOneIcon fontSize="inherit" />
                                  </ToggleButton>
                                  <ToggleButton value="percentage" title="%">
                                    <PercentIcon fontSize="inherit" />
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              </Stack>

                              <Divider />
                              <Typography variant="body2">Kenar Boşlukları (px)</Typography>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Üst"
                                  value={bulkMarginTop}
                                  onChange={(e) => setBulkMarginTop(+e.target.value)}
                                  sx={{ width: 90 }}
                                  inputProps={{ min: 0, max: CANVAS_H }}
                                />
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Alt"
                                  value={bulkMarginBottom}
                                  onChange={(e) => setBulkMarginBottom(+e.target.value)}
                                  sx={{ width: 90 }}
                                  inputProps={{ min: 0, max: CANVAS_H }}
                                />
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Sol"
                                  value={bulkMarginLeft}
                                  onChange={(e) => setBulkMarginLeft(+e.target.value)}
                                  sx={{ width: 90 }}
                                  inputProps={{ min: 0, max: CANVAS_W }}
                                />
                                <TextField
                                  size="small"
                                  type="number"
                                  label="Sağ"
                                  value={bulkMarginRight}
                                  onChange={(e) => setBulkMarginRight(+e.target.value)}
                                  sx={{ width: 90 }}
                                  inputProps={{ min: 0, max: CANVAS_W }}
                                />
                              </Stack>
                              <Stack direction="row" spacing={1}>
                                <Button variant="text" size="small" onClick={() => { setBulkMarginTop(20); setBulkMarginRight(20); setBulkMarginBottom(20); setBulkMarginLeft(20); }}>
                                  Varsayılan (20px)
                                </Button>
                                <Button variant="text" size="small" onClick={() => { setBulkMarginTop(0); setBulkMarginRight(0); setBulkMarginBottom(0); setBulkMarginLeft(0); }}>
                                  0 px
                                </Button>
                              </Stack>

                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ m:0, p:0, lineHeight:1 }}>
                                <Checkbox size="small" checked={bulkDataLabels} onChange={(e) => setBulkDataLabels(e.target.checked)} />
                                <Typography variant="body2">Veri Etiketleri</Typography>
                                <Checkbox size="small" checked={bulkLegend} onChange={(e) => setBulkLegend(e.target.checked)} />
                                <Typography variant="body2">Legend</Typography>
                              </Stack>
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ m:0, p:0, lineHeight:1 }}>
                                <Checkbox size="small" checked={bulkGrid} onChange={(e) => setBulkGrid(e.target.checked)} />
                                <Typography variant="body2">Izgara Çizgileri</Typography>
                                <Checkbox size="small" checked={bulkAutoScale} onChange={(e) => setBulkAutoScale(e.target.checked)} />
                                <Typography variant="body2">Otomatik Ölçek</Typography>
                              </Stack>
                            </Stack>
                          </Box>
                        </Stack>
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setBulkDlg(false)}>Vazgeç</Button>
                        <Button variant="contained" onClick={performBulkAddToSlides} disabled={!bulkSelectedHeaders.length}>Ekle</Button>
                      </DialogActions>
                    </Dialog>

                    <Stack direction="row" spacing={0.75}>
                      <Tooltip title="Geri al">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleUndo}
                            disabled={!canUndo}
                            sx={{ ...historyIconButtonSx }}
                          >
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Yinele">
                        <span>
                          <IconButton
                            size="small"
                            onClick={handleRedo}
                            disabled={!canRedo}
                            sx={{ ...historyIconButtonSx }}
                          >
                            <RedoIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </ToolbarContainer>

                  {/* ---- Text style controls ------------------------- */}
                  {selectedItem?.type === 'text' && (
                    <ToolbarContainer>
                      <FormControl size="small" sx={{ width: 160 }}>
                        <InputLabel>Font</InputLabel>
                        <Select
                          label="Font"
                          value={selectedItem.style.fontFamily || FONTS[0]}
                          onChange={(e) => applyFontFamily(e.target.value)}
                          MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                          {FONTS.map((f) => (
                            <MenuItem key={f} value={f} style={{ fontFamily: f }}>{f}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ width: 80 }}>
                        <InputLabel>Boyut</InputLabel>
                        <Select
                          label="Boyut"
                          value={selectedItem.style.fontSize}
                          onChange={(e) => applyFontSize(Number(e.target.value))}
                          MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                          {[12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48].map((sz) => <MenuItem key={sz} value={sz}>{sz}</MenuItem>) }
                        </Select>
                      </FormControl>
                      <ToolbarColorButton
                        value={selectedItem.style.color}
                        onChange={(e) => applyTextColor(e.target.value)}
                        label="Metin Rengi"
                        IconComponent={FormatColorTextIcon}
                      />
                      <ToggleButton
                        value="bold"
                        selected={
                          activeRichTextId === selectedItem.id
                            ? richTextSelectionState.bold
                            : !!selectedItem.style.bold
                        }
                        size="small"
                        onClick={() => toggleInlineFormat('bold', 'bold')}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <FormatBoldIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton
                        value="italic"
                        selected={
                          activeRichTextId === selectedItem.id
                            ? richTextSelectionState.italic
                            : !!selectedItem.style.italic
                        }
                        size="small"
                        onClick={() => toggleInlineFormat('italic', 'italic')}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <FormatItalicIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButton
                        value="underline"
                        selected={
                          activeRichTextId === selectedItem.id
                            ? richTextSelectionState.underline
                            : !!selectedItem.style.underline
                        }
                        size="small"
                        onClick={() => toggleInlineFormat('underline', 'underline')}
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <FormatUnderlinedIcon fontSize="small" />
                      </ToggleButton>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={selectedItem.style.align}
                        onChange={(e, val) => {
                          if (!val) return;
                          applyTextAlign(val);
                        }}
                      >
                        <ToggleButton value="left" onMouseDown={(e) => e.preventDefault()}>
                          <FormatAlignLeftIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="center" onMouseDown={(e) => e.preventDefault()}>
                          <FormatAlignCenterIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton value="right" onMouseDown={(e) => e.preventDefault()}>
                          <FormatAlignRightIcon fontSize="small" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </ToolbarContainer>
                  )}
                  {selectedItem?.type === 'shape' && selectedShape && (
                    <ToolbarContainer>
                      <FormControl size="small" sx={{ width: 160 }}>
                        <InputLabel>Şekil</InputLabel>
                        <Select
                          label="Şekil"
                          value={selectedShape.kind}
                          onChange={(e) => {
                            const nextKind = e.target.value;
                            if (!nextKind) return;
                            updateShapeItem(selectedItem.id, (shape) => {
                              const nextDefaults = createShapeDefaults(nextKind);
                              const preservedFill = shape.fill || nextDefaults.fill || '#ffffff';
                              const preservedStroke = shape.stroke || nextDefaults.stroke || '#000000';
                              const preservedStrokeStyle = shape.strokeStyle || nextDefaults.strokeStyle || 'solid';
                              const preservedStrokeWidth =
                                typeof shape.strokeWidth === 'number'
                                  ? shape.strokeWidth
                                  : nextDefaults.strokeWidth ?? 2;
                              const strokeWidth =
                                nextKind === 'line'
                                  ? nextDefaults.strokeWidth ?? preservedStrokeWidth
                                  : preservedStrokeWidth;
                              const fillOpacity =
                                nextKind === 'line'
                                  ? nextDefaults.fillOpacity
                                  : clamp(
                                      typeof shape.fillOpacity === 'number'
                                        ? shape.fillOpacity
                                        : nextDefaults.fillOpacity ?? 1,
                                      0,
                                      1,
                                    );
                              const borderRadius =
                                nextKind === 'rectangle' || nextKind === 'roundedRect'
                                  ? (typeof shape.borderRadius === 'number' && shape.borderRadius > 0
                                      ? shape.borderRadius
                                      : nextDefaults.borderRadius ?? 0)
                                  : 0;
                              return {
                                ...nextDefaults,
                                kind: nextKind,
                                fill: preservedFill,
                                fillOpacity,
                                stroke: preservedStroke,
                                strokeStyle: preservedStrokeStyle,
                                strokeWidth: clamp(strokeWidth, 0, 40),
                                borderRadius,
                              };
                            });
                          }}
                          MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 320 } } }}
                        >
                          {SHAPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <opt.Icon fontSize="small" />
                                <span>{opt.label}</span>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {!isSelectedShapeLine && (
                        <ToolbarColorButton
                          value={selectedShape.fill}
                          onChange={(e) => patchSelectedShape({ fill: e.target.value })}
                          label="Dolgu Rengi"
                          IconComponent={FormatColorFillIcon}
                        />
                      )}
                      {!isSelectedShapeLine && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 210, flexShrink: 0 }}>
                          <OpacityIcon fontSize="small" />
                          <Typography variant="body2">Şeffaflık</Typography>
                          <Slider
                            size="small"
                            value={Math.round(clamp(selectedShape.fillOpacity, 0, 1) * 100)}
                            min={0}
                            max={100}
                            onChange={(e, val) => {
                              const numeric = Array.isArray(val) ? val[0] : val;
                              if (typeof numeric !== 'number') return;
                              patchSelectedShape({ fillOpacity: clamp(numeric / 100, 0, 1) });
                            }}
                            sx={{ width: 140 }}
                          />
                          <Typography variant="caption">
                            {Math.round(clamp(selectedShape.fillOpacity, 0, 1) * 100)}%
                          </Typography>
                        </Box>
                      )}
                      <ToolbarColorButton
                        value={selectedShape.stroke}
                        onChange={(e) => patchSelectedShape({ stroke: e.target.value })}
                        label="Çizgi Rengi"
                        IconComponent={BorderColorIcon}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LineWeightIcon fontSize="small" />
                        <TextField
                          size="small"
                          type="number"
                          label="Kalınlık"
                          value={selectedShape.strokeWidth}
                          sx={{ width: 120 }}
                          inputProps={{ min: 0, max: 40, step: 0.5 }}
                          onChange={(e) => {
                            const numeric = Number(e.target.value);
                            if (Number.isNaN(numeric)) return;
                            patchSelectedShape({ strokeWidth: clamp(numeric, 0, 40) });
                          }}
                        />
                      </Box>
                      <FormControl size="small" sx={{ width: 140 }}>
                        <InputLabel>Çizgi Tipi</InputLabel>
                        <Select
                          label="Çizgi Tipi"
                          value={selectedShape.strokeStyle}
                          onChange={(e) => patchSelectedShape({ strokeStyle: e.target.value })}
                          MenuProps={{ disableScrollLock: true }}
                        >
                          <MenuItem value="solid">Düz</MenuItem>
                          <MenuItem value="dashed">Kesik</MenuItem>
                          <MenuItem value="dotted">Noktalı</MenuItem>
                        </Select>
                      </FormControl>
                      {isSelectedShapeRect && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 220, flexShrink: 0 }}>
                          <RoundedCornerIcon fontSize="small" />
                          <Typography variant="body2">Köşe</Typography>
                          <Slider
                            size="small"
                            value={Math.min(
                              selectedShapeRadiusLimit,
                              Math.round(selectedShape.borderRadius || 0),
                            )}
                            min={0}
                            max={Math.max(selectedShapeRadiusLimit, 0)}
                            onChange={(e, val) => {
                              const numeric = Array.isArray(val) ? val[0] : val;
                              if (typeof numeric !== 'number') return;
                              patchSelectedShape({
                                borderRadius: clamp(numeric, 0, selectedShapeRadiusLimit),
                              });
                            }}
                            sx={{ width: 140 }}
                          />
                          <Typography variant="caption">
                            {Math.round(selectedShape.borderRadius || 0)} px
                          </Typography>
                        </Box>
                      )}
                    </ToolbarContainer>
                  )}
                  {selectedIcon && (
                    <ToolbarContainer>
                      <Tooltip title="İkonu Değiştir">
                        <IconButton
                          size="small"
                          sx={toolbarIconButtonSx}
                          onClick={() => openIconDialog(selectedIcon.id)}
                        >
                          <CategoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <ToolbarColorButton
                        value={selectedIconColor}
                        onChange={(e) => patchSelectedIcon({ color: e.target.value })}
                        label="İkon Rengi"
                        IconComponent={FormatColorTextIcon}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 220 }}>
                        <LineWeightIcon fontSize="small" />
                        <Typography variant="body2">Çizgi Kalınlığı</Typography>
                        <Slider
                          size="small"
                          value={selectedIconStroke}
                          min={ICON_STROKE_MIN}
                          max={ICON_STROKE_MAX}
                          step={0.25}
                          onChange={(e, val) => {
                            const next = Array.isArray(val) ? val[0] : val;
                            if (typeof next !== 'number') return;
                            patchSelectedIcon({
                              strokeWidth: clamp(next, ICON_STROKE_MIN, ICON_STROKE_MAX),
                            });
                          }}
                          sx={{ width: 160 }}
                        />
                        <Typography variant="caption">
                          {selectedIconStroke.toFixed(2)}
                        </Typography>
                      </Box>
                    </ToolbarContainer>
                  )}
                  {selectedItem?.type === 'table' && selectedTable && selectedTableLink && (
                    <ToolbarContainer>
                      <Chip
                        size="small"
                        color="success"
                        icon={<LinkIcon fontSize="small" />}
                        label="E-tablo"
                        sx={{ fontWeight: 600 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        Kaynak: {selectedTable.__analysisName || selectedItem.__analysisName || selectedTable.__groupName || selectedItem.__groupName || 'Analiz'}
                      </Typography>
                      <Button
                        size="small"
                        startIcon={<LinkOffIcon fontSize="small" />}
                        onClick={() => detachLinkedTable(selectedItem.id)}
                      >
                        Bağımsız yap
                      </Button>
                    </ToolbarContainer>
                  )}
                  {selectedItem?.type === 'table' && selectedTable && (
                    <>
                      {activeRichTextContext?.type === 'table' && activeRichTextContext.itemId === selectedItem.id && (
                        <ToolbarContainer>
                          <ToggleButton
                            value="bold"
                            selected={richTextSelectionState.bold}
                            size="small"
                            onClick={() => toggleInlineFormat('bold', null)}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <FormatBoldIcon fontSize="small" />
                          </ToggleButton>
                          <ToggleButton
                            value="italic"
                            selected={richTextSelectionState.italic}
                            size="small"
                            onClick={() => toggleInlineFormat('italic', null)}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <FormatItalicIcon fontSize="small" />
                          </ToggleButton>
                          <ToggleButton
                            value="underline"
                            selected={richTextSelectionState.underline}
                            size="small"
                            onClick={() => toggleInlineFormat('underline', null)}
                            onMouseDown={(e) => e.preventDefault()}
                          >
                            <FormatUnderlinedIcon fontSize="small" />
                          </ToggleButton>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={null}
                            onChange={(e, val) => {
                              if (!val) return;
                              applyTextAlign(val);
                            }}
                          >
                            <ToggleButton value="left" onMouseDown={(e) => e.preventDefault()}>
                              <FormatAlignLeftIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="center" onMouseDown={(e) => e.preventDefault()}>
                              <FormatAlignCenterIcon fontSize="small" />
                            </ToggleButton>
                            <ToggleButton value="right" onMouseDown={(e) => e.preventDefault()}>
                              <FormatAlignRightIcon fontSize="small" />
                            </ToggleButton>
                          </ToggleButtonGroup>
                          <ToolbarColorButton
                            value={selectedTable.textColor}
                            onChange={(e) => applyTextColor(e.target.value)}
                            label="Metin Rengi"
                            IconComponent={FormatColorTextIcon}
                          />
                        </ToolbarContainer>
                      )}
                      <ToolbarContainer >
                        

                     
                  
                      <FormControl size="small" sx={{ width: 160 }}>
                        <InputLabel>Font</InputLabel>
                        <Select
                          label="Font"
                          value={selectedTable.fontFamily || FONTS[0]}
                          onChange={(e) =>
                            updateTableItem(selectedItem.id, (table) => ({
                              ...table,
                              fontFamily: e.target.value,
                            }))
                          }
                          MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                          {FONTS.map((f) => (
                            <MenuItem key={f} value={f} style={{ fontFamily: f }}>
                              {f}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        size="small"
                        type="number"
                        label="Boyut"
                        sx={{ width: 90 }}
                        value={selectedTable.fontSize}
                        inputProps={{ min: 8, max: 48 }}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          if (Number.isNaN(val)) return;
                          updateTableItem(selectedItem.id, (table) => ({
                            ...table,
                            fontSize: clamp(val, 8, 72),
                          }));
                        }}
                      />
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={selectedTable.align}
                        onChange={(e, val) =>
                          val &&
                          updateTableItem(selectedItem.id, (table) => ({
                            ...table,
                            align: val,
                          }))
                        }
                      >
                        <ToggleButton value="left"><FormatAlignLeftIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="center"><FormatAlignCenterIcon fontSize="small" /></ToggleButton>
                        <ToggleButton value="right"><FormatAlignRightIcon fontSize="small" /></ToggleButton>
                      </ToggleButtonGroup>
                      <ToolbarColorButton
                        value={selectedTable.textColor}
                        onChange={(e) =>
                          updateTableItem(selectedItem.id, (table) => ({
                            ...table,
                            textColor: e.target.value,
                          }))
                        }
                        label="Metin Rengi"
                        IconComponent={FormatColorTextIcon}
                      />
                      <ToolbarColorButton
                        value={selectedTable.headerFill}
                        onChange={(e) =>
                          updateTableItem(selectedItem.id, (table) => ({
                            ...table,
                            headerFill: e.target.value,
                          }))
                        }
                        label="Başlık Dolgusu"
                        IconComponent={FormatColorFillIcon}
                      />
                      <Tooltip title={`İç Boşluk: ${Math.round(selectedTable.cellPadding)} px`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 160, flexShrink: 0 }}>
                          <PaddingIcon fontSize="small" />
                          <Slider
                            size="small"
                            value={selectedTable.cellPadding}
                            min={2}
                            max={24}
                            step={1}
                            sx={{ width: 120 }}
                            onChange={(e, val) => {
                              const numeric = Array.isArray(val) ? val[0] : val;
                              if (typeof numeric !== 'number') return;
                              updateTableItem(selectedItem.id, (table) => ({
                                ...table,
                                cellPadding: Math.round(numeric),
                              }));
                            }}
                          />
                        </Box>
                      </Tooltip>
                      <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'center' }}>
                        {Math.round(selectedTable.cellPadding)} px
                      </Typography>
                      <Divider orientation="vertical" flexItem sx={{ height: 32, mx: 0.5, borderColor: 'rgba(148,163,184,0.4)' }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LineWeightIcon fontSize="small" />
                        <TextField
                          size="small"
                          type="number"
                          label="Çizgi Kalınlığı"
                          value={selectedTable.border.width}
                          sx={{ width: 120 }}
                          inputProps={{ min: 0.5, max: 8, step: 0.5 }}
                          onChange={(e) => {
                            const val = Number(e.target.value);
                            if (Number.isNaN(val)) return;
                            updateTableItem(selectedItem.id, (table) => ({
                              ...table,
                              border: { ...table.border, width: clamp(val, 0.5, 12) },
                            }));
                          }}
                        />
                      </Box>
                      <FormControl size="small" sx={{ width: 140 }}>
                        <InputLabel>Çizgi Tipi</InputLabel>
                        <Select
                          label="Çizgi Tipi"
                          value={selectedTable.border.style || 'solid'}
                          onChange={(e) =>
                            updateTableItem(selectedItem.id, (table) => ({
                              ...table,
                              border: { ...table.border, style: e.target.value },
                            }))
                          }
                          MenuProps={{ disableScrollLock: true }}
                        >
                          <MenuItem value="solid">Düz</MenuItem>
                          <MenuItem value="dashed">Kesik</MenuItem>
                          <MenuItem value="dotted">Noktalı</MenuItem>
                        </Select>
                      </FormControl>
                      <ToolbarColorButton
                        value={selectedTable.border.color}
                        onChange={(e) =>
                          updateTableItem(selectedItem.id, (table) => ({
                            ...table,
                            border: { ...table.border, color: e.target.value },
                          }))
                        }
                        label="Çizgi Rengi"
                        IconComponent={BorderColorIcon}
                      />
                    </ToolbarContainer>
                    </>
                  )}
                  {selectedItem?.type === 'image' && (
                    <ToolbarContainer>
                      <Tooltip title={`Opaklık: ${Math.round(selectedImageOpacity * 100)}%`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
                          <OpacityIcon fontSize="small" />
                          <Slider
                            size="small"
                            value={selectedImageOpacity}
                            min={0.1}
                            max={1}
                            step={0.05}
                            sx={{ width: 140 }}
                            onChange={(e, val) => {
                              const numeric = Array.isArray(val) ? val[0] : val;
                              if (typeof numeric !== 'number') return;
                              updateImageItem(selectedItem.id, {
                                opacity: clamp(numeric, 0, 1),
                              });
                            }}
                          />
                          <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
                            {Math.round(selectedImageOpacity * 100)}%
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ImageSearchIcon fontSize="small" />}
                        onClick={() => openImageDialog(selectedItem.id)}
                        sx={{ textTransform: 'none' }}
                      >
                        Görseli değiştir
                      </Button>
                    </ToolbarContainer>
                  )}

                  {/* ---- GRAPH controls -------------------------------- */}
                  {selectedItem?.type === 'graph' && activeBaseGraph && (
                    <>
                    <ToolbarContainer>
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Tip</InputLabel>
                        <Select label="Tip" value={activeOverrides.graphType || activeBaseGraph.graphType} onChange={(e) => patchGraphOverrides({ graphType: e.target.value })} MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 300 } } }}>
                          {['bar', 'barStacked', 'histogram', 'line', 'area', 'pie', 'scatter', 'radar', 'table'].map((t) => (<MenuItem key={t} value={t}>{t}</MenuItem>))}
                        </Select>
                      </FormControl>
                      {ORIENTABLE_TYPES.includes(activeOverrides.graphType || activeBaseGraph.graphType) && (
                        <ToggleButtonGroup exclusive size="small" value={activeOverrides.orientation || 'vertical'} onChange={(e, v) => v && patchGraphOverrides({ orientation: v })}>
                          <ToggleButton value="vertical" title="Dikey"><SwapVertIcon fontSize="inherit" /></ToggleButton>
                          <ToggleButton value="horizontal" title="Yatay"><SwapHorizIcon fontSize="inherit" /></ToggleButton>
                        </ToggleButtonGroup>
                      )}
                      <ToggleButtonGroup exclusive size="small" value={activeOverrides.sortOrder || 'none'} onChange={(e, v) => v && patchGraphOverrides({ sortOrder: v })}>
                        <ToggleButton value="none" title="Sırasız">–</ToggleButton>
                        <ToggleButton value="asc" title="Artan"><NorthIcon fontSize="inherit" /></ToggleButton>
                        <ToggleButton value="desc" title="Azalan"><SouthIcon fontSize="inherit" /></ToggleButton>
                      </ToggleButtonGroup>
                      <ToggleButtonGroup exclusive size="small" value={activeOverrides.metric || 'count'} onChange={(e, v) => v && patchGraphOverrides({ metric: v })}>
                        <ToggleButton value="count" title="Sayı"><LooksOneIcon fontSize="inherit" /></ToggleButton>
                        <ToggleButton value="percentage" title="%"><PercentIcon fontSize="inherit" /></ToggleButton>
                      </ToggleButtonGroup>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Palet</InputLabel>
                        <Select
                          label="Palet"
                          value={activeOverrides.paletteKey || 'default'}
                          onChange={(e) => {
                            const key = e.target.value;
                            patchGraphOverrides({ paletteKey: key, colors: COLOR_PALETTES[key] });
                          }}
                          MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 300 } } }}
                        >
                          {Object.entries(COLOR_PALETTES).map(([k, arr]) => (
                            <MenuItem key={k} value={k}>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                {arr.slice(0, 4).map((clr, i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      bgcolor: clr,
                                      borderRadius: 0.5,
                                      border: '1px solid #e5e7eb',
                                    }}
                                  />
                                ))}
                                <Typography variant="body2" ml={1}>
                                  {k.charAt(0).toUpperCase() + k.slice(1)}
                                </Typography>
                              </Stack>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <ToggleButtonGroup exclusive size="small" value={activeSettings.baseType} onChange={(e, v) => { if (!v) return; patchGraphOverrides({ settings: { ...activeSettings, baseType: v } }); }}>
                        <ToggleButton value="none" title="Baz Yok"><NotInterestedIcon fontSize="inherit" /></ToggleButton>
                        <ToggleButton value="participants" title="Katılımcı"><PeopleIcon fontSize="inherit" /></ToggleButton>
                        <ToggleButton value="answers" title="Cevap"><FormatListNumberedIcon fontSize="inherit" /></ToggleButton>
                      </ToggleButtonGroup>
                      <ToolbarColorButton
                        value={activeOverrides.color || '#6366f1'}
                        onChange={(e) => patchGraphOverrides({ color: e.target.value })}
                        label="Grafik Rengi"
                      />
                      <Tooltip title="Grafik Ayarları">
                        <IconButton size="small" sx={toolbarIconButtonSx} onClick={() => setSettingsDlg(true)}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ExpandMoreIcon fontSize="small" />}
                        onClick={(e) => setAxisMenuAnchor(e.currentTarget)}
                        sx={{ textTransform: 'none' }}
                      >
                        Eksen
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        endIcon={<ExpandMoreIcon fontSize="small" />}
                        onClick={(e) => setLabelMenuAnchor(e.currentTarget)}
                        sx={{ textTransform: 'none' }}
                      >
                        Etiket
                      </Button>
                      {answerOptions.length > 1 && (
                        <Button variant="outlined" size="small" onClick={() => setAnswersDlg(true)}>Cevaplar</Button>
                      )}
                    </ToolbarContainer>
                    <Menu
                      anchorEl={axisMenuAnchor}
                      open={Boolean(axisMenuAnchor)}
                      onClose={() => setAxisMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      disableScrollLock
                    >
                      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 220 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Yazı Tipi</InputLabel>
                          <Select
                            label="Yazı Tipi"
                            value={activeSettings.axisFontFamily || FONTS[0]}
                            onChange={(e) => patchGraphOverrides({ settings: { ...activeSettings, axisFontFamily: e.target.value } })}
                            MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 260 } } }}
                          >
                            {FONTS.map((font) => (
                              <MenuItem key={font} value={font}>{font}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Punto"
                          size="small"
                          type="number"
                          value={activeSettings.axisFontSize ?? 11}
                          inputProps={{ min: 8, max: 32 }}
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            if (Number.isNaN(next)) return;
                            const clamped = Math.min(48, Math.max(6, next));
                            patchGraphOverrides({ settings: { ...activeSettings, axisFontSize: clamped } });
                          }}
                        />
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">Yön</Typography>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={String(activeSettings.axisAngle ?? 0)}
                            onChange={(e, v) => {
                              if (v === null || v === undefined) return;
                              const parsed = Number(v);
                              if (Number.isNaN(parsed)) return;
                              patchGraphOverrides({ settings: { ...activeSettings, axisAngle: parsed } });
                            }}
                          >
                            {ROTATION_OPTIONS.map((opt) => (
                              <ToggleButton key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                        </Stack>
                      </Box>
                    </Menu>
                    <Menu
                      anchorEl={labelMenuAnchor}
                      open={Boolean(labelMenuAnchor)}
                      onClose={() => setLabelMenuAnchor(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                      disableScrollLock
                    >
                      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 240 }}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Konum</InputLabel>
                          <Select
                            label="Konum"
                            value={activeSettings.dataLabelPosition || 'inside'}
                            onChange={(e) => patchGraphOverrides({ settings: { ...activeSettings, dataLabelPosition: e.target.value } })}
                            MenuProps={{ disableScrollLock: true }}
                          >
                            <MenuItem value="inside">İç</MenuItem>
                            <MenuItem value="outside">Dış</MenuItem>
                            <MenuItem value="center">Merkez</MenuItem>
                            <MenuItem value="top">Üst</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl size="small" fullWidth>
                          <InputLabel>İçerik</InputLabel>
                          <Select
                            label="İçerik"
                            value={activeSettings.dataLabelContent || 'auto'}
                            onChange={(e) => patchGraphOverrides({ settings: { ...activeSettings, dataLabelContent: e.target.value } })}
                            MenuProps={{ disableScrollLock: true }}
                          >
                            <MenuItem value="auto">Otomatik</MenuItem>
                            <MenuItem value="value">Değer</MenuItem>
                            <MenuItem value="percent">Yüzde</MenuItem>
                            <MenuItem value="both">İkisi</MenuItem>
                          </Select>
                        </FormControl>
                        <Stack direction="row" spacing={1}>
                          <FormControl size="small" sx={{ flex: 1 }}>
                            <InputLabel>Yazı Tipi</InputLabel>
                            <Select
                              label="Yazı Tipi"
                              value={activeSettings.dataLabelFontFamily || FONTS[0]}
                              onChange={(e) => patchGraphOverrides({ settings: { ...activeSettings, dataLabelFontFamily: e.target.value } })}
                              MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 260 } } }}
                            >
                              {FONTS.map((font) => (
                                <MenuItem key={font} value={font}>{font}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Punto"
                            size="small"
                            type="number"
                            value={activeSettings.dataLabelFontSize ?? 11}
                            inputProps={{ min: 8, max: 32 }}
                            onChange={(e) => {
                              const next = Number(e.target.value);
                              if (Number.isNaN(next)) return;
                              const clamped = Math.min(48, Math.max(6, next));
                              patchGraphOverrides({ settings: { ...activeSettings, dataLabelFontSize: clamped } });
                            }}
                          />
                        </Stack>
                        <Stack spacing={1}>
                          <Typography variant="caption" color="text.secondary">Yön</Typography>
                          <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={String(activeSettings.dataLabelAngle ?? 0)}
                            onChange={(e, v) => {
                              if (v === null || v === undefined) return;
                              const parsed = Number(v);
                              if (Number.isNaN(parsed)) return;
                              patchGraphOverrides({ settings: { ...activeSettings, dataLabelAngle: parsed } });
                            }}
                          >
                            {ROTATION_OPTIONS.map((opt) => (
                              <ToggleButton key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </ToggleButton>
                            ))}
                          </ToggleButtonGroup>
                        </Stack>
                        <TextField
                          label="Ondalık"
                          size="small"
                          type="number"
                          value={activeSettings.dataLabelDigits ?? ''}
                          placeholder={effectiveMetric === 'percentage' ? '1' : '0'}
                          inputProps={{ min: 0, max: 4 }}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              patchGraphOverrides({ settings: { ...activeSettings, dataLabelDigits: null } });
                              return;
                            }
                            const next = Number(val);
                            if (Number.isNaN(next)) return;
                            const clamped = Math.min(6, Math.max(0, Math.round(next)));
                            patchGraphOverrides({ settings: { ...activeSettings, dataLabelDigits: clamped } });
                          }}
                        />
                      </Box>
                    </Menu>
                    </>
                  )}
                </Stack>

                {/* --------- Canvas ------------------------------------ */}
                <Box
                  ref={canvasContainerRef}
                  sx={{
                    width: '100%',
                    aspectRatio: '16 / 9', // oran sabit kalır
                    position: 'relative',
                    overflowX: 'hidden',
                    overflowY: 'hidden',
                    boxSizing: 'border-box',
                    background: 'linear-gradient(180deg, rgba(248,250,252,0.92) 0%, rgba(226,232,240,0.92) 85%)',
                    borderRadius: { xs: 2, md: 3 },
                  }}
                >
              
                    <Box
                      ref={canvasRef}
                      onPointerDown={handleCanvasPointerDown}
                      sx={{
                        width: CANVAS_W,
                        height: CANVAS_H,
                        position: 'relative',
                        mx: 'auto',
                        bgcolor: '#ffffff',
                        borderRadius: '12px',
                        border: '1px solid rgba(15,23,42,0.08)',
                        boxShadow: '0 28px 54px rgba(15,23,42,0.18)',
                        backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
                        backgroundPosition: '0 0',
                        backgroundImage:
                          'linear-gradient(to right, rgba(100,116,139,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(100,116,139,0.12) 1px, transparent 1px)',
                        backgroundBlendMode: 'soft-light',
                        overflow: 'hidden',
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        willChange: 'transform',
                        userSelect: 'none',
                        zIndex: 1,
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: 0,
                          borderRadius: 'inherit',
                          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.55)',
                          pointerEvents: 'none',
                        },
                      }}
                    >
                      {template?.dataUrl && (
                        <Box
                          component="img"
                          src={template.dataUrl}
                          alt=""
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: template.mode === 'stretch' ? 'fill' : template.mode,
                            opacity: typeof template.opacity === 'number' ? template.opacity : 1,
                            pointerEvents: 'none'
                          }}
                        />
                      )}
                      {activeSlide.items.map((it, index) => {
                        const baseGraph = it.type === 'graph' ? allGraphs[it.graphIndex] : null;
                        const tableData = it.type === 'table' ? ensureTableData(it.table) : null;
                        const tableLink = it.type === 'table' ? sanitizeTableLink(it.tableLink) : null;
                        const isShape = it.type === 'shape';
                        const isIcon = it.type === 'icon';
                        const IconComponent =
                          isIcon && LUCIDE_ICON_SET.has(it.icon) ? LucideIcons[it.icon] : null;
                        const iconColor =
                          isIcon && typeof it.color === 'string' && it.color
                            ? it.color
                            : DEFAULT_ICON_COLOR;
                        const iconStroke = isIcon
                          ? clamp(
                              typeof it.strokeWidth === 'number'
                                ? it.strokeWidth
                                : DEFAULT_ICON_STROKE,
                              ICON_STROKE_MIN,
                              ICON_STROKE_MAX
                            )
                          : DEFAULT_ICON_STROKE;
                        const iconSize = isIcon ? Math.max(16, Math.min(it.w, it.h)) : null;
                        const isSelected = selectedItemIds.includes(it.id);
                        const isPrimarySelected = selectedItemId === it.id;
                        const showActionBar = isSelected && (!isMultiSelection || isPrimarySelected);
                        const itemRotation = normalizeRotation(
                          typeof it.rotation === 'number' ? it.rotation : 0
                        );
                        const isRotating = rotatingItemId === it.id;
                        const shouldShowActionsBelow = it.y * scale <= 48; // keep action bar in view near canvas top
                        const actionsVisibleTransform = shouldShowActionsBelow
                          ? 'translate(-50%, 24px)'
                          : 'translate(-50%, -120%)';
                        const actionsHiddenTransform = shouldShowActionsBelow
                          ? 'translate(-50%, 44px)'
                          : 'translate(-50%, -140%)';
                        const baseLayerIndex = index + 1;
                        const isActiveLayer = showActionBar || isRotating;
                        const currentLayer = isActiveLayer ? baseLayerIndex + 1000 : baseLayerIndex;
                        return (
                          <Rnd
                            key={it.id}
                            style={{ zIndex: currentLayer }}
                            size={{ width: it.w, height: it.h }}
                            position={{ x: it.x, y: it.y }}
                            bounds="parent"
                            grid={[GRID_SIZE, GRID_SIZE]}
                            scale={scale}
                            enableResizing={!isMultiSelection}
                            lockAspectRatio={isIcon ? 1 : false}
                            cancel=".rich-text-editable"
                            onDragStart={(e, d) => handleItemDragStart(it, d)}
                            onDrag={(e, d) => handleItemDrag(it, d)}
                            onDragStop={(e, d) => handleItemDragStop(it, d)}
                            onResizeStop={(e, dir, ref, delta, pos) => {
                              const widthPx = parseInt(ref.style.width, 10);
                              const heightPx = parseInt(ref.style.height, 10);
                              updateItems((items) =>
                                items.map((p) => {
                                  if (p.id !== it.id) return p;
                                  let nextW = widthPx;
                                  let nextH = heightPx;
                                  if (isIcon) {
                                    const baseSize = Math.min(widthPx, heightPx);
                                    const snappedSize = Math.max(
                                      GRID_SIZE * 2,
                                      Math.round(baseSize / GRID_SIZE) * GRID_SIZE
                                    );
                                    const finalSize = clamp(
                                      snappedSize,
                                      GRID_SIZE * 2,
                                      Math.min(CANVAS_W, CANVAS_H)
                                    );
                                    nextW = finalSize;
                                    nextH = finalSize;
                                  }
                                  return { ...p, w: nextW, h: nextH, x: pos.x, y: pos.y };
                                })
                              );
                            }}
                            onPointerDown={(e) => handleItemMouseDown(e, it.id)}
                          >
                            <Box
                              sx={{
                                width: '100%',
                                height: '100%',
                                position: 'relative',
                                border: '1px solid',
                                borderColor: isShape && !isSelected ? 'transparent' : isSelected ? '#6366f1' : 'transparent',
                                borderRadius: isShape ? 0 : 1,
                                boxShadow: isSelected ? '0 0 0 1px rgba(99,102,241,0.25)' : 'none',
                                overflow:
                                  isSelected || isShape || it.type === 'table' || isIcon
                                    ? 'visible'
                                    : 'hidden',
                                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                              }}
                            >
                              <Box
                                className="item-action-bar"
                                sx={{
                                  position: 'absolute',
                                  top: 0,
                                  left: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  padding: '4px 6px',
                                  borderRadius: 999,
                                  backgroundColor: 'rgba(248,250,252,0.95)',
                                  border: '1px solid rgba(148,163,184,0.35)',
                                  boxShadow: '0 8px 18px rgba(15,23,42,0.22)',
                                  transform: showActionBar ? actionsVisibleTransform : actionsHiddenTransform,
                                  opacity: showActionBar ? 1 : 0,
                                  transition: 'transform 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease',
                                  pointerEvents: showActionBar ? 'auto' : 'none',
                                  zIndex: 40,
                                }}
                              >
                                <IconButton
                                  size="small"
                                  onPointerDown={(event) => handleItemRotationPointerDown(event, it)}
                                  onClick={(event) => event.preventDefault()}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    color: '#0f172a',
                                    transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(99,102,241,0.18)',
                                      color: '#312e81',
                                    },
                                    '&:active': {
                                      transform: 'scale(0.92)',
                                    },
                                  }}
                                >
                                  <RotateRightIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleItemMenuOpen(e, it.id)}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    color: '#0f172a',
                                    transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
                                    '&:hover': {
                                      backgroundColor: 'rgba(99,102,241,0.18)',
                                      color: '#312e81',
                                    },
                                    '&:active': {
                                      transform: 'scale(0.92)',
                                    },
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Box>
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  position: 'relative',
                                  transform: `rotate(${itemRotation}deg)`,
                                  transformOrigin: 'center center',
                                  transition: isRotating ? 'none' : 'transform 0.12s ease',
                                }}
                              >
                                {it.type === 'text' && (
                                  <RichTextCanvasEditor
                                    item={it}
                                    isActive={activeRichTextId === it.id}
                                    onActivate={(element) => {
                                      if (element) {
                                        activeRichTextElementRef.current = element;
                                        activeRichTextContextRef.current = { type: 'text', itemId: it.id };
                                        setActiveRichTextContext({ type: 'text', itemId: it.id });
                                        setActiveTableCell((prev) => (prev && prev.itemId === it.id ? null : prev));
                                        setActiveRichTextId((prev) => (prev === it.id ? prev : it.id));
                                        setSelectedItemId(it.id);
                                      } else {
                                        if (activeRichTextElementRef.current && activeRichTextElementRef.current.dataset?.itemId === it.id) {
                                          activeRichTextElementRef.current = null;
                                        }
                                        if (activeRichTextContextRef.current && activeRichTextContextRef.current.type === 'text' && activeRichTextContextRef.current.itemId === it.id) {
                                          activeRichTextContextRef.current = null;
                                          setActiveRichTextContext((prev) => (prev && prev.type === 'text' && prev.itemId === it.id ? null : prev));
                                        }
                                        setActiveRichTextId((prev) => (prev === it.id ? null : prev));
                                      }
                                    }}
                                    onContentChange={({ html, plain, measuredHeight }) => {
                                      updateTextItemFromRichContent(it.id, {
                                        html,
                                        plain,
                                        measuredHeight,
                                      });
                                    }}
                                  />
                                )}
                                {it.type === 'graph' && baseGraph && (
                                  <GraphView
                                    baseGraph={baseGraph}
                                    overrides={it.overrides}
                                    brandColorMap={brandColorMap}
                                    fallbackPalette={globalPaletteColors}
                                  />
                                )}
                                {it.type === 'table' && tableData && (
                                  <TableCanvas
                                    table={tableData}
                                    itemId={it.id}
                                    activeCell={activeTableCell}
                                    onActivateCell={handleTableCellActivate}
                                    onDeactivateCell={handleTableCellDeactivate}
                                    onChange={(next) => updateTableItem(it.id, () => next)}
                                    onRequestSelect={() => setSelectedItemId(it.id)}
                                    onRequestResize={(resize) => resizeTableItem(it.id, resize)}
                                    isLinked={!!tableLink}
                                  />
                                )}
                                {it.type === 'image' && (
                                  <Box
                                    component="img"
                                    src={it.src}
                                    alt={
                                      it.meta?.photographer
                                        ? `Pexels — ${it.meta.photographer}`
                                        : 'Sunum görseli'
                                    }
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      opacity:
                                        typeof it.opacity === 'number'
                                          ? clamp(it.opacity, 0, 1)
                                          : 1,
                                      pointerEvents: 'none',
                                      userSelect: 'none',
                                    }}
                                  />
                                )}
                                {isIcon && (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      pointerEvents: 'none',
                                      userSelect: 'none',
                                      p: 1,
                                    }}
                                  >
                                    {IconComponent ? (
                                      <IconComponent
                                        color={iconColor}
                                        strokeWidth={iconStroke}
                                        absoluteStrokeWidth
                                        size={iconSize}
                                        style={{
                                          width: iconSize,
                                          height: iconSize,
                                          maxWidth: '100%',
                                          maxHeight: '100%',
                                        }}
                                      />
                                    ) : (
                                      <Typography variant="caption" color="text.secondary">
                                        {it.icon || 'İkon'}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                                {isShape && (
                                  <Box
                                    sx={{
                                      width: '100%',
                                      height: '100%',
                                      pointerEvents: 'none',
                                      userSelect: 'none',
                                    }}
                                  >
                                    <ShapeCanvas shape={it.shape} width={it.w} height={it.h} />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Rnd>
                        );
                      })}
                      {isMultiSelection && multiSelectionBounds && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: multiSelectionBounds.x,
                            top: multiSelectionBounds.y,
                            width: multiSelectionBounds.w,
                            height: multiSelectionBounds.h,
                            border: '1px dashed rgba(99,102,241,0.7)',
                            borderRadius: 1,
                            boxShadow: '0 0 0 1px rgba(99,102,241,0.12)',
                            pointerEvents: 'none',
                            zIndex: 5,
                          }}
                        />
                      )}
                      {marqueeRect && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: marqueeRect.x,
                            top: marqueeRect.y,
                            width: marqueeRect.w,
                            height: marqueeRect.h,
                            border: '1px solid rgba(99,102,241,0.5)',
                            backgroundColor: 'rgba(99,102,241,0.12)',
                            pointerEvents: 'none',
                            zIndex: 6,
                          }}
                        />
                      )}
                      <Menu
                        anchorEl={itemMenu.anchorEl}
                        open={Boolean(itemMenu.anchorEl)}
                        onClose={handleItemMenuClose}
                        elevation={6}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        keepMounted
                      >
                        <MenuItem
                          onClick={() => {
                            const targetId = itemMenu.itemId;
                            handleItemMenuClose();
                            if (targetId) duplicateItem(targetId);
                          }}
                        >
                          <ListItemIcon>
                            <ContentCopyIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Kopyala" />
                        </MenuItem>
                        {activeMenuItem?.type === 'image' && (
                          <MenuItem
                            onClick={() => {
                              const targetId = itemMenu.itemId;
                              handleItemMenuClose();
                              if (targetId) openImageDialog(targetId);
                            }}
                          >
                            <ListItemIcon>
                              <ImageSearchIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Görseli değiştir" />
                          </MenuItem>
                        )}
                        {activeMenuItem?.type === 'icon' && (
                          <MenuItem
                            onClick={() => {
                              const targetId = itemMenu.itemId;
                              handleItemMenuClose();
                              if (targetId) openIconDialog(targetId);
                            }}
                          >
                            <ListItemIcon>
                              <CategoryIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="İkonu değiştir" />
                          </MenuItem>
                        )}
                        <MenuItem
                          onClick={() => {
                            const targetId = itemMenu.itemId;
                            handleItemMenuClose();
                            if (targetId) bringItemToFront(targetId);
                          }}
                        >
                          <ListItemIcon>
                            <FlipToFrontIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Öne getir" />
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            const targetId = itemMenu.itemId;
                            handleItemMenuClose();
                            if (targetId) sendItemToBack(targetId);
                          }}
                        >
                          <ListItemIcon>
                            <FlipToBackIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary="Arkaya gönder" />
                        </MenuItem>
                        <Divider />
                        <MenuItem
                          onClick={() => {
                            const targetId = itemMenu.itemId;
                            handleItemMenuClose();
                            if (targetId) removeItem(targetId);
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <ListItemIcon>
                            <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />
                          </ListItemIcon>
                          <ListItemText primary="Sil" />
                        </MenuItem>
                      </Menu>
                    </Box>

                </Box>
              </Box>
              <Box
                sx={{
                  borderRadius: 3,
                  border: '1px solid rgba(148,163,184,0.2)',
                  backgroundColor: 'rgba(248,250,252,0.92)',
                  boxShadow: '0 12px 24px rgba(148, 163, 184, 0.16)',
                  p: { xs: 1.5, md: 2 },
                }}
              >
                <Stack spacing={1.25}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}
                    useFlexGap
                  >
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#1e293b' }}>
                      Slaytlar
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={(e) => setLayoutMenuAnchor(e.currentTarget)}
                        sx={{ textTransform: 'none' }}
                      >
                        Sayfa Ekle
                      </Button>
                    </Stack>
                  </Stack>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      overflowX: 'auto',
                      pb: 0.5,
                      '&::-webkit-scrollbar': { height: 6 },
                      '&::-webkit-scrollbar-thumb': {
                        bgcolor: 'rgba(148,163,184,0.5)',
                        borderRadius: 999,
                      },
                    }}
                  >
                    {slides.map((s, i) => {
                      const layoutMeta =
                        SLIDE_LAYOUT_MAP[s.layout] || SLIDE_LAYOUT_MAP[DEFAULT_LAYOUT];
                      const isActive = i === activeIdx;
                      return (
                        <Box
                          key={s.id}
                          sx={{
                            flex: '0 0 auto',
                            width: 180,
                            border: isActive ? '2px solid #6366f1' : '1px solid #d1d5db',
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            position: 'relative',
                            bgcolor: '#fff',
                            boxShadow: isActive
                              ? '0 12px 20px rgba(99,102,241,0.18)'
                              : '0 6px 14px rgba(15,23,42,0.12)',
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            transform: isActive ? 'translateY(-3px)' : 'none',
                          }}
                          onClick={() => setActiveIdx(i)}
                          onDoubleClick={() => setRenameIdx(i)}
                        >
                          <Box
                            sx={{
                              p: 0.5,
                              bgcolor: isActive ? '#eef2ff' : '#f8fafc',
                              fontSize: 12,
                              textAlign: 'center',
                              borderBottom: '1px solid #e2e8f0',
                            }}
                          >
                            {s.name}
                          </Box>
                          <SlideThumbnail
                            slide={s}
                            allGraphs={allGraphs}
                            brandColorMap={brandColorMap}
                            template={template}
                            fallbackPalette={globalPaletteColors}
                          />
                          <Box
                            sx={{
                              px: 1,
                              py: 0.5,
                              bgcolor: '#f8fafc',
                              borderTop: '1px solid #e2e8f0',
                            }}
                          >
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {layoutMeta.label}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSlide(i);
                            }}
                            sx={{ position: 'absolute', top: 4, right: 4, bgcolor: '#fff' }}
                          >
                            <CloseIcon fontSize="inherit" />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                </Stack>
                <Menu
                  anchorEl={layoutMenuAnchor}
                  open={Boolean(layoutMenuAnchor)}
                  onClose={() => setLayoutMenuAnchor(null)}
                  MenuListProps={{ dense: true }}
                  disableScrollLock
                >
                  {SLIDE_LAYOUTS.map((layout) => (
                    <MenuItem
                      key={layout.key}
                      onClick={() => {
                        addSlide(layout.key);
                        setLayoutMenuAnchor(null);
                      }}
                    >
                      <ListItemText
                        primary={layout.label}
                        secondary={layout.description}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Stack>
        </Paper>
    </Stack>

      {/* ---------------- Rename dialog --------------------------- */}
      {renameIdx !== null && (
        <RenameDialog open={renameIdx !== null} initial={slides[renameIdx]?.name || ''} onClose={(val) => { if (val !== null) renameSlide(renameIdx, val); setRenameIdx(null); }} />
      )}

      {/* ---------------- Settings dialog ------------------------- */}
      {selectedItem?.type === 'graph' && (
        <ChartSettingsDialog open={settingsDlg} onClose={() => setSettingsDlg(false)} settings={activeSettings} onSave={(s) => patchGraphOverrides({ settings: s })} />
      )}

      {/* ---------------- Answer select dialog -------------------- */}
      {selectedItem?.type === 'graph' && (
        <AnswerSelectDialog open={answersDlg} onClose={() => setAnswersDlg(false)} options={answerOptions} selected={activeSettings.visibleAnswers ?? answerOptions} onSave={(sel) => patchGraphOverrides({ settings: { ...activeSettings, visibleAnswers: sel } })} />
      )}
      {/* ---------------- Add Graph dialog -------------------- */}
      <Dialog
        open={linkedTableDlg}
        onClose={handleLinkedTableDialogClose}
        maxWidth="sm"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>E-tablo ekle</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '65vh' }}>
          {savedLinkedTableAssets.length ? (
            <Stack spacing={2}>
              <TextField
                label="Tablo ara"
                placeholder="Tablo ara"
                size="small"
                value={linkedTableSearch}
                onChange={(e) => setLinkedTableSearch(e.target.value)}
              />
              {filteredLinkedTables.length ? (
                <List dense disablePadding>
                  {filteredLinkedTables.map((tbl, idx) => (
                    <ListItemButton
                      key={`linked-table-${tbl.uid || tbl.__sourceTableId || idx}`}
                      onClick={() => handleLinkedTablePick(tbl)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        border: '1px solid rgba(148,163,184,0.4)',
                      }}
                    >
                      <ListItemText
                        primary={tbl.header || `Tablo ${idx + 1}`}
                        secondary={
                          tbl.__analysisName ||
                          tbl.analysisName ||
                          tbl.groupName ||
                          tbl.__groupName ||
                          ''
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aramanıza uygun e-tablo bulunamadı.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Bu sunumda kaydedilmiş e-tablo bulunmuyor.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLinkedTableDialogClose}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={graphDlg} onClose={() => setGraphDlg(false)} maxWidth="sm" fullWidth disableScrollLock>
        <DialogTitle>Grafik Ekle</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth size="small">
            <InputLabel>Grafik Seç</InputLabel>
            <Select
              label="Grafik Seç"
              value={tempGraphHeader}
              onChange={(e) => setTempGraphHeader(e.target.value)}
              MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 300 } } }}
            >
              {allGraphs.map((g, idx) => {
                const { analysisName, groupName } = extractGraphMeta(g);
                const secondary = [
                  analysisName && `Analiz: ${analysisName}`,
                  groupName && `Grup: ${groupName}`,
                ]
                  .filter(Boolean)
                  .join(' • ');
                return (
                  <MenuItem key={`${g.header || 'graph'}-${idx}`} value={g.header}>
                    <ListItemText
                      primary={g.header || 'Grafik'}
                      secondary={secondary || null}
                      secondaryTypographyProps={{ noWrap: true }}
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGraphDlg(false)}>Vazgeç</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!tempGraphHeader) return;
              setSelectedGraphHeader(tempGraphHeader);
              addGraph(tempGraphHeader);
              setGraphDlg(false);
              setTempGraphHeader('');
            }}
          >
            Ekle
          </Button>
        </DialogActions>
      </Dialog>
      {/* ---------------- Brand color dialog -------------------- */}
      {/* ---------------- Analysis import dialog -------------------- */}
      <Dialog
        open={analysisDlg}
        onClose={(_, reason) => {
          if (analysisSaving) return;
          setAnalysisDlg(false);
        }}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>Analizden Grafik/Tablo Seç</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {analysisLoading && !analysisList.length ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 160,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : analysisList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Bu organizasyona ait kayıtlı analiz bulunamadı.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Analiz</InputLabel>
                <Select
                  label="Analiz"
                  value={selectedAnalysisId}
                  onChange={(e) => setSelectedAnalysisId(e.target.value)}
                  MenuProps={{ disableScrollLock: true, PaperProps: { sx: { maxHeight: 320 } } }}
                >
                  {analysisList.map((a) => {
                    const rawSecondary =
                      a.projectCode ||
                      a.projectID ||
                      a.projectId ||
                      (a.projectID ? `Proje: ${a.projectID}` : '');
                    const secondary = rawSecondary ? String(rawSecondary) : null;
                    return (
                      <MenuItem key={a.id} value={a.id}>
                        <ListItemText
                          primary={a.title || a.name || 'Analiz'}
                          secondary={secondary}
                          secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                        />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {selectedAnalysis ? (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <TextField
                      size="small"
                      placeholder="Grafik veya tablo ara"
                      value={analysisSearch}
                      onChange={(e) => setAnalysisSearch(e.target.value)}
                      sx={{ minWidth: 220, flex: 1 }}
                    />
                    <Button
                      size="small"
                      variant="text"
                      onClick={handleSelectAllFiltered}
                      disabled={!filteredAnalysisItems.length}
                    >
                      {allFilteredSelected ? 'Seçimi Kaldır' : 'Hepsini Seç'}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={reloadSelectedAnalysis}
                      disabled={analysisGraphsLoading}
                    >
                      Yenile
                    </Button>
                  </Stack>

                  {analysisFetchError && (
                    <Typography variant="body2" color="error">
                      {analysisFetchError}
                    </Typography>
                  )}

                  {analysisGraphsLoading ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 180,
                      }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  ) : filteredAnalysisItems.length ? (
                    <Box
                      sx={{
                        maxHeight: '48vh',
                        overflowY: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: 1,
                      }}
                    >
                      <List dense disablePadding>
                        {filteredAnalysisItems.map((item) => {
                          const checked = analysisSelectedKeys.includes(item.uid);
                          const typeLabel = item.type === 'table' ? 'Tablo' : 'Grafik';
                          const detail =
                            item.type === 'graph'
                              ? item.graphType
                              : item.rowCount != null
                                ? `Satır: ${item.rowCount}`
                                : null;
                          const secondary = [typeLabel, detail, item.groupName]
                            .filter(Boolean)
                            .join(' • ');
                          return (
                            <ListItemButton
                              key={item.uid}
                              dense
                              selected={checked}
                              onClick={() => toggleAnalysisItem(item.uid)}
                            >
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <Checkbox
                                  edge="start"
                                  size="small"
                                  checked={checked}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={item.header}
                                secondary={secondary || null}
                                secondaryTypographyProps={{ noWrap: true }}
                              />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {analysisSearch.trim()
                        ? 'Aramanızla eşleşen öğe bulunamadı.'
                        : 'Bu analizde aktarılabilir grafik veya tablo bulunamadı.'}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Seçili öğe sayısı: {selectedCount}
                  </Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  İçerikleri görüntülemek için bir analiz seçin.
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {analysisSaveError && (
            <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>
              {analysisSaveError}
            </Typography>
          )}
          <Button onClick={() => !analysisSaving && setAnalysisDlg(false)} disabled={analysisSaving}>
            Kapat
          </Button>
          <Button
            variant="contained"
            onClick={handleImportSelected}
            disabled={!analysisSelectedKeys.length || analysisSaving}
          >
            {analysisSaving ? 'Ekleniyor...' : 'Seçilenleri Ekle'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={iconDialogState.open}
        onClose={handleIconDialogClose}
        maxWidth="md"
        fullWidth
        disableScrollLock
      >
        <DialogTitle>İkon Seç</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          <Stack spacing={2}>
            <TextField
              size="small"
              fullWidth
              placeholder="Ara (örn. arrow, trend)"
              value={iconSearch}
              onChange={(e) => setIconSearch(e.target.value)}
            />
            {filteredIconOptions.length > 0 ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(96px, 1fr))',
                  gap: 1.5,
                }}
              >
                {filteredIconOptions.map((opt) => {
                  const IconComponent = opt.Component;
                  const isActive = iconDialogState.targetId
                    ? iconDialogTarget?.icon === opt.name
                    : false;
                  return (
                    <ButtonBase
                      key={opt.name}
                      onClick={() => handleIconPick(opt.name)}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: isActive ? 'primary.main' : 'rgba(148,163,184,0.35)',
                        backgroundColor: isActive ? 'rgba(79,70,229,0.12)' : '#fff',
                        p: 1.25,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        boxShadow: isActive
                          ? '0 10px 24px rgba(79,70,229,0.25)'
                          : '0 6px 16px rgba(148,163,184,0.18)',
                        transition: 'all 0.18s ease',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'rgba(79,70,229,0.12)',
                          boxShadow: '0 12px 28px rgba(79,70,229,0.25)',
                        },
                      }}
                    >
                      <IconComponent
                        color={iconDialogTarget?.color || DEFAULT_ICON_COLOR}
                        strokeWidth={iconDialogTarget?.strokeWidth ?? DEFAULT_ICON_STROKE}
                        size={36}
                      />
                      <Typography
                        variant="caption"
                        sx={{ textAlign: 'center', lineHeight: 1.2, mt: 0.25 }}
                      >
                        {opt.name.replace(/([a-z0-9])([A-Z])/g, '$1 $2')}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aramanıza uygun ikon bulunamadı.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleIconDialogClose}>Kapat</Button>
        </DialogActions>
      </Dialog>

      <BrandColorDialog
        open={brandDlg}
        onClose={() => setBrandDlg(false)}
        value={brandColors}
        onChange={setBrandColors}
      />
      <TemplateDialog
        open={templateDlg}
        initial={template}
        onClose={(val) => {
          setTemplateDlg(false);
          if (val === null) return;
          setTemplate(val);
        }}
      />
      <ImagePickerDialog
        open={imageDialogState.open}
        existingItem={imageDialogExistingItem}
        onClose={handleImageDialogClose}
      />
    </Container>
  </Box>
  );
}
